import {
  useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent, type MouseEvent as ReactMouseEvent,
  type PointerEvent, type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';

import { Panel, PanelGrid } from '../../../components/Panel.tsx';
import { Icon } from '../../../shell/Icon.tsx';
import { MetricCard, MetricStrip } from '../../../components/MetricCard.tsx';
import { StateLabel } from '../../../components/StatusBadge.tsx';
import { MatrixKey } from '../../../components/StatusMatrix.tsx';
import { Plot } from '../../../components/Plot.tsx';
import { TimeAxis } from '../../../components/Ribbon.tsx';
import { Sparkline } from '../../../components/Sparkline.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { Empty } from '../../../components/Empty.tsx';
import { SegmentedControl } from '../../../components/Toolbar.tsx';
import { Button } from '../../../components/Button.tsx';
import {
  BATTERY_BAD, BATTERY_WARN, CERT_BAD_DAYS, CERT_WARN_DAYS,
  SEEN_BAD_MS, SEEN_WARN_MS,
  FLEET_METRICS, fleetExceptions, type FleetException,
} from '../../../components/fleetMetrics.ts';
import { ReasonList } from '../../../components/ReasonList.tsx';
import { DataTable, MeterCell, type Column } from '../../../components/DataTable.tsx';
import { Timestamp } from '../../../components/Timestamp.tsx';
import {
  MATRIX_KEY, STATUS_STATE, LIVENESS_STATE, level, levelTone, matrixTone, type Tone,
} from '../../../components/state.ts';
import { scanState } from '../../../components/scanState.ts';
import { RobotLink } from '../../../components/AppLink.tsx';
import { NO_VALUE, duration, local, midEllipsis, num, relative, utc } from '../../../components/format.ts';
import { axisClock, ticks } from '../../../components/timeScale.ts';
import { useNow } from '../../../components/useNow.ts';

import { apiFetch } from '../../../auth/apiFetch.ts';
import { useStats } from '../../../hooks/useStats.ts';
import { useRobots, MORPHOLOGIES, type Robot, type RobotStatus, type Liveness } from '../../../hooks/useRobots.ts';
import type { TelemetryReading } from '../../../hooks/useTelemetry.ts';
import type { Command, CommandName, CommandStatus } from '../../../hooks/useCommands.ts';
import type { ProcessingState, Scan } from '../../../hooks/useScans.ts';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
/* The console's ONE hold. `L` stops every timer in the app by setting
   TanStack's focus manager false, and the top bar, the surface strip and
   this surface's eight panel marks all have to read the same switch — a
   second copy of it here would be a second definition of "live". */
import { useFeedWord, useLiveHold } from '../../../shell/TopBar.tsx';
import {
  Blank, SkeletonMatrix, SkeletonPlot, SkeletonStrip, SkeletonTable, StoppedContext, faultOf,
  stateWord, type SkeletonCell,
  type SkeletonColumn,
} from '../../../components/states/index.ts';
import styles from './Dashboard.module.css';

/* ==========================================================================
   Fleet operations overview — the screen an operator leaves up on a wall.

   Four questions, and nothing else on the surface:

     is the fleet healthy   → the KPI strip, the 6×28 status matrix and the
                              charge distribution beside it
     what needs me now      → the exception table and the command queue
     what is happening      → the command ledger and the site plots
     what is trending       → ingest, coverage and command rate over the real
                              window every reporting robot covers

   Every figure comes off /api/stats, /api/robots, the per-robot telemetry
   history or the per-robot command list. Nothing is smoothed, extrapolated or
   invented: where a robot has never reported, its cells are hollow, its
   readings are em dashes and it is absent from the site plot rather than
   drawn at the origin.
   ========================================================================== */

/* ---- one loading vocabulary ---------------------------------------------
   Every panel on this surface is fed by one of three endpoints, and while one
   of them is on its first read the panel says so IN THE SAME WORDS wherever
   it appears — the band, the two facet rails, the site plots, the exception
   table and the ledger. It used to be five panels of placeholder bars beside
   two panels of text, which reads as two different things happening.

   The line goes where the first row would be (§8, tell 3), and the panel's own
   1px indeterminate bar under the header carries the fact that it is loading;
   this says WHICH SOURCE, which is the part the bar cannot. */
const READING = {
  robots: 'Reading roster…',
  telemetry: 'Reading telemetry…',
  commands: 'Reading command ledger…',
  scans: 'Reading capture sessions…',
} as const;

const HOUR = 3_600_000;

/** `18:00` at 10px mono, plus the tick's own 1px rule and 3px inset — what a
    ruler label needs to the right of where it lands. */
const AXIS_LABEL_PX = 34;


/* Deepest history the API will hand back for one robot in one call. It is a
   cap, not a window, which is why the trend panel derives its own start time
   from the data rather than assuming 24 h — see `historyWindow`. */
const TELEMETRY_SAMPLES = 500;
const COMMAND_SAMPLES = 200;

const STATUS_ORDER: readonly RobotStatus[] = [
  'active', 'idle', 'charging', 'error', 'maintenance',
];
const LIVENESS_ORDER: readonly Liveness[] = [
  'live', 'backfilling', 'stale', 'offline', 'provisioning',
];

/* Non-terminal command states: the queue an operator is answerable for. */
const OPEN_COMMANDS: ReadonlySet<CommandStatus> = new Set(['pending', 'sent', 'acked']);

/** Which slice of the ledger the rail is showing. */
const NO_SELECTION: ReadonlySet<string> = new Set<string>();

type CommandView = 'all' | 'open' | 'failed';

/** How long a command may sit in a non-terminal state before it is worth a
    look, and before it is a fault. Only ever applied to OPEN commands. */
/* ---- declared cadences ---------------------------------------------------
   The intervals the hooks below and `hooks/useRobots` / `hooks/useStats`
   actually set. Every panel's freshness mark is judged against the one its
   own source declares, so a constant that drifts from its hook makes a panel
   lie — which is why each is named beside the query that sets it and the
   fan-outs read these rather than repeating a literal. */
const EVERY_ROSTER = 5_000;
const EVERY_STATS = 5_000;
const EVERY_TELEMETRY = 120_000;
const EVERY_COMMANDS = 20_000;
const EVERY_SCANS = 60_000;


const QUEUE_WARN_MS = 900_000;
const QUEUE_BAD_MS = 7_200_000;

const COMMAND_TONE: Record<CommandStatus, Tone> = {
  pending: 'warn',
  sent: 'nominal',
  acked: 'nominal',
  completed: 'good',
  failed: 'error',
  cancelled: 'neutral',
};

/* --------------------------------------------------------------------------
   Fleet-wide history.

   The contract has no fleet aggregate for either series, so both are unioned
   client-side from the per-robot endpoints. They are deliberately on slower
   cadences than the 5 s fleet poll: an hourly bucket does not change in five
   seconds, and 28 requests that often would be dishonest about what the panel
   actually costs.

   A FAILED per-robot call is never coerced to an empty list. Twenty-eight
   requests can fail independently, and the difference between "this robot
   sent nothing this hour" and "we could not ask it" is the difference between
   a real dip in the trace and a hole we drew ourselves. So each leg records
   whether it answered; the union carries the count of the ones that did not,
   and a call where EVERY leg failed throws rather than returning an empty
   fleet history.
   -------------------------------------------------------------------------- */

/** What a fan-out over the roster actually came back with. */
interface FanOut<T> {
  value: T;
  /** Robots whose leg failed. Their absence is not a zero. */
  failed: number;
  /** Robots asked. */
  asked: number;
}

/** The first failure's own words — a status line or the browser's message. */
function legFailure(e: unknown): string {
  if (e instanceof Error && e.message.trim().length > 0) return e.message.trim();
  return 'request failed';
}

function useFleetTelemetry(ids: readonly string[]) {
  const key = ids.join(',');
  return useQuery({
    queryKey: ['fleet-telemetry', key, TELEMETRY_SAMPLES] as const,
    enabled: ids.length > 0,
    refetchInterval: EVERY_TELEMETRY,
    queryFn: async (): Promise<FanOut<Map<string, TelemetryReading[]>>> => {
      let failed = 0;
      let reason: string | null = null;
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await apiFetch(
              `/api/robots/${encodeURIComponent(id)}/telemetry?limit=${TELEMETRY_SAMPLES}`,
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const body = (await r.json()) as { readings?: TelemetryReading[] };
            return [id, body.readings ?? []] as const;
          } catch (e) {
            failed += 1;
            reason ??= legFailure(e);
            return [id, [] as TelemetryReading[]] as const;
          }
        }),
      );
      if (failed === ids.length) throw new Error(reason ?? 'request failed');
      return { value: new Map(pairs), failed, asked: ids.length };
    },
  });
}

function useFleetCommands(ids: readonly string[]) {
  const key = ids.join(',');
  return useQuery({
    queryKey: ['fleet-commands', key] as const,
    enabled: ids.length > 0,
    refetchInterval: EVERY_COMMANDS,
    queryFn: async (): Promise<FanOut<Command[]>> => {
      let failed = 0;
      let reason: string | null = null;
      const lists = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await apiFetch(
              `/api/robots/${encodeURIComponent(id)}/commands?limit=${COMMAND_SAMPLES}`,
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const body = (await r.json()) as { commands?: Command[] };
            return body.commands ?? [];
          } catch (e) {
            failed += 1;
            reason ??= legFailure(e);
            return [] as Command[];
          }
        }),
      );
      if (failed === ids.length) throw new Error(reason ?? 'request failed');
      return { value: lists.flat(), failed, asked: ids.length };
    },
  });
}

/* The third fan-out, and the one this surface was missing entirely: what the
   fleet has actually CAPTURED. These are forestry rovers whose whole reason to
   exist is the session — `row-14`, `orchard-b`, `yard-sweep` — and an
   operations overview that reports battery, link and command queue while
   saying nothing about nine failed captures has answered "is the fleet
   healthy" and not "did we get the data".

   Polled slower than the ledger (a session lasts tens of minutes, and its
   pipeline state moves on the order of minutes) and slower than telemetry,
   because nothing on this panel is a live value. */
function useFleetScans(ids: readonly string[]) {
  const key = ids.join(',');
  return useQuery({
    queryKey: ['fleet-scans', key] as const,
    enabled: ids.length > 0,
    refetchInterval: EVERY_SCANS,
    queryFn: async (): Promise<FanOut<Scan[]>> => {
      let failed = 0;
      let reason: string | null = null;
      const lists = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await apiFetch(`/api/robots/${encodeURIComponent(id)}/scans`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const body = (await r.json()) as { scans?: Scan[] };
            return body.scans ?? [];
          } catch (e) {
            failed += 1;
            reason ??= legFailure(e);
            return [] as Scan[];
          }
        }),
      );
      if (failed === ids.length) throw new Error(reason ?? 'request failed');
      return { value: lists.flat(), failed, asked: ids.length };
    },
  });
}

/* --------------------------------------------------------------------------
   Reachability — the state this surface used to be missing.

   Every panel here reads from exactly one endpoint, and a request that FAILED
   is a different fact from a request that came back empty. On an operations
   screen that distinction is the whole difference between "the fleet is
   quiet" and "we cannot see the fleet", and it is the one a console is least
   allowed to get wrong: rendering a dead API as `0 faulted · 0 needs
   attention · every robot inside its limits` asserts good news nobody has.

   So a rejection never becomes an empty array. It becomes a `Feed`, and a
   Feed that is `down` suppresses every figure derived from it — the KPI reads
   `—`, the panel flies `Polling off` at the last instant it was genuinely
   current (never `00:00:00Z`, which is not a time anything happened), and one
   `--error` line names the endpoint and the failure where the first row would
   have been.

   This is the same rule the happy path already keeps for a single missing
   reading — hollow matrix cell, hatched battery column, `BATT —` — applied to
   the case where the missing reading is all of them.
   -------------------------------------------------------------------------- */

interface Feed {
  /** Holding data we currently trust. */
  ok: boolean;
  /** The request failed and we hold nothing: this source is unknowable. */
  down: boolean;
  /** Holding data, but the refresh behind it is failing. Figures stand; the
      panel stops claiming to be live. */
  stale: boolean;
  /** Has never resolved — first paint, not a failure. */
  loading: boolean;
  /** Last SUCCESSFUL update, epoch ms. null = never once succeeded. */
  at: number | null;
  /** The path, for the error line. Real, and specific enough to grep. */
  endpoint: string;
  /** The failure in the console's ONE set of words — `no reply`, `server
      fault`, `rate-limited` — off `components/states`' taxonomy, so a panel
      row and the supply ledger cannot describe one event two ways. */
  detail: string;
  /** The browser's own message, verbatim, for the row's title. Null = none. */
  raw: string | null;
  /** How the console names this source in one word — `robots`, `stats`,
      `telemetry`. What a panel header has room to print beside a status code
      when the full path does not fit; the path is in the mark's own title and
      spelled in full on the panel's own fault row. */
  label: string;
  /** THE HOOK'S OWN DECLARED CADENCE, ms. The number a panel's freshness is
      judged against: a 20 s ledger 25 s old has stopped, a 2 min history 25 s
      old has not. Without it every panel would have to share one page-wide
      idea of "late" and five of them would be wrong. */
  everyMs: number;
}

/** The subset of a query result reachability is decided from. */
interface QueryLike {
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  dataUpdatedAt: number;
}

function feedOf(
  q: QueryLike,
  hasData: boolean,
  endpoint: string,
  label: string,
  everyMs: number,
): Feed {
  const raw = q.error === null ? '' : q.error.message.trim();
  return {
    label,
    everyMs,
    ok: !q.isError && hasData,
    down: q.isError && !hasData,
    stale: q.isError && hasData,
    loading: q.isPending,
    /* 0 is TanStack's "never fetched" sentinel, and printing it as an epoch
       is how this screen used to claim a successful poll at 00:00:00Z. */
    at: q.dataUpdatedAt > 0 ? q.dataUpdatedAt : null,
    endpoint,
    /* ONE EVENT, ONE WORDING. This used to be the browser's own exception
       text — `Failed to fetch` in seven panel rows, over a supply ledger 700px
       below calling the same event `no reply`. One fault described twice is
       two faults as far as an operator scanning the screen is concerned, so
       the word comes from `components/states`' taxonomy, which is the same
       function the ledger, the banner and every other panel line ask. The raw
       message is still one hover away, on the row's title. */
    detail: (() => {
      const fault = faultOf(q.error);
      if (fault !== null) return stateWord(fault.class);
      return raw.length === 0 ? 'read failed' : raw.length > 64 ? `${raw.slice(0, 63)}…` : raw;
    })(),
    raw: raw.length === 0 ? null : raw,
  };
}

/** A feed nothing has asked for yet — the fan-outs before the roster lands. */
const FEED_IDLE: Feed = {
  ok: false, down: false, stale: false, loading: true,
  at: null, endpoint: '', detail: '', raw: null, label: '', everyMs: 0,
};

/**
 * The per-robot fan-outs are DERIVED from the roster, so their state is too.
 *
 * A query that is disabled because its input never arrived is not "loading" —
 * left as such it spins a skeleton for ever and flies a liveness chip over a
 * panel that will never fill. Three cases, and each has a different truth:
 *
 *   roster down     → this feed is down as well, and the endpoint to name is
 *                     the ROSTER's: the per-robot call was never reached, so
 *                     blaming it would send an operator to the wrong log.
 *   roster pending  → genuinely idle; wait.
 *   roster empty    → nothing to fetch. The poll is current and the answer is
 *                     "no robots", which is a fact, not a failure.
 */
function fanFeed(
  q: QueryLike,
  hasData: boolean,
  endpoint: string,
  label: string,
  everyMs: number,
  roster: Feed,
  hasIds: boolean,
): Feed {
  /* The roster's failure, under the roster's own name and cadence: the
     per-robot call was never reached, so judging this feed against ITS
     interval would be judging a request nobody made. */
  if (roster.down) return { ...roster };
  if (roster.loading) return FEED_IDLE;
  if (!hasIds) return { ...roster, endpoint, label, everyMs };
  return feedOf(q, hasData, endpoint, label, everyMs);
}

/* ==========================================================================
   PER-PANEL FRESHNESS AS AN INSTRUMENT, NOT A STRING.

   THE BAR §5 and tell #10: freshness is answered PER PANEL, and
   `inorbit-fleet-status-matrix.png` is the proof — `Fleet Status: Live` and
   `Fleet Log: Live off` in one screenshot. What this surface used to render
   was the WORD without the instrument: eight headers printing `Polling` /
   `20 s` / `60 s` / `2 min` off `feed.ok`, which is only ever false when the
   request threw. Two states it could therefore not say, and both are states
   an operator has to be able to see from across the room:

     THE CONSOLE IS HELD.  `L` stops every timer in the app (TopBar's
       `useLiveHold`, which sets TanStack's focus manager false). Nothing is
       arriving, by choice. The top bar says `HOLD`, the surface strip says
       `Held`, the floor says `polling held` — and all eight panel headers
       went on claiming a live poll, with filled dots.

     A SOURCE HAS GONE QUIET WITHOUT THROWING.  A poll that simply stops
       landing — a tab throttled, a request that hangs to its timeout, a
       gateway holding the connection open — leaves the last good answer in
       the cache and `isError` false. `Polling` over a value four minutes old
       is the single most expensive lie this screen can tell.

   So the mark is DERIVED, per panel, from that panel's own source: the age of
   its last successful read against ITS OWN declared cadence. Five readings,
   and the fill of the dot carries the split before the colour does:

     ● Polling 5s      inside its own window. Steel; a healthy fleet spends no
                       colour saying so.
     ◌ Held 41s        the console is held. Amber, and the shell strip's own
                       word for the same act. The age is printed once it
                       is past due, because "I stopped it" and "this is 41 s
                       old" are different facts and the operator needs both.
     ◌ 38s stale       nothing has landed for well over the cadence, and
                       nothing threw. Amber.
     ◌ stats 503       the source is answering with a failure. Red, named, and
                       carrying the status the server actually returned.

   And a panel whose values are past their own refresh window drops those
   values to 55% — the header stays at full contrast, so what is legible from
   the far side of the room is exactly the pair "this panel is frozen, and
   here is why". A dimmed panel is the same convention `foxglove` uses for a
   stalled camera tile (§5: "a stale feed dims to 40% and shows a mono age").
   ========================================================================== */

/**
 * How far past its own cadence a source may drift before its panel stops
 * claiming to be current.
 *
 * One and a half cadences plus two seconds. Not one cadence flat, which is
 * what the naive reading gives: a 5 s poll is momentarily "5.04 s old" at the
 * end of every single cycle, and a mark that flickers amber once a cycle is a
 * blinking alarm (§7 — banned) rather than an instrument. Not three cadences
 * either, which is the TOP BAR's slack: that band reports the console's whole
 * supply and can afford to wait; a panel header is answering for one source
 * and should say so the cycle after it misses.
 *
 * A source with no cadence (`0`, read on demand) is never late — it is a
 * document, not a feed.
 */
function lateAfter(everyMs: number): number {
  return everyMs <= 0 ? Number.POSITIVE_INFINITY : everyMs * 1.5 + 2_000;
}

type MarkState = 'wait' | 'live' | 'held' | 'late' | 'stale' | 'down';

interface Mark {
  state: MarkState;
  /** Since the last SUCCESSFUL read. Null where there has never been one. */
  ageMs: number | null;
  /** The values this feed draws are real, and older than its own window. */
  frozen: boolean;
}

/**
 * One feed, one now, one hold → one reading.
 *
 * Order matters and it is the order of severity: a source that is failing is
 * failing whether or not the operator is also holding, and a hold is a fact
 * about the console rather than about the source, so it ranks under the
 * failure and over mere age.
 */
function markOf(feed: Feed, now: number, held: boolean): Mark {
  const ageMs = feed.at === null ? null : Math.max(0, now - feed.at);
  const past = ageMs !== null && ageMs > lateAfter(feed.everyMs);
  /* Nothing on screen, so nothing to freeze — the body is an error line and
     dimming a fault report is the one thing this must not do. */
  if (feed.down) return { state: 'down', ageMs, frozen: false };
  /* Figures stand, the refresh behind them is throwing. Frozen regardless of
     age: this is not drift, it is a source that has stopped. */
  if (feed.stale) return { state: 'stale', ageMs, frozen: true };
  /* A source whose FIRST read is still in flight is not late. Nothing has
     stopped and nothing is stale, so painting it amber would spend the
     degraded colour — the one an operator scans a wall of panels for — on
     eight panels that are working. Neutral and hollow until there is an
     answer to be current or late about; the panel's own 1px bar under the
     header is what says a read is running. */
  if (feed.loading && feed.at === null) return { state: 'wait', ageMs: null, frozen: false };
  /* Held: the mark flips on the instant the hold does, because the panel has
     stopped updating on the instant the hold does. The DIMMING waits for the
     value to actually go past due — an operator holds the console in order to
     read it, and greying a two-second-old reading the moment they press `L`
     punishes the gesture instead of reporting it. */
  if (held) return { state: 'held', ageMs, frozen: past };
  if (past) return { state: 'late', ageMs, frozen: true };
  return { state: 'live', ageMs, frozen: false };
}

/** A cadence in the same idiom: `5s`, `20s`, `2m`. */
function everyShort(ms: number): string {
  if (ms <= 0) return 'on demand';
  const s = Math.round(ms / 1000);
  return s < 90 ? `${s}s` : `${Math.round(s / 60)}m`;
}

/**
 * The failure, short enough for the narrowest panel header on this page.
 *
 * An HTTP status is already the shortest true thing there is, so it is what
 * gets printed. A dead socket is not: the browser's own phrase for it is
 * `Failed to fetch`, 22 characters with the source name in front of it, and
 * measured at 1280 that mark wraps the KPI strip onto a second flex row it
 * has no height for and clips the Estate panel's title to `Est…`. A panel
 * header that has deleted its own name to print a fault has lost the more
 * important half.
 *
 * `no reply` is that fact compressed, not paraphrased — the request got no
 * reply — and the verbatim string is never lost: it is in this mark's own
 * title, and on the `--error` fault row the panel draws where its first row
 * would be — which is the one place on this page that spells a failure out.
 */
function reasonShort(detail: string): string {
  const code = /\b([45]\d{2})\b/.exec(detail);
  if (code !== null) return code[1]!;
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(detail)) {
    return 'no reply';
  }
  return detail.length <= 12 ? detail : `${detail.slice(0, 11)}…`;
}

const MARK_TONE: Record<MarkState, Tone> = {
  wait: 'neutral',
  live: 'nominal',
  held: 'warn',
  late: 'warn',
  stale: 'error',
  down: 'error',
};

/**
 * The mark itself. Sized for a `Panel`'s `meta` slot, one line, mono.
 *
 * `now` and `held` are passed rather than hooked so that eight of these do
 * not open eight subscriptions to the same two sources — the view already
 * holds both, and a mark that re-rendered on its own beat could disagree with
 * the age printed in the row beneath it.
 */
function FeedMark({
  feed,
  mark,
  named = false,
}: {
  feed: Feed;
  mark: Mark;
  /** Print the source's name even where the state does not need it.
      A panel with ONE source does not: the mark is under that panel's title
      and there is nothing to disambiguate. A panel with TWO does — `◌ 48s
      stale` over Fleet history's telemetry-and-commands stack says something
      has stopped and leaves the operator to guess which of the two traces
      they are still allowed to believe. */
  named?: boolean | undefined;
}) {
  const tone = MARK_TONE[mark.state];
  const feedWord = useFeedWord();
  const live = mark.state === 'live';
  const stamp = feed.at === null ? null : new Date(feed.at).toISOString();
  const every = everyShort(feed.everyMs);
  const age = mark.ageMs === null ? null : duration(mark.ageMs, { compact: 1 });

  let word: ReactNode;
  let title: string;
  switch (mark.state) {
    case 'wait':
      word = <span className={styles.markWord}>{feedWord}</span>;
      title = `${feed.endpoint} — first read in flight`;
      break;
    case 'live':
      word = (
        <>
          <span className={styles.markWord}>{feedWord}</span>
          <span className={styles.markEvery}>{every}</span>
        </>
      );
      title = `${feedWord} ${feed.endpoint} every ${every}`
        + (stamp === null ? '' : ` · last read ${utc(stamp)}${age === null ? '' : ` (${age} ago)`}`);
      break;
    case 'held':
      word = (
        <>
          <span className={styles.markWord}>Held</span>
          {mark.frozen && age !== null && <span className={styles.markAge}>{age}</span>}
        </>
      );
      /* No source name here on purpose: a hold is a fact about the CONSOLE,
         not about one endpoint, and every panel on the page says it at once. */
      title = `Polling is held for the whole console (press L to resume).`
        + ` ${feed.endpoint} normally reads every ${every}`
        + (stamp === null ? '.' : ` · last read ${utc(stamp)}${age === null ? '' : ` (${age} ago)`}`);
      break;
    case 'late':
      word = (
        <>
          {named && <span className={styles.markSrc}>{feed.label}</span>}
          <span className={styles.markAge} data-lead={named ? undefined : ''}>{age}</span>
          <span className={styles.markWord}>stale</span>
        </>
      );
      title = `Nothing has landed from ${feed.endpoint} for ${age ?? 'some time'};`
        + ` it polls every ${every}. The values below are from`
        + (stamp === null ? ' an earlier read.' : ` ${utc(stamp)}.`);
      break;
    default:
      /* Same rule as `late`, for the same reason and with more at stake: a
         panel fed by ONE source has that source named on the `--error` fault
         row directly beneath this mark, so repeating it here buys nothing and
         costs the panel its own title — measured in a 424px rail, `commands
         no reply` beside a three-segment control clipped `Commands` to
         `Com…`, which is the state where a panel's identity matters most. */
      word = (
        <>
          {named && <span className={styles.markSrc}>{feed.label}</span>}
          <span className={styles.markWhy} data-lead={named ? undefined : ''}>
            {reasonShort(feed.detail)}
          </span>
        </>
      );
      title = `${feed.endpoint} — ${feed.detail}`
        + (stamp === null
          ? ' · nothing has ever landed from this source this session'
          : ` · last good read ${utc(stamp)}${age === null ? '' : ` (${age} ago)`}`);
      break;
  }

  return (
    <span className={styles.mark} data-tone={tone} data-live={live} title={title}>
      {/* Fill carries the state before the colour does: solid is receiving,
          hollow is not. Colour is the second signal, never the only one. */}
      <StatusDot tone={tone} hollow={!live} />
      {word}
    </span>
  );
}

/* ---- a panel with two sources ------------------------------------------
   A panel header carries ONE mark, and a panel fed by two endpoints may not
   fly the healthy one's light over the other's failure. Fleet history is the
   case: `● Polling 2m` is telemetry's cadence, and it was printed over a
   commands trace whose source had never once answered.

   So the mark is the worst reading among the panel's own sources, named for
   whichever one is actually failing. `wait` deliberately does not rank — a
   first read in flight beside a live one is a page starting up, not a
   degradation, and painting it amber spends the degraded colour (§3) on eight
   healthy panels once a second. A panel's BODY still dims from its own
   PRIMARY source: a live telemetry trace may not be greyed because a second
   series went dark; that series draws its own gap. */
const MARK_RANK: Record<MarkState, number> = {
  live: 0, wait: 0, held: 2, late: 3, stale: 4, down: 5,
};

function worstOf(
  feeds: readonly Feed[],
  now: number,
  held: boolean,
): { feed: Feed; mark: Mark } {
  let pick = { feed: feeds[0]!, mark: markOf(feeds[0]!, now, held) };
  for (const f of feeds.slice(1)) {
    const m = markOf(f, now, held);
    if (MARK_RANK[m.state] > MARK_RANK[pick.mark.state]) pick = { feed: f, mark: m };
  }
  return pick;
}

/* ==========================================================================
   THE FAILURE MODEL IS PER PANEL. THERE IS NO PAGE BANNER.

   §6: "Error: an inline row in the panel with --error text and the actual
   message." What this surface used to draw instead was a full-width band
   above the KPI strip, and it was wrong twice over.

   It was REDUNDANT: a dead command ledger was already reported by the
   Commands panel's own header mark, by the Commands panel's own body, and by
   the KPI strip's trust cell, each of which owns figures that came off that
   endpoint. The band was a fourth voice owning nothing.

   And it was DESTRUCTIVE: it took a row out of the page grid, so a transient
   503 moved every panel on the surface down 27px and moved them back when the
   next poll landed. A fault must be REPORTED by the layout, never PERFORMED
   by it (§7: never animate layout on data arrival).

   So the account moves onto the thing it is about — one row, inside the panel
   whose figures are affected, carrying that source's real message and the
   verb that re-reads that source:

     down   nothing was ever held   → the row IS the body, at the first row's
            position: `Unreachable /api/… · Failed to fetch`.
     stale  the refresh is throwing → the row sits where the first row would
            be and the last-known rows below it drop to 40% (§5: "a stale feed
            dims to 40% and shows a mono age; it does NOT get replaced"). So
            Commands keeps its 113 rows and its `All 113 / Open 30`, and
            nothing on screen claims they are current.
     late   nothing has landed and nothing threw → the same row in --warn, and
            the values dim to 55%: they are real, it is the clock that is
            wrong. This is the only state that has no other retry affordance,
            because nothing threw — without the row a silently stalled console
            can only be recovered by reloading the browser.

     held   NOTHING. `L` is a state the operator chose; the panel marks
            already say `Held`, and an alarm over a deliberate act is an
            alarm about the operator.

   The verb is per-panel and re-reads THAT source. The page-wide `Retry 4`
   this replaces asked an operator to accept four re-reads to fix the one
   panel they were looking at, from 1300px away.
   ========================================================================== */

const FAULT_LEAD: Partial<Record<MarkState, string>> = {
  down: 'Unreachable',
  stale: 'Not refreshing',
  late: 'No reply from',
};

function PanelFault({
  feed,
  mark,
  onRetry,
}: {
  feed: Feed;
  mark: Mark;
  onRetry: (feed: Feed) => void;
}) {
  const lead = FAULT_LEAD[mark.state];
  if (lead === undefined) return null;
  /* Why, in the source's own words where it threw, and in the only words
     available where it did not: how long we have waited against how often we
     are supposed to read. */
  const why = mark.state === 'late'
    ? `nothing for ${mark.ageMs === null ? '?' : duration(mark.ageMs, { compact: 1 })}`
      + ` · polls every ${everyShort(feed.everyMs)}`
    : feed.detail;
  /* When the figures under this row were last true. Omitted where there are
     none — a source that has never answered has no last-good instant, and
     printing one is how a console comes to claim a poll at 00:00:00Z. */
  const stamp = feed.at === null ? null : new Date(feed.at).toISOString();
  /* The whole sentence, once. It is the row's accessible name and its title,
     so the message is announced and hoverable in FULL at every width — and
     the spans below are free to shed down to a 258px rail panel without a
     screen reader or a mouse ever losing the verbatim string. */
  const said = `${lead} ${feed.endpoint} — ${why}`
    + (feed.raw === null || mark.state === 'late' ? '' : ` (${feed.raw})`)
    + (stamp === null || mark.state === 'down' ? '' : ` · last read ${utc(stamp)}`);
  return (
    <div
      className={styles.faultRow}
      data-tone={mark.state === 'late' ? 'warn' : 'error'}
      /* `status`, not `alert`. One dead origin can put this row in seven
         panels at once, and seven assertive interruptions is a klaxon, not a
         report — the same reason §7 bans a permanently blinking alarm. Polite
         and queued: every one is still announced, in the order they appear,
         without cutting off whatever the operator was reading. */
      role="status"
      aria-label={said}
      title={said}
    >
      {/* §5, level 3: a full-height 2px tick at the row's leading edge, in the
          tone of the thing it reports. It is the row's only fill. */}
      <span className={styles.faultEdge} aria-hidden="true" />
      <span className={styles.faultLead} aria-hidden="true">{lead}</span>
      <span className={styles.failWhere} aria-hidden="true">{feed.endpoint}</span>
      {/* Two spellings of the same message, one visible at a time: a 412px
          rail panel cannot hold `Failed to fetch` after the verb, the path
          and the verb-button, and a message cut to `Failed to ` is worse than
          the server's own status word. The swap is a container query on the
          row itself, so it answers to the PANEL's width rather than to the
          viewport's — the same row is 1131px wide under `Needs attention`. */}
      <span className={styles.failWhy} aria-hidden="true">{why}</span>
      <span className={styles.failWhyShort} aria-hidden="true">
        {mark.state === 'late' ? duration(mark.ageMs ?? 0, { compact: 1 }) : reasonShort(feed.detail)}
      </span>
      {stamp !== null && mark.state !== 'down' && (
        <time className={styles.failWhen} dateTime={stamp} aria-hidden="true">
          last {utc(stamp).slice(11)}
        </time>
      )}
      {/* ONE RECOVERY ON THE SURFACE, AND IT IS NOT HERE.

          A dead origin puts this row in seven panels at once, and seven Retry
          buttons in one 1600px frame is a screen that does not know which of
          its verbs is the real one — `components/states`' third rule, and the
          reason the outage banner exists. So a broken read gets a LINE here
          and its verb upstairs.

          `late` is the exception, and it is the one state that earns it:
          nothing threw, so there is no fault for the banner to carry and no
          banner appears. Without this button a silently stalled console could
          only be recovered by reloading the browser. */}
      {mark.state === 'late' && (
        <button
          type="button"
          className={styles.faultRetry}
          onClick={() => onRetry(feed)}
          title={`Re-read ${feed.endpoint}`}
        >
          <Icon name="refresh" size={12} />
          Retry
        </button>
      )}
    </div>
  );
}

/** Are we holding an answer from this source at all? */
function known(feed: Feed): boolean {
  return feed.ok || feed.stale;
}

/** The two ways a figure can be absent, told apart: `…` we have not asked yet,
    `—` we asked and cannot know. Neither of them is `0`. */
function blank(feed: Feed): string {
  return feed.loading ? '…' : NO_VALUE;
}

/** A count that is only a count while we can see it. Never `(0)` on faith. */
function countOf(feed: Feed, n: number): number | string {
  return known(feed) ? n : blank(feed);
}

/** `23/28`, `—/—`. A fraction whose parts come from different feeds reads as
    unknown the moment either of them does. */
function fraction(a: number, b: number, ...feeds: readonly Feed[]): string {
  if (!feeds.every(known)) {
    const b0 = blank(feeds.find((f) => !known(f))!);
    return `${b0}/${b0}`;
  }
  return `${a}/${b}`;
}

/* --------------------------------------------------------------------------
   A width read off the CONTAINER, never off the content inside it.

   This is the hook the responsive column policy hangs on, and the contract is
   the whole point: it reports `contentRect` of a box whose width is set by the
   page grid (`minmax(0, 1fr)`), and it never writes layout of any kind. A
   consumer that picks its column set from this number therefore cannot feed
   its own output back into the measurement — the set is a pure function of the
   viewport, so a table holding still for an hour lays out exactly once.
   -------------------------------------------------------------------------- */
/* --------------------------------------------------------------------------
   WHOLE ROWS, OR NONE.

   Both bottom scrollers used to take a `1fr` share of whatever the viewport
   had left, which is a number with no relationship to the 26px row pitch
   inside them: at 1600×1000 the exception table got 247px and the ledger
   232px, so both ended their last row 9px and 23px into it and the page
   finished on a sliced band of half-glyphs in every frame, at every width.

   The clip is quantised instead. A table's visible height is the 22px sticky
   header plus a whole number of rows; the ≤25px that will not make another row
   stays as the panel's own ground under the last one, where it reads as the
   panel's bottom inset rather than as a row that has been cut in half.

   MEASURED ON THE PARENT, APPLIED TO THE CHILD — and that is load-bearing, not
   style: constraining the box you are observing is a feedback loop, and a
   ResizeObserver in a loop with its own output is how a table comes to
   oscillate by one row forever. The outer box takes the grid's height and is
   never written to; the inner box takes the quantised ceiling.
   -------------------------------------------------------------------------- */

/** `DataTable`'s sticky header, and its single-line row (`--h-row`). */
const THEAD_PX = 22;
const ROW_PX = 26;

/** The tallest `22 + 26n` that fits in `h`, or `undefined` while nothing has
    been measured — an unmeasured box keeps the height the grid gave it rather
    than collapsing to one row for a frame. */
function wholeRows(h: number): number | undefined {
  if (h <= 0) return undefined;
  const rows = Math.floor((h - THEAD_PX) / ROW_PX);
  return rows < 1 ? undefined : THEAD_PX + rows * ROW_PX;
}

/** The same observer as `useBoxWidth`, reporting the other axis. */
function useBoxHeight(): [(el: HTMLDivElement | null) => void, number] {
  return useBoxSize('height');
}

function useBoxWidth(): [(el: HTMLDivElement | null) => void, number] {
  return useBoxSize('width');
}

/**
 * One observer, one axis, and NO `useEffect` — which is the whole fix.
 *
 * Both of these hooks used to disconnect in an effect cleanup
 * (`useEffect(() => () => obs.current?.disconnect(), [])`). Under React 18's
 * StrictMode every effect is mounted, unmounted and mounted again on the first
 * commit, so that cleanup ran once at startup and tore the observer down while
 * the element was still on screen — and nothing ever re-created it, because
 * the setup half does no work. Every width and height on this surface was
 * therefore whatever it measured in its first commit, FOREVER: the exception
 * table's responsive column set, the estate's bar cap, the history panel's
 * header policy and the two bottom clips all stopped answering the page the
 * moment it finished loading. It was invisible in a static screenshot and
 * wrong in every one of them — the ledger's clip was computed against a 385px
 * box that had already become a 228px box.
 *
 * A callback ref is called with `null` when the element leaves, so that is the
 * only teardown this needs and it is the one React guarantees.
 */
function useBoxSize(axis: 'width' | 'height'): [(el: HTMLDivElement | null) => void, number] {
  const [v, setV] = useState(0);
  const obs = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    obs.current?.disconnect();
    obs.current = null;
    if (el === null) return;
    const box = el.getBoundingClientRect();
    setV(axis === 'width' ? box.width : box.height);
    const o = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r !== undefined) setV(axis === 'width' ? r.width : r.height);
    });
    o.observe(el);
    obs.current = o;
  }, [axis]);
  return [ref, v];
}

/** The viewport's height. Nothing on this page can change it, which is what
    makes it a safe budget to derive the band's bank count from — measuring the
    band's own box would be a loop. */
function useViewportHeight(): number {
  const [h, setH] = useState(() => window.innerHeight);
  useEffect(() => {
    const on = () => setH(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return h;
}

/* --------------------------------------------------------------------------
   Derivations. All of them are counts or means over readings that exist.
   -------------------------------------------------------------------------- */

interface History {
  /** Bucket start times, epoch ms, one per COMPLETE hour in the window. */
  times: number[];
  /** Telemetry rows that arrived in each bucket, fleet-wide. */
  readings: number[];
  /** Distinct robots that sent at least one row in each bucket. */
  reporters: number[];
  /** Commands issued in each bucket, fleet-wide. */
  issued: number[];
  /** Mean battery over the rows in each bucket. null where none arrived. */
  battery: (number | null)[];
  from: number;
  to: number;
  /** Robots that contributed at least one reading. */
  reporting: number;
  /** Robots whose history hit the sample cap — they set the window's start. */
  capped: number;
  /** WHICH SOURCE THE X-AXIS CAME OFF. The window is normally derived from the
      telemetry the fan-out returned; with no telemetry at all the ledger's own
      timestamps are the only clock left, and with neither the ruler is a
      nominal 24 h that describes the fallback and not the fleet. The header's
      span label is only printed for the first two. */
  source: 'telemetry' | 'commands' | 'nominal';
}

/**
 * The window every reporting robot actually covers.
 *
 * A per-robot call returns at most `TELEMETRY_SAMPLES` rows, so a busy robot's
 * history reaches back less far than a quiet one's. Bucketing over a nominal
 * "last 24 h" would therefore undercount the early hours and draw a ramp that
 * is an artefact of the page size, not of the fleet. The honest start is the
 * LATEST first-sample among the capped robots — before that instant at least
 * one robot's rows are missing, so no total is trustworthy.
 */
function historyWindow(
  byRobot: Map<string, TelemetryReading[]>,
  commands: readonly Command[],
  now: number,
): History {
  let start = -Infinity;
  let earliest = Infinity;
  let reporting = 0;
  let capped = 0;

  for (const rows of byRobot.values()) {
    if (rows.length === 0) continue;
    reporting += 1;
    /* Newest first, per the API contract. */
    const oldest = Date.parse(rows[rows.length - 1]!.ts);
    if (Number.isNaN(oldest)) continue;
    earliest = Math.min(earliest, oldest);
    if (rows.length >= TELEMETRY_SAMPLES) {
      capped += 1;
      start = Math.max(start, oldest);
    }
  }
  let source: History['source'] = 'telemetry';
  if (!Number.isFinite(start)) {
    if (Number.isFinite(earliest)) {
      start = earliest;
    } else {
      /* Nothing reported. The command ledger is a real series with real
         timestamps and it is still in hand, so it — not a nominal day — sets
         the axis it is about to be drawn on. Clamped to a day so a ledger that
         reaches back a fortnight does not turn a throughput panel into a
         calendar. */
      let issuedFirst = Infinity;
      for (const c of commands) {
        const t = Date.parse(c.issued_at);
        if (Number.isFinite(t)) issuedFirst = Math.min(issuedFirst, t);
      }
      if (Number.isFinite(issuedFirst)) {
        start = Math.max(issuedFirst, now - 24 * HOUR);
        source = 'commands';
      } else {
        start = now - 24 * HOUR;
        source = 'nominal';
      }
    }
  }

  /* Only complete hours. The hour in progress is a partial count and would
     draw a cliff at the right edge that is not a drop in throughput. */
  const to = Math.floor(now / HOUR) * HOUR;
  const from = Math.min(to, Math.ceil(start / HOUR) * HOUR);

  const times: number[] = [];
  for (let t = from; t < to; t += HOUR) times.push(t);
  if (times.length === 0) times.push(from);

  const n = times.length;
  const counts = new Array<number>(n).fill(0);
  const sums = new Array<number>(n).fill(0);
  const batteryN = new Array<number>(n).fill(0);
  const reporters = new Array<number>(n).fill(0);
  const issued = new Array<number>(n).fill(0);

  const bucket = (t: number): number => {
    if (Number.isNaN(t) || t < from || t >= to) return -1;
    const i = Math.floor((t - from) / HOUR);
    return i < 0 || i >= n ? -1 : i;
  };

  for (const rows of byRobot.values()) {
    /* One robot may not count twice towards coverage in the same hour. */
    const seen = new Set<number>();
    for (const row of rows) {
      const i = bucket(Date.parse(row.ts));
      if (i < 0) continue;
      counts[i]! += 1;
      seen.add(i);
      if (row.battery_pct != null && !Number.isNaN(row.battery_pct)) {
        sums[i]! += row.battery_pct;
        batteryN[i]! += 1;
      }
    }
    for (const i of seen) reporters[i]! += 1;
  }

  for (const c of commands) {
    const i = bucket(Date.parse(c.issued_at));
    if (i >= 0) issued[i]! += 1;
  }

  return {
    times,
    readings: counts,
    reporters,
    issued,
    battery: counts.map((_, i) => (batteryN[i]! === 0 ? null : sums[i]! / batteryN[i]!)),
    from,
    to,
    reporting,
    capped,
    source,
  };
}

/**
 * A y-ceiling for a COUNT that `Plot`'s tick chooser cannot label fractionally.
 *
 * `Plot` pads an unpinned axis by 8% and then picks a "nice" step from
 * {1, 2, 2.5, 5, 10} × 10ⁿ. Six commands in the busiest hour therefore becomes
 * a 0–6.48 axis, a step of 2.5, and a gridline reading `2.5` — two and a half
 * commands issued in an hour, on a series whose values are integers by
 * construction. Pinning the ceiling to the count itself removes the padding
 * that reaches that multiplier; 7 is the one integer ceiling that still lands
 * in the 2.5 band at a unit magnitude (7/3 = 2.33), so it takes the next.
 */
function countMax(values: readonly number[]): number {
  const peak = values.length === 0 ? 0 : Math.max(...values);
  const hi = Math.max(2, Math.ceil(peak));
  return hi === 7 ? 8 : hi;
}

/** Newest-first battery trace for one robot, thinned to ~48 points. */
function batteryTrace(rows: readonly TelemetryReading[] | undefined): number[] {
  if (rows === undefined || rows.length === 0) return [];
  const step = Math.max(1, Math.ceil(rows.length / 48));
  const out: number[] = [];
  for (let i = rows.length - 1; i >= 0; i -= step) {
    const v = rows[i]!.battery_pct;
    if (v != null && !Number.isNaN(v)) out.push(v);
  }
  return out;
}

const TONE_RANK: Partial<Record<Tone, number>> = {
  error: 4, offline: 3, maintenance: 2, warn: 1,
};

/** The single worst reading a robot is carrying, projected onto the health scale. */
function worstTone(e: FleetException | undefined): Tone {
  if (e === undefined) return 'nominal';
  let worst: Tone = 'nominal';
  for (const r of e.reasons) {
    if ((TONE_RANK[r.tone] ?? 0) > (TONE_RANK[worst] ?? 0)) worst = r.tone;
  }
  return worst;
}

function metaString(r: Robot, key: string): string | null {
  const v = r.meta[key];
  return typeof v === 'string' ? v : null;
}

function siteOf(r: Robot): string {
  return metaString(r, 'site') ?? 'unassigned';
}

interface Group {
  key: string;
  label: string;
  robots: Robot[];
  live: number;
  attention: number;
  /** Worst-first census of the group, for the mix bar. */
  mix: { tone: Tone; n: number }[];
  worst: Tone;
  battery: number | null;
  /** Days until the first certificate in this group expires. */
  certDays: number | null;
  /** Distinct firmware strings running in this group. */
  firmware: string[];
}

const MIX_ORDER: readonly Tone[] = ['error', 'offline', 'maintenance', 'warn', 'nominal'];

/* The census bar's own legend, in the words MATRIX_KEY uses, so the bar and
   the matrix beside it name the same five colours the same way. */
const MIX_LABEL: Partial<Record<Tone, string>> = {
  error: 'fault',
  offline: 'no data',
  maintenance: 'held',
  warn: 'not current',
  nominal: 'nominal',
};

function buildGroups(
  robots: readonly Robot[],
  worstBy: Map<string, Tone>,
  order: readonly string[] | null,
  of: (r: Robot) => string,
): Group[] {
  const bins = new Map<string, Robot[]>();
  for (const r of robots) {
    const k = of(r);
    const list = bins.get(k);
    if (list === undefined) bins.set(k, [r]); else list.push(r);
  }

  const keys = order === null
    ? [...bins.keys()].sort((a, b) => (bins.get(b)!.length - bins.get(a)!.length) || a.localeCompare(b))
    : order.filter((k) => bins.has(k));

  return keys.map((key) => {
    const members = bins.get(key) ?? [];
    const census = new Map<Tone, number>();
    let live = 0;
    let attention = 0;
    let worst: Tone = 'nominal';
    let battSum = 0;
    let battN = 0;
    let certDays: number | null = null;
    const fw = new Set<string>();

    for (const r of members) {
      const tone = worstBy.get(r.robot_id) ?? 'nominal';
      census.set(tone, (census.get(tone) ?? 0) + 1);
      if ((TONE_RANK[tone] ?? 0) > (TONE_RANK[worst] ?? 0)) worst = tone;
      if (tone !== 'nominal') attention += 1;
      if (r.liveness === 'live' || r.liveness === 'backfilling') live += 1;
      if (r.battery_pct != null) { battSum += r.battery_pct; battN += 1; }
      if (r.cert_not_after != null) {
        const d = (Date.parse(r.cert_not_after) - Date.now()) / 86_400_000;
        if (!Number.isNaN(d)) certDays = certDays === null ? d : Math.min(certDays, d);
      }
      if (r.firmware != null) fw.add(r.firmware);
    }

    return {
      key,
      label: key,
      robots: members,
      live,
      attention,
      mix: MIX_ORDER.map((tone) => ({ tone, n: census.get(tone) ?? 0 })).filter((s) => s.n > 0),
      worst,
      battery: battN === 0 ? null : battSum / battN,
      certDays,
      firmware: [...fw].sort(),
    };
  });
}

/** The estate is grouped by exactly the same function the Groups panel uses,
    and that is the point of it being one function: a row under `AGENT BUILD`
    and a row under `SITE` are then the same object with the same census, the
    same worst reading and the same headcount, so the two panels cannot
    disagree about how many robots are outside a limit on `3.0.0-rc2`. The cut
    is the only difference — a bad build is a fault that runs across every site
    and every morphology, which is the one thing neither of those groupings can
    show. */

/* --------------------------------------------------------------------------
   Site geometry.

   Three sites on two continents, so a single world map is three dots and no
   information. What an operator can actually use is each site's OWN frame: the
   robots' true positions relative to their site's centroid, at one scale, with
   a scale bar naming it. The projection is a local tangent plane — good to
   centimetres over a yard, which is two orders of magnitude finer than the
   spread it is drawing.
   -------------------------------------------------------------------------- */

/** Width ÷ height of the plot box, enforced by `.siteBox { aspect-ratio }`.
    Both axes divide by the same metres-per-pixel, so the shape is true. */
const SITE_ASPECT = 1.7;
const M_PER_DEG_LAT = 110_574;

interface SiteDot {
  robot: Robot;
  tone: Tone;
  /** Fraction of the box, 0–1, y measured downwards. */
  x: number;
  y: number;
}

interface SiteGeo {
  key: string;
  label: string;
  total: number;
  dots: SiteDot[];
  /** Metres across the whole box — what the scale bar is derived from. */
  spanM: number;
  attention: number;
  worst: Tone;
}

function buildSites(
  groups: readonly Group[],
  worstBy: Map<string, Tone>,
): SiteGeo[] {
  return groups.map((g) => {
    const fixed = g.robots.filter((r) => r.lat != null && r.lon != null);
    const label = (fixed[0] ?? g.robots[0]) === undefined
      ? g.label
      : metaString((fixed[0] ?? g.robots[0])!, 'site_label') ?? g.label;

    if (fixed.length === 0) {
      return {
        key: g.key, label, total: g.robots.length, dots: [], spanM: 0,
        attention: g.attention, worst: g.worst,
      };
    }

    const lat0 = fixed.reduce((a, r) => a + r.lat!, 0) / fixed.length;
    const lon0 = fixed.reduce((a, r) => a + r.lon!, 0) / fixed.length;
    const mPerLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);

    const xs = fixed.map((r) => (r.lon! - lon0) * mPerLon);
    const ys = fixed.map((r) => (r.lat! - lat0) * M_PER_DEG_LAT);
    const minX = Math.min(...xs); const maxX = Math.max(...xs);
    const minY = Math.min(...ys); const maxY = Math.max(...ys);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    /* The vertical reference: the larger of the two extents, padded, floored
       so a site with one robot still draws a sane 100 m box. The 1.32 leaves
       room for the 8px heading tick on a robot parked at the frame's edge. */
    const ref = Math.max(maxX - minX, maxY - minY, 100) * 1.32;

    return {
      key: g.key,
      label,
      total: g.robots.length,
      spanM: ref * SITE_ASPECT,
      attention: g.attention,
      worst: g.worst,
      dots: fixed.map((r, i) => ({
        robot: r,
        tone: worstBy.get(r.robot_id) ?? 'nominal',
        x: 0.5 + (xs[i]! - cx) / (ref * SITE_ASPECT),
        y: 0.5 - (ys[i]! - cy) / ref,
      })),
    };
  });
}

/** A round distance close to a third of the box, and where it lands as a
    fraction — a cartographic scale bar, not a decorative rule. The 1-2-5
    ladder rounds at the geometric midpoints, so the bar lands near a third of
    the box rather than over half of it. */
function scaleBar(spanM: number): { label: string; frac: number } | null {
  if (!(spanM > 0)) return null;
  const target = spanM / 3;
  const mag = 10 ** Math.floor(Math.log10(target));
  const n = target / mag;
  const step = (n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10) * mag;
  return {
    label: step >= 1000
      ? `${(step / 1000).toFixed(step % 1000 === 0 ? 0 : 1)} km`
      : `${Math.round(step)} m`,
    frac: step / spanM,
  };
}

/* --------------------------------------------------------------------------
   Two hand-built instruments: the fleet texture below and the site plot after
   it. Neither exists as a primitive; both are noted in the hand-off.
   -------------------------------------------------------------------------- */

/** Roving-tabindex arrow handling shared by the two dot/bar groups below. */
function rove(
  e: KeyboardEvent<HTMLElement>,
  i: number,
  count: number,
  go: (n: number) => void,
): void {
  const map: Record<string, number> = {
    ArrowRight: i + 1, ArrowDown: i + 1,
    ArrowLeft: i - 1, ArrowUp: i - 1,
    Home: 0, End: count - 1,
  };
  const next = map[e.key];
  if (next === undefined) return;
  e.preventDefault();
  go(Math.max(0, Math.min(count - 1, next)));
}

/* ==========================================================================
   FleetTexture — the whole fleet, as one full-width band.

   THE BAR §8 tell 11: at fleet scale the screen must not scroll and must not
   grow rows. Six readings per robot are painted as texture — five metric rows
   of cells, and the operational MODE as its own ribbon underneath, the way
   `inorbit-fleet-status-matrix.png` does it. A name is read only where the
   texture breaks.

   FOUR THINGS THIS DOES THAT A PLAIN MATRIX CANNOT
   ------------------------------------------------
   0. IT FITS. The pitch is not 19px, it is *derived* — 19px is the ceiling. The
      band measures its own track and the row-label column beside it, then hands
      the roster whatever pitch divides into what is left, down to a 6px floor;
      under that it wraps into stacked banks of equal pitch, for as many banks
      as a share of the viewport's height can hold. Nothing scrolls, nothing
      hides behind an `overflow: auto` with no scrollbar, and on the day the
      roster genuinely outgrows the last bank the caption prints the shortfall
      in `--warn` beside the reading count. 150 robots is one 1350px bank of
      6px cells at 1600 — the whole fleet, no interaction. See `geometry`.

   1. COLUMN GROUPS. Re-ordering columns and marking nothing makes the control
      that re-ordered them unreadable. Every ordering here groups the columns
      by the thing it sorted on — morphology for `Id`, the worst reading for
      `Worst`, the site for `Site` — and each run gets a 1px rule through the
      whole band, a 2px worst-reading tick and a mono label with its headcount.
      "Which site is on fire" is answerable without opening anything.

   2. THE MODE RIBBON. Mode is the one categorical reading a fleet view must
      not flatten: `matrixTone` collapses active, idle and charging to one
      steel so the two faults stay findable, which is right for a metric row
      and wrong for "what is the fleet doing". Lifting mode out of the cells
      into a 28px ribbon lets it keep its own five hues, and costs the grid
      nothing — the reading count is identical.

   3. THE CENSUS, AND THE CHARGE PROFILE UNDER IT. The band spans the whole
      panel grid: ~1440px at 1600, ~1700 at 1920. Twenty-eight robots need
      532px of that, so the surplus carries each row's own census — the same
      colours as the ink beside it, the counts named in words, and a
      100 %-stacked bar whose segment boundaries line up down the six rows.
      Hovering a column lights the bucket that robot falls in on every row at
      once. Under it, in the height the rotated id track opens up on the other
      side, the fleet's charge as a 20-bin distribution: the one reading four
      census buckets cannot shape. As the roster grows, the texture first
      COMPRESSES to keep the census — down to a 12px pitch — and only then
      sheds it and takes the whole band. Neither the band nor the census is
      ever left holding pixels it is not painting.

   `StatusMatrix` has none of these (no column groups, no ribbon row, no
   per-row aside, no derived pitch), which is why this is local rather than a
   call to it. See the hand-off note.
   ========================================================================== */

/* ---- geometry: the pitch is DERIVED, never assumed ----------------------
   The band's one job is zero-interaction whole-fleet visibility, so the
   roster's size decides the pitch — the pitch does not decide how much of the
   roster you get. 19px is the ceiling (the InOrbit benchmark: a 13px cell on a
   19.4px pitch, and it is 19.4 there only because that frame had 1675px for 87
   robots). Below the 6px floor a cell stops being a cell, so the band wraps
   into stacked banks of EQUAL pitch rather than compressing further, and only
   past the last bank does it drop columns — loudly, in the caption. Nothing on
   this band ever scrolls. */

/** The benchmark pitch, and the widest a cell track is ever painted. */
const MAX_PITCH = 19;
/** The narrowest. Under this the ink is thinner than the gutter beside it. */
const MIN_PITCH = 6;
/** Hard cap on stacked banks, whatever the height budget says. */
const MAX_BANKS = 4;
/** The band's share of the viewport, which is what actually decides how many
    banks it may stack. Deriving this rather than fixing it is the same rule as
    the pitch: a band that stacks a bank the page cannot hold has not shown the
    roster, it has hidden a third of it under the panel below. */
/* Measured, not chosen: the page grid gives band 1 an `auto` row between a
   fixed 258px band and a `minmax(190px, 1fr)` exception table, so what is
   actually available to it at 1000px of viewport is ~320px — a band that
   takes more does not push the page taller, it has its last bank's MODE
   ribbon clipped off by the panel edge with nothing saying so. 0.3 is that
   reserve expressed as a share, and it is a CEILING, not a target. */
const BAND_SHARE = 0.3;
const BAND_MIN_H = 200;
const BAND_MAX_H = 460;
/** The group-label strip above each bank's cells. */
const GROUPS_H = 15;
/** The seam between two banks — `--sp-4`, matching the stylesheet. */
const BANK_GAP = 8;
/** The rotated-id track under a single bank: `--w-collabel` plus its `--sp-2`
    inset, measured off `.texNames`. Only ever paid when `showNames`. */
const NAMES_H = 76;
/** The caption line and the gap under it — `.texCap` at `--lh-mono`, then the
    stylesheet's `--sp-3`. */
const CAP_H = 24;
/** What the Panel wraps the texture in: a 28px header, its two hairlines and
    the body's `--pad-panel` top and bottom. Measured, not assumed: the band's
    grid row is `auto` and CSS grid WILL shrink an `auto` row below its content
    when the rows above and below it have already claimed the container — so
    the row is given an explicit floor (`--band-h`) computed from these, and a
    floor that is 20px light is a clipped MODE ribbon with nothing saying so. */
const PANEL_CHROME = 50;
/** What the row is given before any geometry has been derived: five metric
    rows and a ribbon at the benchmark pitch, the rotated id track, the caption
    and the panel's own chrome — 138 + 76 + 24 + 50. */
const BAND_DEFAULT_H = 288;

/** The gutter is a fixed FRACTION of the pitch, so the 13 : 6 ink-to-gap ratio
    the benchmark has survives compression instead of the ink vanishing into a
    constant 6px gutter: 19 → 13, 14 → 10, 9 → 6, 6 → 4. */
function cellOf(pitch: number): number {
  return pitch - Math.min(6, Math.max(1, Math.round(pitch * 0.32)));
}

/** How tall a cell of that width is drawn. Square at the benchmark; floored at
    9px so a compressed column is a short bar, never a hairline. */
function cellHeightOf(cell: number): number {
  return Math.max(9, Math.min(13, cell));
}

/** What the band paints for `n` robots in `track` px of column room. */
interface Geometry {
  /** Columns in a bank. The last bank may hold fewer. */
  cols: number;
  banks: number;
  /** Column pitch — the cell plus its gutter. */
  pitch: number;
  cell: number;
  cellH: number;
  /** Row pitch. Independent of the column pitch: rows have their own budget. */
  rowPitch: number;
  ribbon: number;
  ribbonInk: number;
  /** What the cells and their bank labels cost vertically. */
  bandHeight: number;
  /** Robots the band actually paints. */
  shown: number;
  /** Robots it could not. Printed — never silently clipped. */
  hidden: number;
}

/**
 * `n` robots, `track` px of column room, `budget` px of band height.
 *
 * One bank at the widest pitch that fits, then — only once the cell would go
 * under the floor — a second bank, a third, for as long as the height budget
 * can hold one. What is left over after the last bank the caption prints.
 */
function geometry(n: number, track: number, budget: number): Geometry {
  /* `want` is the balanced column count — n split evenly over `banks`. The
     pitch is derived from THAT, so every bank is drawn at the same pitch, and
     then each bank is FILLED to what the track can actually hold at it. The
     two differ because the pitch is an integer: 400 robots over two banks
     balances at 200 columns, which at a 6px pitch paints 1 200 of 1 343
     available px and leaves 143 of them holding nothing. Filling instead makes
     the last bank the only ragged one — the way a paragraph wraps — and can
     cost a whole bank less height. */
  const fit = (want: number, banks: number): Geometry => {
    const balanced = Math.max(1, want);
    const pitch = Math.max(
      MIN_PITCH,
      Math.min(MAX_PITCH, Math.floor(track / balanced) || MAX_PITCH),
    );
    const cols = Math.max(1, Math.min(n || 1, Math.floor(track / pitch) || balanced));
    const used = Math.max(1, Math.min(banks, Math.ceil((n || 1) / cols)));
    const cell = cellOf(pitch);
    const cellH = cellHeightOf(cell);
    const rowPitch = cellH + 6;
    const ribbon = rowPitch + 9;
    return {
      cols, banks: used, pitch, cell, cellH, rowPitch,
      ribbon,
      ribbonInk: rowPitch + 1,
      bandHeight: used * (GROUPS_H + CELL_METRICS.length * rowPitch + ribbon)
        + (used - 1) * BANK_GAP,
      shown: Math.min(n, cols * used),
      hidden: Math.max(0, n - cols * used),
    };
  };
  /* Pre-measurement: draw at the benchmark, and let the observer correct it on
     the same frame. Guessing small here would flash a compressed band. */
  if (track <= 0 || n === 0) return fit(Math.max(n, 1), 1);
  const perBank = Math.max(1, Math.floor(track / MIN_PITCH));
  const want = Math.min(MAX_BANKS, Math.max(1, Math.ceil(n / perBank)));
  /* One bank always renders, budget or no budget — it is the panel. Every bank
     after it has to earn its height against what the page can actually give,
     and a bank that would be drawn past the panel's edge is worse than the
     shortfall printed on the caption line: the clipped one looks complete. */
  let best = fit(Math.min(perBank, n), 1);
  for (let b = 2; b <= want; b += 1) {
    const g = fit(Math.min(perBank, Math.ceil(n / b)), b);
    if (g.bandHeight > budget) break;
    best = g;
  }
  return best;
}

/** The row-label track before it has been measured — `MODE`/`LINK`/`BATT` at
    10px/600 uppercase. It IS measured (the pitch is derived from what is left
    after it, and a 30px error there is three columns clipped off the end), but
    a first paint needs a number. */
const LABEL_COL = 69;
/** The grid's own column gap, `--sp-4`. */
const LABEL_GAP = 8;
/** The rule between the texture and its census, and its two insets. */
const CENSUS_GAP = 17;
/** What the census's five fixed slots need before the words start clipping.
    Below this the whole census sheds and the texture takes the band: at 150
    robots the ink is the answer, and a clipped state word is the a11y carrier
    of the state gone. */
const CENSUS_MIN = 460;
/** And what it needs before the STACKED BAR is worth drawing beside the words.
    The bar sheds first because it is the redundant half — every figure it
    encodes is printed as a number two centimetres to its left. */
const CENSUS_BAR_MIN = 620;
/** The pitch the TEXTURE will compress to in order to keep its census. Above
    this the census is worth more than the extra 5px of gutter; below it the
    ink is the answer and the census goes. Without this rule the band has a
    dead zone — at 1600 a 60-robot fleet is 1140px of cells, 211px short of the
    census's floor, so the census sheds and 211px of the band paints nothing.
    Compressing to 14px instead buys the census back and spends nothing. */
const CENSUS_KEEP_PITCH = 12;
/** Characters of a robot id that survive in the rotated label track. Anything
    longer is middle-ellipsed so the discriminating END stays — `jetrover-01`,
    never eleven identical columns reading `jetrover…`. */
const NAME_CHARS = 11;
/** Columns past which the rotated id track is 76px spent on a wall of vertical
    strings nobody can read anyway, and the first thing the band should sell
    for pitch. The readout on the caption line names the pointed-at robot at
    any width, so nothing is lost but the wall. */
const NAME_MAX_COLS = 40;
/** And the pitch below which an 11-char id set vertically stops resolving. */
const NAME_MIN_PITCH = 15;
/* ---- naming a run that is narrower than its own name --------------------
   A run in the group strip can be one column wide — under `Worst` ordering
   `no data` and `held` are routinely a single robot, 19px at the benchmark
   pitch and 9px at 150 robots. The strip therefore has two hard constraints
   that pull against each other, and only one of them is negotiable:

     · a label MUST sit over its own run. A group header is a claim about
       which columns it counts, so a label that has drifted onto the next
       run's columns is not a cramped label, it is a WRONG one — and it is
       wrong in the ordering an operator uses during an incident.
     · a label would LIKE to be its full name.

   An earlier version let the labels flow — each placed at its own anchor or
   at the end of the previous label, whichever was further right, with a
   leader drawn back to the anchor. That reads fine on paper and is a lie on
   the pixels: measured at 1600 in `Worst`, `held 1` landed 49.6px past its
   own column, directly over two `not current` robots, and `not current 7`
   straddled into `nominal`. Three of five labels pointed at the wrong
   robots, and at 150 columns the drift makes the whole strip meaningless.

   So position is fixed and the WORDS give way. Every run is a box at exactly
   `start * pitch` for exactly `count * pitch`, `overflow: hidden`, and the
   text inside it steps down a two-rung ladder: full name and count, then the
   name alone, then NOTHING. No label can reach another run's columns, because
   no label can leave its own box.

   TWO RUNGS, AND THE STUBS ARE GONE. The ladder used to bottom out at three,
   two and one characters, which on the fleet's own default severity sort put
   `nd` over the one no-data column and `he` over the one held column — two
   letters that name neither the group nor anything else, in a strip whose
   whole job is to say which robots a run counts. An abbreviation an operator
   has to decode is worse than a mark they can read directly, and there is
   already a better mark available: a run too narrow for its own name draws its
   EXTENT RULE in its own worst tone instead of in the neutral hairline, so the
   held run is a purple rule over its column and the no-data run a grey one —
   §5 level 3, and the census key 20px to the right names both hues in words.

   So a run is named three further ways and never guessed at: by the hue of its
   rule and its tick, both in the key; by the box's `title` and `aria-label`,
   which always carry the full string; and by pointing at it, which washes
   exactly the columns it counts and prints `group  held  1 robot` on the
   caption line. */

/** The string whose rendered width divided by its length IS the strip's
    character advance. Ten digits, in the strip's own face and size. */
const GROUP_PROBE = '0123456789';
/** The words' inset from the run's own leading edge. The 2px tone tick does
    NOT come out of this budget — it is 4px tall in the lane above the words,
    so it costs the text no horizontal room; the inset exists only to keep the
    first glyph off the boundary rule. */
const GROUP_PAD = 4;
/** The 1px seam kept clear at the run's trailing edge so two adjacent boxes
    read as two boxes. */
const GROUP_SEAM = 1;

/** A run the pointer is parked on, in the band's own coordinates. */
interface GroupPick {
  /** Which bank — a run never crosses a bank seam, so this identifies it. */
  bank: number;
  /** Column index of the run's first robot, within that bank. */
  start: number;
  count: number;
  label: string;
  tone: Tone;
}

interface PlacedGroup {
  g: ColGroup;
  /** True position of the run's first column — where the tick is painted. */
  anchor: number;
  /** The run's own width, and the hard clip on everything inside it. */
  span: number;
  /** The words the box can actually hold. Empty when it can hold none. */
  name: string;
  /** The headcount, glued to the name — dropped one rung before the name is. */
  count: string;
}

/**
 * Every run boxed at its true extent; the text fitted to that box.
 *
 * `ch` is the measured advance of one character of the strip's own mono face,
 * so this does not guess at a font it cannot see.
 */
function placeGroups(
  groups: readonly ColGroup[],
  pitch: number,
  ch: number,
): { placed: PlacedGroup[]; dropped: number; crowded: boolean } {
  const placed: PlacedGroup[] = [];
  let dropped = 0;
  /* A bank whose runs average under four characters of room is not a strip of
     labels — past that line the words go wholesale and the strip reports its
     run count instead. The toned extent rules, the ticks and the hover
     readout never go, so every run is still marked and still nameable. */
  const track = groups.reduce((n, g) => n + g.count, 0) * pitch;
  const crowded = groups.length > 1 && track / groups.length < 4 * ch + GROUP_PAD;
  if (crowded) {
    for (const g of groups) {
      placed.push({
        g,
        anchor: g.start * pitch,
        span: Math.max(1, g.count * pitch - GROUP_SEAM),
        name: '',
        count: '',
      });
    }
    return { placed, dropped: groups.length, crowded };
  }
  for (const g of groups) {
    const span = Math.max(1, g.count * pitch - GROUP_SEAM);
    const room = span - GROUP_PAD;
    const count = String(g.count);
    /* The ladder, and it is two rungs deep on purpose: the headcount is shed
       first, then the name goes whole. Nothing here abbreviates. At the
       benchmark 19px pitch a one-column run has 14px of room, which is two
       characters of an 10px mono face — and two characters of `no data` or of
       `held` is a stub that names nothing, sitting directly over a column it
       is then trusted to describe. */
    const rungs: readonly (readonly [string, string])[] = [
      [g.label, count],
      [g.label, ''],
    ];
    const rung = rungs.find(
      ([name, n]) => (name.length + (n === '' ? 0 : 1 + n.length)) * ch <= room,
    );
    placed.push({
      g,
      anchor: g.start * pitch,
      span,
      name: rung?.[0] ?? '',
      count: rung?.[1] ?? '',
    });
    if (rung === undefined) dropped += 1;
  }
  return { placed, dropped, crowded };
}

/** The five metric rows. MODE is not among them: it is the ribbon. */
const CELL_METRICS = FLEET_METRICS.filter((m) => m.key !== 'mode');
const MODE_METRIC = FLEET_METRICS.find((m) => m.key === 'mode')!;
/* The matrix's row labels, uppercased the way the band prints them. Chrome, so
   the instrument draws them whether or not there is a roster to fill it. */
const MATRIX_ROWS: readonly string[] = [
  ...CELL_METRICS.map((m) => m.label.toUpperCase()),
  MODE_METRIC.label.toUpperCase(),
];

/* The group register's own column spec, so the instrument and the table it
   stands in for put their headings on the same x. */
/* The instrument sheds the same columns the table does, at the same widths —
   `groupCols` is the one policy and both read it. A skeleton pinned to eight
   columns printed `GroupRobots` on top of itself in a 330px rail, which is a
   loading state inventing a defect the real table does not have. */
function groupColumns(cols: 'full' | 'mid' | 'lean' | 'min'): readonly SkeletonColumn[] {
  const out: SkeletonColumn[] = [
    { label: 'Group' },
    { label: 'Robots', width: '44px', align: 'right' },
    { label: 'Link', width: '52px', align: 'right' },
    { label: 'Attn', width: '38px', align: 'right' },
  ];
  if (cols === 'full' || cols === 'mid') out.push({ label: 'Census', width: '56px' });
  out.push({ label: 'Batt', width: '46px', align: 'right' });
  if (cols !== 'min') out.push({ label: 'Cert', width: '52px', align: 'right' });
  if (cols === 'full') out.push({ label: 'Builds', width: '96px' });
  return out;
}

/* The estate rail's three columns — a name, its headcount, its census bar. */
const ESTATE_COLUMNS: readonly SkeletonColumn[] = [
  { label: undefined },
  { width: '34px', align: 'right' },
  { width: '96px' },
];

/* The two strips the fleet-history panel stacks, named, so an unread window
   still says WHICH two quantities it would have drawn. */
const HISTORY_LANES = [
  { label: 'Telemetry', unit: '/h' },
  { label: 'Commands', unit: '/h' },
];

/* The three rail panels, likewise. They were the last three on this surface
   whose down state was a fault line over an empty body — the instrument left
   the screen with the values. `Sites` is three named plots on a shared
   ruler, so it takes a strip of three cells; `Capture` is a four-stage bar
   rail, so it takes four labelled rows; `Commands` is a table and takes its
   own columns. */
const SITE_CELLS: readonly SkeletonCell[] = [{}, {}, {}];
const CAPTURE_COLUMNS: readonly SkeletonColumn[] = [
  { label: undefined },
  { width: '30px', align: 'right' },
  { width: '120px' },
];
const CMD_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'Robot · command' },
  { label: 'State', width: '96px' },
  { label: 'In state', width: '64px', align: 'right' },
];

/* The exception register's, likewise. */
const ATTENTION_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'Robot', width: '200px', second: true },
  { label: 'Site', width: '150px' },
  { label: 'Readings out of limits' },
  { label: 'Charge', width: '100px' },
  { label: 'Battery', width: '110px', align: 'right' },
  { label: 'Last seen', width: '110px', align: 'right' },
];

/* ---- column groups ------------------------------------------------------ */

/** A run of adjacent columns sharing whatever the current ordering sorted on. */
interface ColGroup {
  key: string;
  label: string;
  /** Column index the run starts at — where the 1px rule is painted. */
  start: number;
  count: number;
  /** The run's worst reading, as a 2px tick at its leading edge (§5, level 3). */
  tone: Tone;
}

function columnGroups(
  items: readonly Robot[],
  of: (r: Robot) => string,
  worstBy: ReadonlyMap<string, Tone>,
): ColGroup[] {
  const out: ColGroup[] = [];
  items.forEach((r, i) => {
    const key = of(r);
    const tone = worstBy.get(r.robot_id) ?? 'nominal';
    const last = out[out.length - 1];
    if (last !== undefined && last.key === key) {
      last.count += 1;
      if ((TONE_RANK[tone] ?? 0) > (TONE_RANK[last.tone] ?? 0)) last.tone = tone;
    } else {
      out.push({ key, label: key, start: i, count: 1, tone });
    }
  });
  return out;
}

/* ---- the per-row census -------------------------------------------------- */

interface Seg {
  key: string;
  /** The bucket, in words — colour is never the sole carrier. */
  label: string;
  n: number;
  tone: Tone;
  hollow?: boolean | undefined;
}

interface CensusRow {
  key: string;
  segs: readonly Seg[];
  /** Which bucket a robot falls in, so a hovered column lights its bucket. */
  of: (r: Robot) => string;
}

/* The three continuous readings, bucketed at the SAME thresholds the cells
   beside them are painted with — so a robot in the amber bucket is the robot
   whose cell is amber, and the census can never disagree with the texture. */
function battBucket(r: Robot): string {
  const v = r.battery_pct;
  if (v == null) return 'none';
  if (v <= BATTERY_BAD) return 'bad';
  if (v <= BATTERY_WARN) return 'warn';
  return 'ok';
}

function seenBucket(r: Robot, now: number): string {
  if (r.last_seen == null) return 'none';
  const age = now - Date.parse(r.last_seen);
  if (Number.isNaN(age)) return 'none';
  if (age >= SEEN_BAD_MS) return 'bad';
  if (age >= SEEN_WARN_MS) return 'warn';
  return 'ok';
}

function certBucket(r: Robot, now: number): string {
  if (r.cert_not_after == null) return 'none';
  const days = (Date.parse(r.cert_not_after) - now) / 86_400_000;
  if (Number.isNaN(days)) return 'none';
  if (days <= CERT_BAD_DAYS) return 'bad';
  if (days <= CERT_WARN_DAYS) return 'warn';
  return 'ok';
}

/** Six censuses, in the row order of the band: the five metric rows, then the
    ribbon. Every count is a tally over the roster in hand — nothing here is
    read from an aggregate endpoint, so no figure can disagree with the cells
    it is drawn beside. */
function buildCensus(fleet: readonly Robot[], now: number): CensusRow[] {
  const row = (
    key: string,
    of: (r: Robot) => string,
    defs: readonly Omit<Seg, 'n'>[],
  ): CensusRow => {
    const tally = new Map<string, number>();
    for (const r of fleet) {
      const k = of(r);
      tally.set(k, (tally.get(k) ?? 0) + 1);
    }
    return { key, of, segs: defs.map((d) => ({ ...d, n: tally.get(d.key) ?? 0 })) };
  };

  /* Every row is five slots wide and nothing may sit in front of them: a mean
     printed ahead of the BATT buckets shunts that row's dots one slot right,
     and a census whose columns do not line up is six unrelated lines. The
     fleet mean is on the strip above, in a card of its own. */
  return [
    row('link', (r) => r.liveness, LIVENESS_ORDER.map((l) => ({
      key: l,
      label: LIVENESS_STATE[l].label,
      /* Projected, exactly like the cells: this rail is the LINK row's own
         legend, so it has to be painted in the LINK row's own colours. */
      tone: matrixTone(LIVENESS_STATE[l].tone),
      hollow: LIVENESS_STATE[l].hollow,
    }))),
    row('batt', battBucket, [
      { key: 'ok', label: `>${BATTERY_WARN}`, tone: 'nominal' },
      { key: 'warn', label: `${BATTERY_BAD}–${BATTERY_WARN}`, tone: 'warn' },
      { key: 'bad', label: `≤${BATTERY_BAD}`, tone: 'error' },
      { key: 'none', label: NO_VALUE, tone: 'offline', hollow: true },
    ]),
    row('seen', (r) => seenBucket(r, now), [
      { key: 'ok', label: '<2m', tone: 'nominal' },
      { key: 'warn', label: '2m–1h', tone: 'warn' },
      { key: 'bad', label: '≥1h', tone: 'error' },
      { key: 'none', label: 'never', tone: 'offline', hollow: true },
    ]),
    row('cert', (r) => certBucket(r, now), [
      { key: 'ok', label: `>${CERT_WARN_DAYS}d`, tone: 'nominal' },
      { key: 'warn', label: `≤${CERT_WARN_DAYS}d`, tone: 'warn' },
      { key: 'bad', label: `≤${CERT_BAD_DAYS}d`, tone: 'error' },
      { key: 'none', label: 'none', tone: 'offline', hollow: true },
    ]),
    row('reg', (r) => (r.active ? 'on' : 'off'), [
      { key: 'on', label: 'active', tone: 'nominal' },
      { key: 'off', label: 'inactive', tone: 'offline', hollow: true },
    ]),
    /* The ribbon's legend — the one row in the FULL mode palette, because the
       ink it names is painted that way too. */
    row('mode', (r) => r.status, STATUS_ORDER.map((s) => ({
      key: s,
      label: STATUS_STATE[s].label,
      tone: STATUS_STATE[s].tone,
    }))),
  ];
}

/* ---- the charge profile -------------------------------------------------- */

/* The BATT row of the census answers "how many robots are under the redline".
   It cannot answer the question an operator actually plans a shift around —
   WHERE the fleet's charge sits. Nineteen robots over 40 % is a different
   fleet at 44 % than it is at 92 %, and the four buckets read identically in
   both. So the census keeps the counts and this keeps the shape: one 20-bin
   histogram of the same readings, at the same thresholds, over the same
   roster, drawn in the band's surplus width rather than in a panel of its own.

   Bins are half-OPEN on the low side — `(lo, hi]` — because the thresholds
   they must agree with are `<=`. A robot at exactly 15.0 % is redline in the
   cells beside it, so it has to land in a redline bin here too, and
   `ceil(v / 5) - 1` is the indexing that puts it there. */

/** 5 % per bin: the only width that puts BOTH thresholds — 15 and 40 — on a
    bin boundary, so no bar is half redline and half reclaim. */
const CHARGE_STEP = 5;
const CHARGE_BINS = 100 / CHARGE_STEP;

/** Which bin a reading falls in, or `null` for a robot with no reading. */
function chargeBin(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.max(0, Math.min(CHARGE_BINS - 1, Math.ceil(v / CHARGE_STEP) - 1));
}

/** A bin's tone, taken from the bin's own upper edge through the same two
    thresholds the BATT cells use — so the histogram cannot be coloured
    differently from the texture above it. */
function chargeTone(bin: number): Tone {
  const hi = (bin + 1) * CHARGE_STEP;
  if (hi <= BATTERY_BAD) return 'error';
  if (hi <= BATTERY_WARN) return 'warn';
  return 'nominal';
}

interface ChargeProfile {
  bins: readonly number[];
  peak: number;
  known: number;
  /** Robots the fleet holds no charge reading for. Counted, never binned. */
  none: number;
  /** Mean of the readings in hand, as a percentage, or `null` for none. */
  mean: number | null;
}

function buildCharge(fleet: readonly Robot[]): ChargeProfile {
  const bins = new Array<number>(CHARGE_BINS).fill(0);
  let known = 0;
  let none = 0;
  let sum = 0;
  for (const r of fleet) {
    const b = chargeBin(r.battery_pct);
    if (b === null) { none += 1; continue; }
    bins[b] += 1;
    known += 1;
    sum += r.battery_pct as number;
  }
  return {
    bins,
    peak: Math.max(1, ...bins),
    known,
    none,
    mean: known === 0 ? null : sum / known,
  };
}

/* ---- the capture pipeline ------------------------------------------------

   A session's life is `raw` → `processing` → `copc`, or `failed`, and a
   second, independent axis says whether its bytes have reached the bucket at
   all (`sync_state`). Both are on the contract; neither is aggregated by any
   endpoint, so this is a fan-out over the roster and a tally here.

   The pipeline order is the ORDER OF THE LIFECYCLE, not descending count. A
   facet rail sorted by size answers "which is biggest"; this one has to answer
   "where is the backlog", and that is only legible when the stages are in the
   order the bytes move through them.

   Colour: `failed` and nothing else. `raw` and `processing` are the normal
   path — a session that has just landed is not a degraded session, and
   painting the whole pipeline amber would spend the surface's warn hue on
   sessions doing exactly what they should (§3: colour where a threshold is
   crossed, nowhere else). */
const CAPTURE_ORDER: readonly ProcessingState[] = ['raw', 'processing', 'copc', 'failed'];

interface CaptureStage {
  key: string;
  n: number;
  tone: Tone;
  hollow?: boolean | undefined;
  /** Start of the oldest session still sitting in this stage. */
  oldest: number | null;
}

interface CaptureProfile {
  total: number;
  stages: readonly CaptureStage[];
  failed: number;
  /** Sessions whose bytes have not finished reaching the bucket, and how many. */
  syncing: number;
  syncingBytes: number;
  /** Start of the most recent session — what makes "Capture" a live panel. */
  newest: number | null;
}

function stamp(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

function buildCapture(scans: readonly Scan[]): CaptureProfile {
  const counts = new Map<string, { n: number; oldest: number | null }>();
  let syncing = 0;
  let syncingBytes = 0;
  let newest: number | null = null;
  for (const s of scans) {
    const key = s.processing_state ?? 'unknown';
    const at = stamp(s.started_utc) ?? stamp(s.created_at);
    const slot = counts.get(key) ?? { n: 0, oldest: null };
    slot.n += 1;
    if (at !== null && (slot.oldest === null || at < slot.oldest)) slot.oldest = at;
    counts.set(key, slot);
    /* Anything that is not the terminal `synced` is still in flight — and a
       session with no sync_state at all is not evidence that it arrived. */
    if (s.sync_state !== 'synced') {
      syncing += 1;
      syncingBytes += s.size_bytes;
    }
    if (at !== null && (newest === null || at > newest)) newest = at;
  }
  const stages: CaptureStage[] = [];
  for (const k of CAPTURE_ORDER) {
    const key = k ?? 'unknown';
    const slot = counts.get(key);
    stages.push({
      key,
      n: slot?.n ?? 0,
      tone: key === 'failed' ? 'error' : 'nominal',
      oldest: slot?.oldest ?? null,
    });
  }
  /* Sessions the contract allows to carry no state at all (`null` predates the
     column). Only ever drawn when there are some, and drawn hollow — an
     unknown stage is not a stage. */
  const unknown = counts.get('unknown');
  if (unknown !== undefined) {
    stages.push({ key: 'unknown', n: unknown.n, tone: 'neutral', hollow: true, oldest: unknown.oldest });
  }
  return {
    total: scans.length,
    stages,
    failed: counts.get('failed')?.n ?? 0,
    syncing,
    syncingBytes,
    newest,
  };
}

/** Bytes, at the precision an operator reads a transfer backlog at. */
function bytes(n: number): string {
  if (n < 1000) return `${n} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let v = n / 1000;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/**
 * The fleet's charge, as a distribution.
 *
 * Twenty bars, a rule at each of the two watermarks, and a marker where the
 * mean falls. The marker carries no NUMBER: the strip above already prints the
 * fleet mean from `/api/stats`, and a second figure for the same fact, derived
 * from a different endpoint at a different instant, is a number the operator
 * has to reconcile. The mark says where; the card says how much.
 */
function ChargeStrip({
  profile,
  hotBin,
  pick,
  onPick,
}: {
  profile: ChargeProfile;
  /** The bin the pointed-at robot is in — the cursor's mark, spent
      here the same way it is spent on the census counts. */
  hotBin: number | null;
  /** The bin being POINTED AT, which runs the highlight the other way: the
      band marks every column whose robot is in it. A histogram you can only
      look at is a picture; one that answers "which robots are those" without
      leaving the panel is an instrument (THE BAR §5). */
  pick: number | null;
  onPick: (bin: number | null) => void;
}) {
  const { bins, peak, known, none, mean } = profile;
  /* Where the keyboard lands first: the lowest bin that actually holds a
     robot, and 0 for a fleet with no readings at all — `findIndex` returns
     -1 there, and a -1 bin prints a range of −5–0 %. */
  const roving = pick ?? Math.max(0, bins.findIndex((c) => c > 0));
  const step = (d: number) => {
    const next = Math.max(0, Math.min(CHARGE_BINS - 1, (pick ?? roving) + d));
    onPick(next);
  };
  const badPct = (BATTERY_BAD / 100) * 100;
  const warnPct = (BATTERY_WARN / 100) * 100;
  return (
    <div className={styles.chg}>
      <span className={styles.chgHead}>
        charge
        <i>
          {known} read
          <em data-zero={none === 0}>· {none} no reading</em>
        </i>
      </span>
      <div className={styles.chgBody}>
        {/* The no-reading slot keeps its place at zero, exactly as a census
            category with nobody in it does: a strip that grows a column when
            a robot stops answering is a strip that changed shape, and the
            change is the thing the operator was meant to notice. */}
        <span
          className={styles.chgNone}
          data-zero={none === 0}
          title={`${none} robots with no charge reading`}
        >
          <i style={{ height: `${none === 0 ? 0 : Math.max(8, (none / peak) * 100)}%` }} />
        </span>
        {/* One tab stop, then the arrows walk the bins — the same roving
            contract the matrix keeps, so the cross-highlight is not a
            hover-only feature. */}
        <div
          className={styles.chgPlot}
          role="img"
          tabIndex={0}
          aria-label={
            known === 0
              ? 'No charge readings'
              : `Charge distribution over ${known} robots, ${bins
                  .map((c, i) => (c === 0 ? null : `${c} at ${i * CHARGE_STEP}–${(i + 1) * CHARGE_STEP}%`))
                  .filter((x) => x !== null)
                  .join(', ')}`
          }
          onMouseLeave={() => onPick(null)}
          onFocus={() => onPick(roving)}
          onBlur={() => onPick(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
            else if (e.key === 'Home') { e.preventDefault(); onPick(0); }
            else if (e.key === 'End') { e.preventDefault(); onPick(CHARGE_BINS - 1); }
            else if (e.key === 'Escape') { onPick(null); }
          }}
        >
          <span className={styles.chgRule} style={{ left: `${badPct}%` }} aria-hidden="true" />
          <span className={styles.chgRule} style={{ left: `${warnPct}%` }} aria-hidden="true" />
          {mean !== null && (
            <span
              className={styles.chgMean}
              style={{ left: `${Math.max(0, Math.min(100, mean))}%` }}
              data-flip={mean > 66}
              aria-hidden="true"
            >
              <b>mean</b>
            </span>
          )}
          {bins.map((c, i) => (
            <span
              key={i}
              className={styles.chgBin}
              data-tone={chargeTone(i)}
              data-hot={hotBin === i}
              data-pick={pick === i}
              data-zero={c === 0}
              onMouseEnter={() => onPick(i)}
              title={`${i * CHARGE_STEP}–${(i + 1) * CHARGE_STEP} % — ${c} ${c === 1 ? 'robot' : 'robots'}`}
            >
              <i style={{ height: `${c === 0 ? 0 : Math.max(6, (c / peak) * 100)}%` }} />
            </span>
          ))}
        </div>
      </div>
      <div className={styles.chgAxis} aria-hidden="true">
        <span className={styles.chgNoneTick}>—</span>
        <span className={styles.chgTicks}>
          <i style={{ left: '0%' }}>0</i>
          <i style={{ left: `${badPct}%` }} data-tone="error">{BATTERY_BAD}</i>
          <i style={{ left: `${warnPct}%` }} data-tone="warn">{BATTERY_WARN}</i>
          <i style={{ left: '100%' }} data-end="true">100%</i>
        </span>
      </div>
    </div>
  );
}

/* ---- the band ------------------------------------------------------------ */

function FleetTexture({
  items,
  groupKey,
  groupBy,
  worstBy,
  census,
  charge,
  caption,
  cursorId,
  onCursor,
  selected,
  onToggle,
  onToggleRun,
  onPick,
  onHeight,
  onShown,
}: {
  items: readonly Robot[];
  /** What the current ordering sorted on. Runs of it become column groups —
      recomputed per bank, because a bank boundary ends a run. */
  groupKey: (r: Robot) => string;
  /** What this ordering grouped the columns by — printed above the first one. */
  groupBy: string;
  worstBy: ReadonlyMap<string, Tone>;
  census: readonly CensusRow[];
  /** The fleet's charge, binned — the shape the census's four buckets cannot
      carry. Drawn under the census and shed with it. */
  charge: ChargeProfile;
  /** The idle caption, after the reading count. Replaced by a one-robot
      readout on hover, at the same height. */
  caption: ReactNode;
  /** The robot the WHOLE SURFACE is pointing at, held by the page. The band
      does not own its own cursor: a column marked here is the same fact as a
      dot ringed in the site plots and a fix printed in their header, so one
      id drives all of them and pointing at any of them points at all. */
  cursorId: string | null;
  onCursor: (robotId: string | null) => void;
  /** The robots picked out of the texture, shared with the exception table —
      selecting a broken column and ticking its row are the same act. */
  selected: ReadonlySet<string>;
  /** Click, or Space. `shift` extends the run from the last single pick. */
  onToggle: (robotId: string, shift: boolean) => void;
  /** A whole column run, picked from its own label in the group strip. */
  onToggleRun: (robotIds: readonly string[]) => void;
  /** Enter — the column's other verb, and the one that leaves the surface. */
  onPick: (r: Robot) => void;
  /** What the band's own geometry costs the page, in px, so the grid row it
      sits in can be given a floor. Without one the row is `auto`, and an
      `auto` row in a full-height grid whose other rows have already claimed
      the container is shrunk below its content — silently, with `overflow:
      hidden` above it. Reported rather than measured so the floor is known on
      the same frame the geometry is. */
  onHeight: (px: number) => void;
  /** How many of the roster the band actually PAINTED. The panel header's
      `6×N` is a claim about the matrix under it, and at a roster the band has
      had to truncate, `6×1200` over 484 columns is the same defect the
      scrolling version was — a header asserting a census the picture does not
      contain. Reported from here because `shown` is derived here. */
  onShown: (n: number) => void;
}) {
  /* The charge bin being pointed at, which runs the highlight the other way
     round: columns marked FROM the distribution. Held separately from `hover`
     so pointing at a bar cannot also claim to be pointing at a robot. */
  const [binPick, setBinPick] = useState<number | null>(null);
  /* The run being pointed at in the group strip. A run one column wide cannot
     print its own name (see `placeGroups`), so pointing at it is the route
     back to the full string — and it washes exactly the columns it counts, so
     the reader can see that the claim and the columns agree. */
  const [groupPick, setGroupPick] = useState<GroupPick | null>(null);
  const [active, setActive] = useState<[number, number]>([0, 0]);
  const gridRef = useRef<HTMLDivElement | null>(null);
  /* Measured off the BAND, whose width comes from the page grid — so the
     census's shed decision cannot feed back into its own measurement. */
  const [bodyRef, bodyWidth] = useBoxWidth();
  /* And off the row-label track, which is as wide as the word `MODE` and not
     one pixel wider whatever the pitch does. Measuring it rather than assuming
     it is what keeps the derived pitch honest: the track is the band minus
     these labels, and a guess that is 30px light is three columns clipped off
     the right-hand end with nothing on screen saying so. */
  const [labelRef, labelWidth] = useBoxWidth();
  /* One character of the group strip's own mono face. The label placement
     below needs a width for a string it has not rendered yet, and a hard-coded
     0.6em would be a guess about which fallback face the platform picked. */
  const [chRef, chWidth] = useBoxWidth();
  const ch = chWidth > 0 ? chWidth / GROUP_PROBE.length : 6;
  const viewportH = useViewportHeight();

  const n = items.length;
  /* The five metric rows plus the ribbon: one roving grid, so the whole fleet
     is walkable from the keyboard without 168 tab stops. */
  const rowCount = CELL_METRICS.length + 1;
  const ribbonRow = CELL_METRICS.length;

  /* The census is the surplus, so it is decided FIRST, against what the fleet
     would take at the benchmark pitch. Only then does the texture find out how
     much room it has, and compress into it. */
  const labelCol = (labelWidth > 0 ? labelWidth : LABEL_COL) + LABEL_GAP;
  /* What pitch the roster could hold and still leave the census its floor. The
     census is kept whenever that is a pitch worth painting; the texture takes
     the whole band the moment it is not. Both branches hand `geometry` a track
     derived from the BAND's width, never from the cells' own box, so nothing
     here can feed back into its own measurement. */
  const censusPitch = n === 0
    ? MAX_PITCH
    : Math.floor((bodyWidth - labelCol - CENSUS_GAP - CENSUS_MIN) / n);
  const showCensus = bodyWidth === 0 || censusPitch >= CENSUS_KEEP_PITCH;
  const track = showCensus
    ? n * Math.min(MAX_PITCH, Math.max(CENSUS_KEEP_PITCH, censusPitch))
    : Math.max(0, bodyWidth - labelCol);
  /* The bar is measured against what the census actually got, not against what
     it would have got at the benchmark pitch. */
  const showCenBar = bodyWidth === 0
    || (showCensus && bodyWidth - labelCol - CENSUS_GAP - track >= CENSUS_BAR_MIN);
  const budget = Math.min(
    BAND_MAX_H,
    Math.max(BAND_MIN_H, Math.round(viewportH * BAND_SHARE)),
  );
  const geo = geometry(n, track, budget);
  const { cols, banks, pitch, shown, hidden } = geo;
  /* 76px of rotated ids is the band's most expensive furniture and the first
     thing it sells: past 40 columns they are a wall of vertical strings nobody
     reads, and under a 15px pitch they do not resolve at all. The caption-line
     readout names the pointed-at robot at every width. */
  const showNames = banks === 1 && shown <= NAME_MAX_COLS && pitch >= NAME_MIN_PITCH;

  /* The floor the band's grid row is given, in the page's own coordinates.
     Everything in it is a measured constant of the stylesheet beside it. */
  const panelHeight = geo.bandHeight + (showNames ? NAMES_H : 0) + CAP_H + PANEL_CHROME;
  useEffect(() => { onHeight(panelHeight); }, [onHeight, panelHeight]);
  useEffect(() => { onShown(shown); }, [onShown, shown]);

  /* One slice per bank, each with its own runs: a run that crosses a bank
     boundary is two runs, and labelling it once over the first half would put
     a headcount over columns that are not there. */
  const painted = useMemo(() => items.slice(0, shown), [items, shown]);
  /* The cursor, resolved from an id to a column. Derived rather than stored:
     an index cached across a poll would point at whichever robot had since
     taken that column, which at 5-second polling is a readout that quietly
     changes subject. A robot the band did not paint (or that the roster has
     dropped) resolves to no column, and the band simply marks nothing. */
  const hover = useMemo(() => {
    if (cursorId === null) return null;
    const i = painted.findIndex((r) => r.robot_id === cursorId);
    return i < 0 ? null : i;
  }, [cursorId, painted]);
  const setHover = useCallback((c: number | null) => {
    onCursor(c === null ? null : painted[c]?.robot_id ?? null);
  }, [onCursor, painted]);
  const bankRows = useMemo(() => {
    const out: {
      start: number;
      slice: readonly Robot[];
      groups: readonly ColGroup[];
      placed: readonly PlacedGroup[];
      dropped: number;
      crowded: boolean;
    }[] = [];
    for (let b = 0; b < banks; b += 1) {
      const start = b * cols;
      const slice = painted.slice(start, start + cols);
      if (slice.length === 0) continue;
      const groups = columnGroups(slice, groupKey, worstBy);
      out.push({
        start, slice, groups,
        ...placeGroups(groups, pitch, ch),
      });
    }
    return out;
  }, [painted, banks, cols, groupKey, worstBy, pitch, ch]);

  const focusCell = useCallback((r: number, c: number, count: number, rows: number) => {
    const rr = Math.max(0, Math.min(rows - 1, r));
    const cc = Math.max(0, Math.min(count - 1, c));
    setActive([rr, cc]);
    setHover(cc);
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${rr}:${cc}"]`);
    el?.focus();
  }, [setHover]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, r: number, c: number) => {
    const map: Record<string, [number, number]> = {
      /* Left/Right run the whole roster — across a bank seam, because the
         seam is a wrap in the presentation, not a break in the fleet. */
      ArrowRight: [r, c + 1], ArrowLeft: [r, c - 1],
      ArrowDown: [r + 1, c], ArrowUp: [r - 1, c],
      Home: [r, 0], End: [r, shown - 1],
    };
    const next = map[e.key];
    if (next !== undefined) {
      e.preventDefault();
      focusCell(next[0], next[1], shown, rowCount);
      return;
    }
    if (e.key === 'Escape') { setHover(null); return; }
    /* Space selects, Enter activates — the grid convention, and the same
       split the pointer makes: a click picks the column into the action bar,
       Enter leaves the surface for the robot's own page. */
    if (e.key === ' ') {
      e.preventDefault();
      const it = painted[c];
      if (it !== undefined) onToggle(it.robot_id, e.shiftKey);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const it = painted[c];
      if (it !== undefined) onPick(it);
    }
  };

  const focused = hover === null ? undefined : painted[hover];
  /* The hovered robot's bucket on every census row at once. */
  const hot = focused === undefined
    ? null
    : new Map(census.map((row) => [row.key, row.of(focused)] as const));
  const hotBin = focused === undefined ? null : chargeBin(focused.battery_pct);
  /* Column indices the pointed-at bin holds, and the count the caption prints.
     A Set because the marks are painted per bank, and a bank does not know
     which slice of the roster it is. */
  const binCols = useMemo(() => {
    if (binPick === null) return null;
    const set = new Set<number>();
    painted.forEach((r, i) => { if (chargeBin(r.battery_pct) === binPick) set.add(i); });
    return set;
  }, [binPick, painted]);

  const cellProps = (r: number, c: number, label: string, it: Robot) => {
    const picked = selected.has(it.robot_id);
    return {
      role: 'gridcell' as const,
      'data-cell': `${r}:${c}`,
      'data-hot': hover === c,
      'aria-selected': picked,
      tabIndex: active[0] === r && active[1] === c ? 0 : -1,
      'aria-label': `${label}${picked ? ' — selected' : ''}`,
      title: label,
      onMouseEnter: () => setHover(c),
      onFocus: () => { setActive([r, c]); setHover(c); },
      onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => onKeyDown(e, r, c),
      onClick: (e: ReactMouseEvent<HTMLDivElement>) => onToggle(it.robot_id, e.shiftKey),
      /* The column's other verb, for the hand that is already on the mouse.
         Enter does the same from the keyboard, and the band's caption line
         prints both — a grid of 6px cells cannot advertise its own verbs, so
         the one line above it does. */
      onDoubleClick: () => onPick(it),
    };
  };

  /* Column indices the selection holds, per bank. Derived from ids for the
     same reason the cursor is: the roster repolls every five seconds, and a
     cached index would mark whichever robot had since taken that column. */
  const selCols = useMemo(() => {
    if (selected.size === 0) return null;
    const set = new Set<number>();
    painted.forEach((r, i) => { if (selected.has(r.robot_id)) set.add(i); });
    return set;
  }, [selected, painted]);

  /* Every dimension the band paints, handed to the stylesheet as its own
     variables. The token file's 19/13/28 are the defaults these override — the
     band is the one surface whose pitch is a function of the roster. */
  const vars = {
    '--col-pitch': `${pitch}px`,
    '--cell': `${geo.cell}px`,
    '--cell-h': `${geo.cellH}px`,
    '--cell-pitch': `${geo.rowPitch}px`,
    '--h-ribbon': `${geo.ribbon}px`,
    '--rib-ink': `${geo.ribbonInk}px`,
  } as CSSProperties;

  return (
    <div className={styles.tex}>
      {/* One line, one height, two jobs: the shape of the data while nothing
          is pointed at, and a full readout of one robot the moment something
          is. §5 — click a feature, get the raw values. */}
      <div className={styles.texCap}>
        {focused === undefined ? (
          <>
            {/* Readings PAINTED, not readings the fleet has. The two are the
                same number until the band runs out of banks, and on the day
                they differ the difference is the headline — so the count and
                the shortfall are one unshrinkable span, ahead of a caption
                clause that is allowed to clip. */}
            <span className={styles.texCapCount}>
              {rowCount * shown} readings
              {hidden > 0 && (
                <b className={styles.texMore}>▸ {hidden} robots not shown</b>
              )}
            </span>
            <span className={styles.texCapIdle}>
              {groupPick !== null ? (
                <>
                  <i className={styles.capSep}>·</i>
                  <span className={styles.capKey}>{groupBy}</span>
                  <span className={styles.capVal} data-tone={groupPick.tone}>
                    {groupPick.label}
                  </span>
                  <span className={styles.capVal}>{groupPick.count}</span>
                  <span className={styles.capKey}>
                    {groupPick.count === 1 ? 'robot' : 'robots'}
                  </span>
                </>
              ) : binPick === null ? caption : (
                <>
                  <i className={styles.capSep}>·</i>
                  <span className={styles.capKey}>charge</span>
                  <span className={styles.capVal} data-tone={chargeTone(binPick)}>
                    {binPick * CHARGE_STEP}–{(binPick + 1) * CHARGE_STEP}%
                  </span>
                  <span className={styles.capVal}>
                    {binCols?.size ?? 0}
                  </span>
                  <span className={styles.capKey}>
                    {(binCols?.size ?? 0) === 1 ? 'robot' : 'robots'}
                  </span>
                </>
              )}
            </span>
            <span className={styles.spacer} />
            {/* The key lives under the census, beside the ink it describes,
                and only comes back up here when the census has shed. A key
                that disappears the moment you point at a cell — which is what
                happens when it shares this line with the readout — is a key
                that is missing exactly when it is being used. */}
            {!showCensus && <MatrixKey items={MATRIX_KEY} />}
          </>
        ) : (
          <>
            <span className={styles.readName}>{focused.robot_id}</span>
            <span className={styles.readWhere}>
              {siteOf(focused)}
              {metaString(focused, 'bay') !== null && (
                <i>· {metaString(focused, 'bay')}</i>
              )}
            </span>
            <span className={styles.readList}>
              {FLEET_METRICS.map((m) => {
                const v = m.read(focused);
                return (
                  <span key={m.key} className={styles.readItem}>
                    <span className={styles.readKey}>{m.label}</span>
                    <span
                      className={styles.readVal}
                      data-tone={v?.tone ?? 'neutral'}
                      data-known={v !== null}
                    >
                      {v?.text ?? NO_VALUE}
                    </span>
                  </span>
                );
              })}
            </span>
          </>
        )}
      </div>

      <div
        className={styles.texBody}
        ref={bodyRef}
        data-census={showCensus}
        style={vars}
        onMouseLeave={() => setHover(null)}
      >
        <div className={styles.texLabels} ref={labelRef} aria-hidden="true">
          {bankRows.map((bank) => (
            <div key={bank.start} className={styles.texLabelBank}>
              <span className={styles.texBy}>{groupBy}</span>
              {CELL_METRICS.map((m) => (
                <span key={m.key} className={styles.texRowLabel}>{m.label}</span>
              ))}
              <span className={styles.texRibLabel}>{MODE_METRIC.label}</span>
            </div>
          ))}
        </div>

        {/* The measurement the group-label placement is a function of: one
            hidden run of the strip's own face, measured rather than assumed. */}
        <div className={styles.texGroupProbe} ref={chRef} aria-hidden="true">{GROUP_PROBE}</div>

        <div className={styles.texCols} ref={gridRef}>
          {bankRows.map((bank) => (
            <div
              key={bank.start}
              className={styles.texBank}
              style={{ width: `${bank.slice.length * pitch}px` }}
            >
            <div className={styles.texStack}>
              {/* The pointed-at column, marked at the band's full height. The
                  row hover tint under a 6px cell is 6px of one grey step and
                  cannot be found from across a desk; a full-height washed
                  column can, at any pitch, and it is the cursor's own
                  "this is the current selection" job. */}
              {hover !== null && hover >= bank.start && hover < bank.start + bank.slice.length && (
                <span
                  className={styles.texHot}
                  style={{ left: `${(hover - bank.start) * pitch}px`, width: `${pitch}px` }}
                  aria-hidden="true"
                />
              )}

              {/* The run being pointed at in the group strip, washed at its
                  true extent — the proof that the label and the columns it
                  claims are the same columns. */}
              {groupPick !== null && groupPick.bank === bank.start && (
                <span
                  className={styles.texBinMark}
                  style={{
                    left: `${groupPick.start * pitch}px`,
                    width: `${groupPick.count * pitch - 1}px`,
                  }}
                  aria-hidden="true"
                />
              )}

              {/* The picked columns. Accent, and the only accent on the
                  band: a selection is the one thing here the operator made
                  rather than the fleet, so it is the one thing that wears the
                  console's own hue. A 2px cap at the column's head carries it
                  where the wash under a 6px cell would not (§5, level 3), so
                  a selection survives being scrolled past at 150 robots. */}
              {selCols !== null && bank.slice.map((_, i) => (
                selCols.has(bank.start + i) ? (
                  <span
                    key={`sel${i}`}
                    className={styles.texSel}
                    style={{ left: `${i * pitch}px`, width: `${pitch}px` }}
                    aria-hidden="true"
                  />
                ) : null
              ))}

              {/* Columns the pointed-at charge bin holds. Same full-height
                  mark as the hovered column, at a lower alpha because there
                  can be a dozen of them and they are a SET, not a cursor. */}
              {binCols !== null && bank.slice.map((_, i) => (
                binCols.has(bank.start + i) ? (
                  <span
                    key={`bin${i}`}
                    className={styles.texBinMark}
                    style={{ left: `${i * pitch}px`, width: `${pitch}px` }}
                    aria-hidden="true"
                  />
                ) : null
              ))}

              {/* One hairline per group boundary, through the labels, the five
                  metric rows and the ribbon — the mark the `Site` ordering used
                  to reorder without making. */}
              {bank.groups.slice(1).map((g) => (
                <span
                  key={`${g.key}:${g.start}`}
                  className={styles.texRule}
                  style={{ left: `${g.start * pitch}px` }}
                  aria-hidden="true"
                />
              ))}

              <div
                className={styles.texGroups}
                onMouseLeave={() => setGroupPick(null)}
              >
                {bank.placed.map(({ g, anchor, span, name, count }) => (
                  /* One box per run, at exactly the run's own extent, clipping
                     its own words. A label cannot reach another run's columns
                     because it cannot leave this box.

                     And it is a button, because a run is the answer to "which
                     group is on fire": in `Worst` order the leftmost run IS
                     the fault set, and one press picks all four of them into
                     the action bar above. Hovering already washes exactly the
                     columns it counts, so the press has already shown the
                     operator what it is about to select. */
                  <button
                    type="button"
                    key={`${g.key}:${g.start}`}
                    className={styles.texGroup}
                    style={{ left: `${anchor}px`, width: `${span}px` }}
                    aria-label={`${g.label}, ${g.count} ${g.count === 1 ? 'robot' : 'robots'}. Select this run.`}
                    title={`${g.label} — ${g.count} ${g.count === 1 ? 'robot' : 'robots'}  ·  click to select the run`}
                    onClick={() => onToggleRun(
                      painted
                        .slice(bank.start + g.start, bank.start + g.start + g.count)
                        .map((r) => r.robot_id),
                    )}
                    onMouseEnter={() => setGroupPick({
                      bank: bank.start, start: g.start, count: g.count,
                      label: g.label, tone: g.tone,
                    })}
                  >
                    {/* The run's extent, as a hairline across its own box —
                        and as a 2px rule IN THE RUN'S OWN TONE when the box
                        was too narrow for its name, so a run that cannot say
                        what it is still shows it. */}
                    <i
                      className={styles.texGroupSpan}
                      data-tone={g.tone}
                      data-named={name !== ''}
                    />
                    {/* §5, level 3: the run's worst reading as a 2px tick at
                        its leading edge, so "which group is on fire" is
                        answered before its name is read. */}
                    <i className={styles.texGroupTick} data-tone={g.tone} />
                    {name !== '' && (
                      <span className={styles.texGroupLabel}>
                        <b className={styles.texGroupName}>{name}</b>
                        {count !== '' && <em className={styles.texGroupN}>{count}</em>}
                      </span>
                    )}
                  </button>
                ))}
                {/* Only in the CROWDED state, where every run in the bank has
                    gone wordless at once and the strip has to say what it has
                    become. A `+2` beside three named runs is worse than
                    nothing: the two it counts are already drawn, as toned
                    rules over their own columns, and a bare `+2` pinned to the
                    strip's right edge reads as a footnote on whichever run
                    happens to end there. */}
                {bank.crowded && bank.dropped > 0 && (
                  <span className={styles.texGroupDrop} aria-hidden="true">
                    {bank.dropped} runs
                  </span>
                )}
              </div>

              <div
                className={styles.texGrid}
                role="grid"
                aria-label={banks === 1
                  ? 'Fleet status matrix'
                  : `Fleet status matrix, robots ${bank.start + 1}–${bank.start + bank.slice.length}`}
                aria-rowcount={rowCount}
                aria-colcount={bank.slice.length}
                /* Cells carry `aria-selected`, so the grid has to declare that
                   more than one of them can be. Without it a screen reader
                   reports the pick as a single-selection cursor and the
                   counted verbs above have nothing to agree with. */
                aria-multiselectable="true"
              >
                {CELL_METRICS.map((m, r) => (
                  <div key={m.key} className={styles.texRow} role="row">
                    {bank.slice.map((it, i) => {
                      const v = m.read(it);
                      return (
                        <div
                          key={it.robot_id}
                          className={styles.texCellBox}
                          {...cellProps(r, bank.start + i, `${it.robot_id} — ${m.label} ${v?.text ?? 'no reading'}`, it)}
                        >
                          <span
                            className={styles.texCell}
                            data-tone={v?.tone ?? 'neutral'}
                            data-hollow={v === null || v.hollow === true}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}

                <div className={styles.texRibbon} role="row">
                  {bank.slice.map((it, i) => (
                    <div
                      key={it.robot_id}
                      className={styles.texRibBox}
                      {...cellProps(
                        ribbonRow, bank.start + i,
                        `${it.robot_id} — ${MODE_METRIC.label} ${STATUS_STATE[it.status].label}`,
                        it,
                      )}
                    >
                      <span className={styles.texRibInk} data-tone={STATUS_STATE[it.status].tone} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

              {showNames && (
                <div className={styles.texNames} aria-hidden="true">
                  {bank.slice.map((it, i) => (
                    <div
                      key={it.robot_id}
                      className={styles.texName}
                      data-hot={hover === bank.start + i}
                      onMouseEnter={() => setHover(bank.start + i)}
                    >
                      <span title={it.robot_id}>{midEllipsis(it.robot_id, NAME_CHARS)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {showCensus && (
          <div className={styles.texCensus}>
            {/* The tone key rides the census's own header line. That line was
                a word and a headcount with 700px of nothing between them, and
                the key is exactly what the five colours under it mean — so it
                costs no row, it sits beside the ink it names, and (unlike the
                caption line, which becomes a one-robot readout the moment you
                point at a cell) it is still there while it is being used. The
                31px that buys is what the charge strip below is drawn in, so
                the band's height is unchanged by either. */}
            <span className={styles.texCensusHead}>
              census
              <span className={styles.texHeadKey}>
                <MatrixKey items={MATRIX_KEY} />
              </span>
              <i>{n} robots</i>
            </span>
            {census.map((row) => (
              <div key={row.key} className={styles.cenRow} data-ribbon={row.key === 'mode'}>
                <span className={styles.cenItems}>
                  {row.segs.map((s) => (
                    <span
                      key={s.key}
                      className={styles.cenItem}
                      data-zero={s.n === 0}
                      data-hot={hot?.get(row.key) === s.key}
                    >
                      <i className={styles.cenDot} data-tone={s.tone} data-hollow={s.hollow === true} />
                      <span className={styles.cenLabel}>{s.label}</span>
                      <b className={styles.cenN}>{s.n}</b>
                    </span>
                  ))}
                </span>
                {showCenBar && (
                <span
                  className={styles.cenBar}
                  role="img"
                  aria-label={row.segs.map((s) => `${s.n} ${s.label}`).join(', ')}
                >
                  {row.segs.filter((s) => s.n > 0).map((s) => (
                    <span
                      key={s.key}
                      className={styles.cenSeg}
                      data-tone={s.tone}
                      data-hollow={s.hollow === true}
                      style={{ flexGrow: s.n }}
                    />
                  ))}
                </span>
                )}
              </div>
            ))}
            {/* The strip lives in the height the rotated id track opens up
                beside the census — 214px of texture column against 168px of
                census — and it is drawn only while that track is. Past 40
                columns the ids go, the texture column shrinks to 126px, and a
                strip added there would not fill a hole, it would dig one under
                the cells and take a row off the exception table to do it. Same
                shed ladder as everything else on this band: ids, then the
                strip, then the census bar, then the census. */}
            {showNames && (
              <ChargeStrip
                profile={charge}
                hotBin={hotBin}
                pick={binPick}
                onPick={setBinPick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One site in its own local frame.
 *
 * Stroke-only chrome, a dot per robot at its true relative position, a 5px
 * heading tick where the robot reports one, and a scale bar naming the box.
 * Robots with no fix are not drawn — a robot that has never phoned home has no
 * position, and putting it at the origin would invent one — they are counted
 * in the footer instead.
 */
function SitePlot({
  site,
  hoverId,
  onHover,
  onPick,
}: {
  site: SiteGeo;
  /** The robot the whole panel is reading out, so every plot can dim to it. */
  hoverId: string | null;
  onHover: (robotId: string | null) => void;
  onPick: (r: Robot) => void;
}) {
  const [roving, setRoving] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const bar = scaleBar(site.spanM);

  const focusDot = useCallback((i: number) => {
    setRoving(i);
    ref.current?.querySelector<HTMLElement>(`[data-dot="${i}"]`)?.focus();
  }, []);

  return (
    <div className={styles.site}>
      {/* Fixed. The readout for a pointed-at robot goes to the PANEL's header
          (see `siteRead`), not here: a 137px column cannot hold a five-decimal
          fix, and the version of this that tried wrapped the line, cut the
          site's own name to `ko…` and changed the plot's height under the
          cursor. One readout line per panel is the same contract the matrix
          and the charge profile keep. */}
      <div className={styles.siteHead}>
        <span className={styles.siteTick} data-tone={site.worst} aria-hidden="true" />
        <span className={styles.siteName} title={site.label}>{site.key}</span>
        <span className={styles.siteCount}>
          {site.dots.length}
          <i>/{site.total}</i>
        </span>
      </div>

      <div
        className={styles.siteBox}
        ref={ref}
        role="group"
        aria-label={`${site.label}: ${site.dots.length} of ${site.total} robots with a position fix`}
        onPointerLeave={() => onHover(null)}
      >
        <span className={styles.siteCross} aria-hidden="true" />
        {site.dots.map((d, i) => (
          <button
            key={d.robot.robot_id}
            type="button"
            data-dot={i}
            className={styles.dot}
            data-tone={d.tone}
            data-hollow={d.tone === 'offline'}
            data-head={d.robot.heading_deg != null}
            data-hot={hoverId === d.robot.robot_id}
            tabIndex={i === roving ? 0 : -1}
            style={{
              left: `${(d.x * 100).toFixed(2)}%`,
              top: `${(d.y * 100).toFixed(2)}%`,
              ['--hdg' as string]: `${d.robot.heading_deg ?? 0}deg`,
            }}
            aria-label={
              `${d.robot.robot_id} at lat ${d.robot.lat!.toFixed(5)},`
              + ` lon ${d.robot.lon!.toFixed(5)}`
              + ` — ${STATUS_STATE[d.robot.status].label}, ${LIVENESS_STATE[d.robot.liveness].label}`
            }
            title={
              `${d.robot.robot_id}  lat ${d.robot.lat!.toFixed(5)}, lon ${d.robot.lon!.toFixed(5)}`
              + (d.robot.heading_deg == null
                ? ''
                : `  heading ${Math.round(d.robot.heading_deg)}°`)
              + (metaString(d.robot, 'bay') === null ? '' : `  bay ${metaString(d.robot, 'bay')!}`)
            }
            onFocus={() => { setRoving(i); onHover(d.robot.robot_id); }}
            onBlur={() => onHover(null)}
            onPointerEnter={() => onHover(d.robot.robot_id)}
            onKeyDown={(e) => rove(e, i, site.dots.length, focusDot)}
            onClick={() => onPick(d.robot)}
          />
        ))}
        {/* Which way is up. The dots are a local metric frame about the site
            centroid — lon → x, lat → y — so north IS the top of the box, and
            §5's map furniture says so rather than leaving an operator to
            assume it. The heading ticks on the dots are drawn against this
            same frame, so without it the one rotational quantity on the panel
            has no reference. Stroke only, dim, diagonally opposite the scale
            bar, in the corner the padded extent keeps clear of dots. */}
        <span
          className={styles.siteNorth}
          role="img"
          aria-label="North is up"
          title="North is up. Positions are a local metric frame about the site centroid; headings are the heading_deg the robot reported, against no stated datum."
        >
          N<i />
        </span>
        {bar !== null && (
          <span className={styles.siteScale} style={{ width: `${(bar.frac * 100).toFixed(2)}%` }}>
            <i>{bar.label}</i>
          </span>
        )}
        {site.dots.length === 0 && <span className={styles.siteVoid}>no fix</span>}
      </div>
    </div>
  );
}

/** A group's census as one length-encoded bar: size ∝ headcount, hue only
    where a human has to act. */
function MixBar({
  mix,
  total,
  peak,
  census,
}: {
  mix: Group['mix'];
  total: number;
  peak: number;
  /** The same split in words — the bar is the only place this fact appears. */
  census: string;
}) {
  return (
    <span
      className={styles.mix}
      style={{ width: `${(total / Math.max(1, peak)) * 100}%` }}
      role="img"
      aria-label={census}
      title={census}
    >
      {mix.map((s) => (
        <span
          key={s.tone}
          className={styles.mixSeg}
          data-tone={s.tone}
          data-hollow={s.tone === 'offline'}
          style={{ flexGrow: s.n }}
        />
      ))}
    </span>
  );
}

/* --------------------------------------------------------------------------
   Fleet history — three series, ONE playhead.

   THE BAR §5: "One shared time axis. Every live panel is bound to one
   scrubber." Three separate plots each with their own hover would answer three
   different questions at three different instants. Here the cursor is held
   once, driven into every plot, and every series prints its value for that
   instant on its own header line — a two-column readout that costs no extra
   layout and never covers the trace.
   -------------------------------------------------------------------------- */

interface Trace {
  key: string;
  eyebrow: string;
  unit: string;
  values: readonly (number | null)[];
  digits: number;
  min?: number | undefined;
  max?: number | undefined;
  /** Printed beside the eyebrow when the cursor is parked. */
  extent: string;
  /** THIS SERIES' OWN SOURCE IS NOT ANSWERING. Set it and the strip is drawn
      as a gap carrying this reason, never as a line: `values` for a rate are
      an array the derivation FILLS, so a dead source arrives here as a run of
      honest-looking zeros rather than as an absence. A trace may not be more
      confident than the feed behind it just because its shape survived. */
  gap?: string | undefined;
  /** And whether that gap is a FAILURE or merely a first read still in
      flight. Both are gaps — neither may be drawn as a line at zero — but
      only one of them is red: spending the error colour on a page that is
      one second old is how an operator learns to ignore it (§3). */
  gapTone?: 'error' | 'wait' | undefined;
}

function HistoryStack({
  times,
  traces,
  height,
  foot,
  hint,
  compact = false,
}: {
  times: readonly number[];
  traces: readonly Trace[];
  height: number;
  /** Provenance for the window, printed while the playhead is parked. */
  foot: string;
  /** The affordance hint, and the first thing this line sheds: below ~300px
      the provenance and the hint cannot both stand, and a fact about the
      data outranks a note about the mouse. The keyboard still works. */
  hint?: string | undefined;
  /** Under ~260px the head line cannot hold a title, a unit AND a readout.
      The unit and the readout then SHARE the right-hand slot: the unit while
      the playhead is parked, the reading the instant it is not. The unit is
      what the extent cannot be inferred from — three y-ticks say roughly
      what the range is, nothing on the panel says what the numbers ARE — so
      it is the one that holds the slot at rest. */
  compact?: boolean | undefined;
}) {
  const [cursor, setCursor] = useState<number | null>(null);
  const [axisRef, axisW] = useBoxWidth();
  const n = times.length;

  const at = (clientFrac: number) =>
    n === 0 ? null : Math.max(0, Math.min(n - 1, Math.round(clientFrac * (n - 1))));

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setCursor(at((e.clientX - r.left) / Math.max(1, r.width)));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { setCursor(null); return; }
    const base = cursor ?? n - 1;
    const map: Record<string, number> = {
      ArrowRight: base + 1, ArrowLeft: base - 1, Home: 0, End: n - 1,
    };
    const next = map[e.key];
    if (next === undefined) return;
    e.preventDefault();
    setCursor(Math.max(0, Math.min(n - 1, next)));
  };

  const cursorTime = cursor === null ? null : times[cursor] ?? null;
  const cursorFrac = cursor === null || n <= 1 ? null : cursor / (n - 1);

  const from = times[0] ?? 0;
  const to = times[times.length - 1] ?? 0;
  /* The ruler asks for the same number of ticks the PLOTS above it draw
     gridlines for — `Plot` derives its own from `round(width / 110)`, and a
     ruler that asked for a different count labelled one of three gridlines
     and left the other two anonymous. One rule, measured off the same box,
     so every gridline on this panel has a time under it. */
  const axisTarget = Math.max(2, Math.round((axisW > 0 ? axisW : 300) / 110));
  /* And whether the ruler's LAST label has room to be read where it lands.
     `TimeAxis` drops a tick past 96.5% of the window, a fixed fraction that
     on a 314px ruler still hands `18:00` 17px of runway for 30px of glyphs —
     which is how a time ruler comes to end in `18:0`. Rather than coarsen the
     step (which would leave two of the plots' three gridlines anonymous), the
     final label is turned to read INWARD from its own rule, the way the last
     label on a ruler always has. */
  const flipEnd = useMemo(() => {
    if (axisW === 0) return false;
    const ks = ticks(from, to, axisTarget).filter((k) => k.f <= 0.965);
    const last = ks[ks.length - 1];
    return last !== undefined && (1 - last.f) * axisW < AXIS_LABEL_PX;
  }, [from, to, axisW, axisTarget]);

  return (
    <div
      className={styles.history}
      tabIndex={0}
      role="group"
      aria-label={`Fleet history: ${traces.map((t) => t.eyebrow).join(', ')}. Arrow keys move the playhead.`}
      onPointerMove={onMove}
      onPointerLeave={() => setCursor(null)}
      onKeyDown={onKeyDown}
      onBlur={() => setCursor(null)}
    >
      <div className={styles.traces}>
        {/* ONE playhead for the stack. Every trace reads at this instant. */}
        {cursorFrac !== null && (
          <span
            className={styles.playhead}
            style={{ left: `${(cursorFrac * 100).toFixed(3)}%` }}
            aria-hidden="true"
          />
        )}
        {traces.map((t) => {
          /* A series whose source is unreachable is a GAP, and the gap keeps
             the strip's full height so the stack's shared x-scale and the
             ruler under it do not move when a feed dies. What it must never
             be is a LINE: `issued` is bucketed into a zero-filled array, so a
             command ledger that has never answered used to draw a flat trace
             along y=0 for nineteen hours — a picture of a fleet that issued
             nothing all night, under a header claiming a live poll. */
          const gapped = t.gap !== undefined;
          const v = gapped || cursor === null ? null : t.values[cursor] ?? null;
          return (
            <div key={t.key} className={styles.trace} data-gap={gapped ? '' : undefined}>
              <div className={styles.plotHead}>
                <span className={styles.plotTitle}>{t.eyebrow}</span>
                {!compact && <span className={styles.plotUnit}>{t.unit}</span>}
                <span className={styles.spacer} />
                {gapped ? (
                  /* The reason rides the slot the READING would have used, so
                     the eye finds it where it was already looking, and it is
                     the row's only colour. */
                  <span className={styles.plotGap} data-tone={t.gapTone ?? 'error'}>
                    {NO_VALUE} {t.gap}
                  </span>
                ) : compact && cursor === null ? (
                  <span className={styles.plotUnit}>{t.unit}</span>
                ) : (
                  <span className={styles.plotValue} data-cursor={cursor !== null}>
                    {cursor === null ? t.extent : v === null ? NO_VALUE : num(v)}
                  </span>
                )}
              </div>
              {gapped ? (
                <div
                  className={styles.gapBand}
                  style={{ height: `${height}px` }}
                  role="img"
                  aria-label={`${t.eyebrow}, ${t.unit}: ${t.gap}. No series is drawn.`}
                />
              ) : (
                <Plot
                  times={times}
                  height={height}
                  legend={false}
                  readout={false}
                  cursor={cursorTime}
                  min={t.min}
                  max={t.max}
                  series={[{
                    key: t.key,
                    label: t.eyebrow,
                    values: t.values,
                    unit: '',
                    digits: t.digits,
                  }]}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className={styles.axisBox} ref={axisRef} data-flip={flipEnd}>
        <TimeAxis from={from} to={to} target={axisTarget} />
      </div>
      <div className={styles.historyFoot}>
        <span className={styles.footLead} title={cursorTime === null ? foot : undefined}>
          {cursorTime === null ? foot : axisClock(cursorTime)}
        </span>
        <span className={styles.spacer} />
        {(cursorTime !== null || hint !== undefined) && (
          <span className={styles.footHint}>
            {cursorTime === null ? hint : 'one hour bucket'}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Column sets.

   Both tables declare their columns as MODULE CONSTANTS, and the exception
   table picks one of four fixed sets from the width of its container. That is
   deliberate and it is load-bearing:

     · the visible column set is a pure function of the container's width,
       computed from a ResizeObserver's `contentRect` on a box the table never
       writes into — so a table sitting still cannot re-lay itself;
     · the arrays are constant identities, so the primitive's layout effect
       does not re-run on every 1 s tick of the clock, which is what makes a
       column set measured at t=0 identical at t=60 down to the pixel.

   A column that depends on a value therefore takes it OFF THE ROW rather than
   out of a closure — see `AttentionRow.trace`.
   -------------------------------------------------------------------------- */

interface AttentionRow {
  robot: Robot;
  reasons: readonly FleetException['reasons'][number][];
  site: string;
  bay: string | null;
  build: string;
  /** The worst of this robot's readings — what the leading edge bar paints. */
  tone: Tone;
  /** Battery over the derived window, oldest → newest. */
  trace: readonly number[];
}

/* Every fixed-width column below declares a `max` BELOW its own longest ink.
   That is not a truncation instruction — a `max` under the measured content
   width resolves to "this column takes no share of the surplus" — and it is
   how the panel's spare width ends up in the one column that can turn it into
   ink, instead of opening seven even gaps between columns that are already as
   wide as their values. */
const A_ROBOT: Column<AttentionRow> = {
  key: 'robot',
  header: 'Robot',
  kind: 'identity',
  max: '24ch',
  /* The id is a link, and it is the only one in the row. Double-click still
     opens the robot; this makes the same destination one the browser
     understands — ⌘-click, middle-click, copy link address. See
     `components/AppLink`. */
  cell: (r) => (
    <>
      <span className={styles.name}>{r.robot.name ?? r.robot.robot_id}</span>
      <RobotLink id={r.robot.robot_id} className={styles.id} />
    </>
  ),
};

/* Below ~660px of table the human name is what goes: `jetrover-06` is the
   string the operator types, radios and greps for, and it is half the width. */
const A_ROBOT_ID: Column<AttentionRow> = {
  key: 'robot',
  header: 'Robot',
  kind: 'id',
  mono: true,
  max: '14ch',
  cell: (r) => <RobotLink id={r.robot.robot_id} />,
};

/* Site and bay in one cell: `fremont-facility · B4` is where a human has to
   walk to, and the bay is four characters — it does not deserve a column of
   its own, and splitting it would put a rule through one fact. */
const A_SITE: Column<AttentionRow> = {
  key: 'site',
  header: 'Site',
  kind: 'id',
  mono: true,
  max: '20ch',
  cell: (r) => (
    <>
      {r.site}
      {r.bay !== null && <span className={styles.bay}>{r.bay}</span>}
    </>
  ),
};

const A_BUILD: Column<AttentionRow> = {
  key: 'build',
  header: 'Build',
  kind: 'id',
  mono: true,
  dim: true,
  max: '12ch',
  cell: (r) => r.build,
};

/* The one column that genuinely turns width into information, so it is the
   claimant every wider monitor hands its surplus to.

   How many readings it prints is part of the width policy, not a runtime
   decision: a `text` column's floor is its own longest ink, so a row carrying
   six readings would push the table past its panel on a 1366 laptop however
   the other columns were sized. Each set below therefore names the reason
   count it was measured at. The overflow is COUNTED, never hidden, and the row
   opens the robot, where all six are named. */
function reasonsColumn(shown: number): Column<AttentionRow> {
  return {
    key: 'reasons',
    header: 'Readings out of limits',
    kind: 'text',
    flex: true,
    cell: (r) => (
      /* `data-stretch` is the table primitive's contract for a cell that fills
         its column rather than sitting at its own width. Without it the
         resolver measures this flex box, gets back the column's CURRENT width,
         and adopts that as the column's floor — a base that can only ever
         grow, which is how a table ends up wider than the panel it lives in.
         With it the column takes the declared prose base and is the first
         claimant on whatever width is left, which is what it should be: this
         is the only cell here that turns pixels into information. */
      <span className={styles.reasons} data-stretch>
        <ReasonList reasons={r.reasons.slice(0, shown)} />
        {r.reasons.length > shown && (
          <span className={styles.more}>+{r.reasons.length - shown}</span>
        )}
      </span>
    ),
  };
}

const A_REASONS_4 = reasonsColumn(4);
const A_REASONS_2 = reasonsColumn(2);

const A_TREND: Column<AttentionRow> = {
  key: 'trend',
  header: 'Charge',
  width: '76px',
  align: 'right',
  cell: (r) => (
    r.trace.length < 2
      ? <span className={styles.noTrace}>{NO_VALUE}</span>
      : (
        <Sparkline
          values={r.trace}
          width={64}
          height={14}
          warn={BATTERY_WARN}
          bad={BATTERY_BAD}
          unit="%"
          probe={false}
          label={`${r.robot.robot_id} battery`}
        />
      )
  ),
};

const A_BATTERY: Column<AttentionRow> = {
  key: 'battery',
  header: 'Battery',
  kind: 'meter',
  cell: (r) => <MeterCell value={r.robot.battery_pct} warn={BATTERY_WARN} bad={BATTERY_BAD} />,
};

const A_CERT: Column<AttentionRow> = {
  key: 'cert',
  header: 'Cert expires',
  kind: 'age',
  align: 'right',
  max: '15ch',
  cell: (r) => (
    <Timestamp
      value={r.robot.cert_not_after}
      warnWithin={CERT_WARN_DAYS * 86400}
      badWithin={CERT_BAD_DAYS * 86400}
    />
  ),
};

/**
 * An age that has to agree with the SEEN reading printed beside it.
 *
 * `Timestamp`'s own `staleAfter`/`deadAfter` render a dead age as
 * `--text-dis` — right for a value nobody should trust any more, and wrong
 * here. This row is IN this table because SEEN broke a limit, and the reading
 * that put it here is three columns to the left in `--error-fg`. Rendering
 * the same age dimmer than a fresher one inverts the ranking the table is
 * sorted by: `7h 39m` came out below-text-low grey while `10m 23s` in the row
 * under it was amber.
 *
 * So the age is toned by `fleetMetrics`' own thresholds — the ones the matrix
 * cell, the census bucket and the `SEEN` reason all use — and there is one
 * definition of "how late is late" on the surface.
 */
function AgeCell({
  value,
  warnMs,
  badMs,
}: {
  value: string | null | undefined;
  warnMs: number;
  badMs: number;
}) {
  const now = useNow();
  const t = value == null ? NaN : Date.parse(value);
  /* No timestamp, no tone: the em dash the primitive prints is an absence and
     stays `--text-low`. A missing value must never be painted as a reading. */
  return (
    <span
      className={styles.ageCell}
      data-tone={Number.isNaN(t) ? undefined : levelTone(level(now - t, warnMs, badMs, true))}
    >
      <Timestamp value={value} />
    </span>
  );
}

const A_SEEN: Column<AttentionRow> = {
  key: 'seen',
  header: 'Last seen',
  kind: 'age',
  align: 'right',
  max: '13ch',
  cell: (r) => (
    <AgeCell value={r.robot.last_seen} warnMs={SEEN_WARN_MS} badMs={SEEN_BAD_MS} />
  ),
};

/** Widest set first. Each `min` is the container width at which that set was
    MEASURED to fit at its own content width, identity column intact — a width
    sweep with a clipping detector walking every descendant of every cell, not
    an estimate. Three of the floors came down after that sweep: the previous
    ones were conservative enough to cost a 1600 monitor its charge traces and
    a 1440 its site column, for slack that was never used. The floor below each
    is the width the sweep first drew a truncated reading at — `SEEN 10h` where
    the reading is `10h 58m`, which is a clipped measurement and the one thing
    a shed column is preferable to. */
const ATTENTION_SETS: readonly { min: number; columns: readonly Column<AttentionRow>[] }[] = [
  { min: 1290, columns: [A_ROBOT, A_SITE, A_BUILD, A_REASONS_4, A_TREND, A_BATTERY, A_CERT, A_SEEN] },
  { min: 1190, columns: [A_ROBOT, A_SITE, A_REASONS_4, A_TREND, A_BATTERY, A_CERT, A_SEEN] },
  { min: 1000, columns: [A_ROBOT, A_SITE, A_REASONS_4, A_TREND, A_BATTERY, A_SEEN] },
  { min: 900, columns: [A_ROBOT, A_SITE, A_REASONS_4, A_BATTERY, A_SEEN] },
  { min: 780, columns: [A_ROBOT, A_REASONS_4, A_BATTERY, A_SEEN] },
  { min: 640, columns: [A_ROBOT, A_REASONS_2, A_BATTERY, A_SEEN] },
  { min: 550, columns: [A_ROBOT_ID, A_REASONS_2, A_BATTERY, A_SEEN] },
  { min: 0, columns: [A_ROBOT_ID, A_REASONS_2, A_BATTERY] },
];

function attentionColumns(width: number): readonly Column<AttentionRow>[] {
  /* Width 0 is "not measured yet" — render the set the widest layout uses so
     the first paint is not a narrow table that then widens. */
  if (width === 0) return ATTENTION_SETS[0]!.columns;
  return (ATTENTION_SETS.find((s) => width >= s.min) ?? ATTENTION_SETS[ATTENTION_SETS.length - 1]!)
    .columns;
}

/**
 * The ledger's age cell: how long, not how long ago.
 *
 * `Timestamp` renders a relative age as `4m 26s ago`, which is right in a
 * table whose header says `Last seen` and wrong in one whose header says
 * `In state` — there the suffix restates the header on every row, and in a
 * 410px rail those four characters are 30px that the ledger cannot spare.
 * THE BAR §11.1 wants a queue's age as the bare interval (`50 s`, `6 min`)
 * for exactly this reason. Same thresholds, same colours, same absolute forms
 * in the title; one word less per row.
 */
function QueueAge({
  value,
  warnMs,
  badMs,
}: {
  value: string | null | undefined;
  /** Omitted for a terminal state: history has no deadline. */
  warnMs?: number | undefined;
  badMs?: number | undefined;
}) {
  const now = useNow();
  const t = value == null ? NaN : Date.parse(value);
  if (Number.isNaN(t)) return <span className={styles.qAge}>{NO_VALUE}</span>;
  const age = Math.max(0, now - t);
  return (
    <time
      className={styles.qAge}
      dateTime={value ?? undefined}
      data-tone={warnMs === undefined || badMs === undefined
        ? undefined
        : levelTone(level(age, warnMs, badMs, true))}
      title={`${utc(value)}  ·  ${local(value)}  ·  ${relative(value, now)}`}
    >
      {duration(age)}
    </time>
  );
}

/* The ledger is a feed, so subject and verb are ONE cell: `jetrover-07
   stop_mapping` is the event. Splitting them into two columns costs 30px of
   insets and a column rule inside a 424px rail that has to hold twenty rows,
   and it buys an alignment nobody scans — the question here is "what just
   happened", not "how many pauses are there". */
const COMMAND_COLUMNS: readonly Column<Command>[] = [
  {
    key: 'subject',
    header: 'Robot · command',
    kind: 'identity',
    max: '26ch',
    /* `issued_by` belongs on this line — `auto-dispatch` stopping eight rovers
       at 19:00Z and a person doing it by hand are different events — and it is
       not here because it does not fit: the rail is 410px, the three columns
       already price at 395, and the shortest honest rendering of the principal
       is 13 more characters. It is on the robot's own command surface, which
       is one click away. Measured, not assumed; see the note in the report. */
    /* `data-stretch`: the subject fills its column rather than sitting at its
       own ink, so the column's floor is the declared identity base and the
       rail's surplus lands here instead of on the two fixed readings beside
       it. Without it the three columns priced at their own longest strings —
       `jetrover-07 stop_mapping`, `cancelled`, `4m 26s ago` — summed past the
       410px rail, and the 26px selection column pushed the overflow far
       enough to clip the commit verb off the end of the action bar. */
    cell: (c) => (
      <span className={styles.cmdSubject} data-stretch>
        <span className={styles.cmdRobot}>{c.robot_id}</span>
        <span className={styles.cmdVerb}>{c.command}</span>
      </span>
    ),
  },
  {
    key: 'status',
    header: 'State',
    kind: 'status',
    max: '14ch',
    cell: (c) => <StateLabel tone={COMMAND_TONE[c.status]} label={c.status} />,
  },
  /* Same rule as `Last seen`, and the same bug it was carrying: a command
     still OPEN after two hours was rendering in `--text-dis`, dimmer than one
     that had been open for twenty minutes. An open command that has stopped
     moving is the one thing in this ledger an operator has to chase, so its
     age is toned — a quarter of an hour in state is a look, two hours is a
     fault. A command that has REACHED a terminal state is history: it is
     `completed 10m ago`, and there is no threshold on that, so it stays low. */
  {
    key: 'age',
    header: 'In state',
    kind: 'age',
    align: 'right',
    max: '13ch',
    cell: (c) => (
      OPEN_COMMANDS.has(c.status)
        ? <QueueAge value={c.updated_at} warnMs={QUEUE_WARN_MS} badMs={QUEUE_BAD_MS} />
        : <QueueAge value={c.updated_at} />
    ),
  },
];

/** The agent's own words for why it refused, off `result`. */
function refusal(c: Command): { code: string; error: string } | null {
  const r = c.result;
  if (r === null || typeof r !== 'object') return null;
  const code = typeof r['code'] === 'string' ? r['code'] : null;
  const error = typeof r['error'] === 'string' ? r['error'] : null;
  if (code === null && error === null) return null;
  return { code: code ?? 'FAILED', error: error ?? '' };
}

/* The refusal view's own column set. `State` is dropped from it, because in a
   list filtered to one state it is fourteen rows of the same word and a column
   of fourteen red dots — the least informative 97px on the surface. The agent
   puts a machine code and a sentence in `result` when it refuses, and neither
   is rendered anywhere else in this console: `PLAN_TIMEOUT` on four robots at
   three sites is a navigation-stack story, and `BATTERY_FLOOR` on four is a
   charging one. The code is the cell; the sentence is its title. */
const REFUSAL_COLUMNS: readonly Column<Command>[] = [
  COMMAND_COLUMNS[0]!,
  {
    key: 'why',
    header: 'Reason',
    kind: 'enum',
    max: '15ch',
    cell: (c) => {
      const r = refusal(c);
      /* A refusal with no reason on it is not a reason we can invent. */
      if (r === null) return <span className={styles.cmdNoWhy}>{NO_VALUE}</span>;
      return (
        <span className={styles.cmdWhy} title={r.error === '' ? undefined : r.error}>
          {r.code}
        </span>
      );
    },
  },
  {
    key: 'age',
    header: 'Refused',
    kind: 'age',
    align: 'right',
    max: '13ch',
    cell: (c) => <QueueAge value={c.updated_at} />,
  },
];

/* ==========================================================================
   The commit path.

   Everything above this line is a report. This is the part that touches a
   robot, and it is the only part of the surface that can — so it is written
   to the two rules that matter for a write path on an operations console:

     · nothing is dispatched by a click. A verb STAGES; a second, differently
       shaped control commits (THE BAR §11.9 / tell #31). Direct manipulation
       stages, a button commits, and the two are never the same press.
     · what a verb can do is read off the platform's own contract, never
       assumed. `/api/command-profiles` declares, per morphology, which
       commands exist, what hazard class each is in and which params it
       requires; a verb the selected robots' profiles do not all carry is
       disabled and prints the reason rather than failing at the API.

   Both selection surfaces — the exception table and the ledger — put their
   bar in the band their own header occupied, so selecting shifts nothing.
   ========================================================================== */

/** The three commands every morphology in this fleet's profile set carries,
    which is why they are the ones on the bar. Neither `Stop` nor `Return` is
    destructive — they are RECOVERY (§11.7), so neither is red; the console
    spends red on the one verb here that destroys queued work. */
interface RobotVerb {
  key: string;
  label: string;
  command: CommandName;
  /** The past participle the receipt prints: `3 held`, `3 recalled`. */
  said: string;
}

/* No glyphs on these. A verb's label IS its affordance, and three icons in a
   22px band that has to hold a count, three counted verbs and a clear in a
   410px rail is 60px spent on decoration (§8, tell 8). */
const ROBOT_VERBS: readonly RobotVerb[] = [
  { key: 'hold', label: 'Hold', command: 'pause', said: 'held' },
  { key: 'return', label: 'Return', command: 'return_home', said: 'recalled' },
  { key: 'stop', label: 'Stop', command: 'stop', said: 'stopped' },
];

/** What the ledger's three verbs are allowed to act on. A command that has
    already reached a terminal state cannot be acknowledged or cancelled, and
    one that is still open cannot be retried — so each verb carries the count
    of the selection it ACTUALLY applies to, not the size of the selection. */
const ACKABLE: ReadonlySet<CommandStatus> = new Set(['pending', 'sent']);
const CANCELABLE: ReadonlySet<CommandStatus> = new Set(['pending', 'sent', 'acked']);
const RETRYABLE: ReadonlySet<CommandStatus> = new Set(['failed', 'cancelled']);

type CmdVerbKey = 'ack' | 'retry' | 'cancel';

const CMD_VERBS: readonly {
  key: CmdVerbKey;
  label: string;
  applies: ReadonlySet<CommandStatus>;
  /** Why the excluded rows are excluded, printed on the disabled verb. */
  needs: string;
  danger?: boolean;
}[] = [
  { key: 'ack', label: 'Acknowledge', applies: ACKABLE, needs: 'pending or sent' },
  { key: 'retry', label: 'Retry', applies: RETRYABLE, needs: 'failed or cancelled' },
  {
    key: 'cancel', label: 'Cancel', applies: CANCELABLE,
    needs: 'pending, sent or acked', danger: true,
  },
];

/* ---- the platform's own command contract -------------------------------- */

interface ProfileCommand {
  command: string;
  hazard: string;
  required_params: readonly string[];
}

interface CommandProfile {
  morphology: string;
  version: string;
  commands: readonly ProfileCommand[];
}

/** `/api/command-profiles` — static per deployment, so it is read once and
    never polled. It is what makes a disabled verb honest: `koko` has no
    `start_capture`, and a console that offered one would be lying about the
    fleet rather than about the network. */
function useCommandProfiles() {
  return useQuery({
    queryKey: ['command-profiles'] as const,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async (): Promise<Map<string, CommandProfile>> => {
      const r = await apiFetch('/api/command-profiles');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = (await r.json()) as { profiles?: CommandProfile[] };
      return new Map((body.profiles ?? []).map((p) => [p.morphology, p]));
    },
  });
}

/** Whether every one of these morphologies declares `command`, and what the
    contract says about it. `null` while the profiles have not landed — the
    verb then stays enabled, because refusing to act on a fleet because a
    static lookup table is in flight is worse than letting the API refuse. */
function profileCheck(
  profiles: Map<string, CommandProfile> | undefined,
  morphologies: readonly string[],
  command: string,
): { ok: boolean; hazard: string | null; version: string | null; missing: string[] } {
  if (profiles === undefined || morphologies.length === 0) {
    return { ok: true, hazard: null, version: null, missing: [] };
  }
  const missing: string[] = [];
  let hazard: string | null = null;
  let version: string | null = null;
  for (const m of morphologies) {
    const p = profiles.get(m);
    const c = p?.commands.find((x) => x.command === command);
    if (p === undefined || c === undefined) { missing.push(m); continue; }
    hazard ??= c.hazard;
    version ??= p.version;
  }
  return { ok: missing.length === 0, hazard, version, missing };
}

/* ---- shift-click, without the primitive having to hand back an event ----- */

/**
 * The modifier at the instant of the last press, latched at the document.
 *
 * `DataTable`'s checkbox reports the NEXT SELECTION, not the event that
 * produced it — which is the right contract for a primitive and leaves range
 * selection with nothing to read. Rather than fork the primitive, the latch
 * is read on capture, before the change event it belongs to is delivered, so
 * shift-click and shift-space both arrive correctly. Noted in the report as
 * the one thing `DataTable` is missing.
 */
function useShiftLatch(): { current: boolean } {
  const ref = useRef(false);
  useEffect(() => {
    const on = (e: Event) => {
      const m = e as MouseEvent & KeyboardEvent;
      ref.current = m.shiftKey === true;
    };
    document.addEventListener('pointerdown', on, true);
    document.addEventListener('keydown', on, true);
    return () => {
      document.removeEventListener('pointerdown', on, true);
      document.removeEventListener('keydown', on, true);
    };
  }, []);
  return ref;
}

/**
 * Turn a primitive's "here is the next set" into a range model.
 *
 * The delta between the old set and the new one is a single key for every
 * press that came off one checkbox; with shift held, that key and the last
 * un-shifted one bracket a run in the CURRENT row order. Select-all and
 * clear-all arrive as multi-key deltas and pass straight through.
 */
function rangeSelect(
  keys: readonly string[],
  prev: ReadonlySet<string>,
  next: ReadonlySet<string>,
  shift: boolean,
  anchor: { current: string | null },
): ReadonlySet<string> {
  let delta: string | null = null;
  let count = 0;
  for (const k of next) if (!prev.has(k)) { delta = k; count += 1; }
  for (const k of prev) if (!next.has(k)) { delta = k; count += 1; }
  if (count !== 1 || delta === null) return next;
  if (shift && anchor.current !== null) {
    const a = keys.indexOf(anchor.current);
    const b = keys.indexOf(delta);
    if (a >= 0 && b >= 0) {
      const out = new Set(prev);
      for (let i = Math.min(a, b); i <= Math.max(a, b); i += 1) out.add(keys[i]!);
      return out;
    }
  }
  anchor.current = delta;
  return next;
}

/* ---- the bar ------------------------------------------------------------- */

/** A verb as the bar draws it: its own count, and its own reason to be off. */
interface BarVerb {
  key: string;
  label: string;
  /** How many of the selection this verb would actually touch. */
  n: number;
  danger?: boolean | undefined;
  /** Printed on the control when `n` is 0 or the profile refuses it. */
  why?: string | undefined;
}

/** What has been staged and not yet sent. Held by the page, not by the bar:
    the same staged action is legible from the matrix's header and from the
    table's, because there is one of it. */
interface Staged {
  scope: 'robots' | 'commands';
  verb: string;
  label: string;
  /** The one-line restatement — subject, hazard, contract version, count. */
  says: string;
  n: number;
  danger: boolean;
  run: () => Promise<Receipt>;
}

interface Receipt {
  scope: 'robots' | 'commands';
  ok: number;
  failed: number;
  /** The API's own words for the first refusal. Never paraphrased. */
  reason: string | null;
  verb: string;
  at: number;
}

/**
 * The counted action bar, in the band the header was already using.
 *
 * Three states, one height, one position: the verbs, the staged commit pair,
 * and the receipt. Nothing here moves the surface — the bar replaces a
 * header, it never inserts a row (§8, tell 3; and the reason the page-level
 * outage band was deleted).
 */
function ActionBar({
  count,
  verbs,
  staged,
  receipt,
  sending,
  onStage,
  onDiscard,
  onCommit,
  onClear,
  subject,
}: {
  /** `null` where the host band already prints it — `DataTable` puts
      `n selected` at the head of its own action bar, and two counts in one
      22px row is the primitive and the caller both answering. */
  count: number | null;
  verbs: readonly BarVerb[];
  staged: Staged | null;
  receipt: Receipt | null;
  sending: boolean;
  onStage: (key: string) => void;
  onDiscard: () => void;
  onCommit: () => void;
  onClear: () => void;
  /** `robots` / `commands` — the noun the count is counting. */
  subject: string;
}) {
  if (staged !== null) {
    return (
      <div className={styles.actBar} data-staged="">
        {/* The restatement. It is not a paraphrase of the button: it names the
            wire command, its hazard class and the profile version that
            declared it, so what is about to leave the browser is on screen
            in the platform's own vocabulary. */}
        <span
          className={styles.actSays}
          data-danger={staged.danger ? '' : undefined}
          title={staged.says}
        >
          {staged.says}
        </span>
        <span className={styles.spacer} />
        {/* Cancel first, commit second, equal size, never smaller (§11.9).
            `Discard` rather than `Cancel`, because on the ledger's bar the
            staged verb IS cancel and two of them on one row is a control an
            operator has to read twice. */}
        <Button size="sm" onClick={onDiscard} disabled={sending}>Discard</Button>
        <Button
          size="sm"
          variant={staged.danger ? 'danger' : 'primary'}
          badge={staged.n}
          disabled={sending}
          onClick={onCommit}
          /* The verb is one word because the whole sentence is four
             characters to its left and never scrolls away; the accessible
             name carries it in full. */
          title={`Send — ${staged.says}`}
          aria-label={`Send ${staged.verb}, ${staged.n}`}
        >
          {sending ? 'Sending' : 'Send'}
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.actBar}>
      {count !== null && receipt === null && (
        <span className={styles.actCount}>
          {count} {subject}
        </span>
      )}
      {receipt !== null && (
        /* The outcome, in the same band, in the API's own words. It stays in
           the count's slot until the selection changes or is cleared — a
           receipt that fades on a timer is a receipt nobody read, and the
           trailing ✕ that clears the selection clears this with it, so the
           bar never carries two dismiss glyphs. The durable record is the
           ledger underneath: `sent` → `acked` is in the State column the
           instant the refetch lands, and a dispatched `pause` is a new row. */
        <span
          className={styles.actReceipt}
          data-tone={receipt.failed > 0 ? 'error' : 'good'}
          role="status"
          title={receipt.reason ?? undefined}
        >
          {receipt.ok} {receipt.verb}
          {receipt.failed > 0 && (
            <>
              {' · '}{receipt.failed} refused
              {receipt.reason !== null && <i> — {receipt.reason}</i>}
            </>
          )}
        </span>
      )}
      <span className={styles.spacer} />
      {verbs.map((v) => (
        <Button
          key={v.key}
          size="sm"
          badge={v.n}
          variant={v.danger === true ? 'danger' : 'secondary'}
          disabled={v.n === 0 || v.why !== undefined}
          title={v.why ?? `Stage ${v.label.toLowerCase()} for ${v.n}`}
          onClick={() => onStage(v.key)}
        >
          {v.label}
        </Button>
      ))}
      <button
        type="button"
        className={styles.actX}
        onClick={onClear}
        aria-label="Clear selection"
        title="Clear selection"
      >
        <Icon name="close" size={12} />
      </button>
    </div>
  );
}

/** The API's own refusal, in one line. Never paraphrased and never
    invented — a validation body is unwrapped to its `detail`/`msg` because
    that is the sentence the platform wrote, and everything else is passed
    through verbatim. */
function apiReason(status: number, text: string): string {
  const t = text.trim();
  if (t === '') return `HTTP ${status}`;
  try {
    const body = JSON.parse(t) as unknown;
    if (body !== null && typeof body === 'object') {
      const d = (body as Record<string, unknown>)['detail'];
      if (typeof d === 'string') return `${status} ${d}`;
      if (Array.isArray(d)) {
        const first = d[0] as Record<string, unknown> | undefined;
        const msg = first?.['msg'];
        if (typeof msg === 'string') return `${status} ${msg}`;
      }
    }
  } catch {
    /* not JSON — the raw body is the message. */
  }
  return `${status} ${t.replace(/\s+/g, ' ')}`;
}

/** One leg of a dispatch. Resolves to the API's words on failure. */
async function leg(url: string, init: RequestInit): Promise<string | null> {
  try {
    const r = await apiFetch(url, init);
    if (r.ok) return null;
    return apiReason(r.status, await r.text().catch(() => ''));
  } catch (e) {
    return legFailure(e);
  }
}

/** Fan a dispatch out over its targets and tally what came back. Nothing is
    optimistic: the surface does not move until the ledger has been re-read. */
async function dispatch(
  scope: 'robots' | 'commands',
  verb: string,
  legs: readonly (() => Promise<string | null>)[],
): Promise<Receipt> {
  const out = await Promise.all(legs.map((f) => f()));
  const failed = out.filter((x) => x !== null);
  return {
    scope,
    ok: out.length - failed.length,
    failed: failed.length,
    reason: failed[0] ?? null,
    verb,
    at: Date.now(),
  };
}

/* ========================================================================== */

/** A figure that only exists while its feed does. `…` first paint, `—` down. */
function fmt(n: number | null | undefined, feed: Feed): string {
  if (feed.down) return NO_VALUE;
  if (feed.loading) return '…';
  if (n == null) return NO_VALUE;
  return n.toLocaleString();
}

export function Dashboard() {
  const now = useNow();
  /* The console-wide hold. Not a local toggle: `L` in the shell's surface
     strip stops every poll in the app, and this surface's job is to SAY so
     rather than to keep flying eight live lights over a frozen page. */
  const { held } = useLiveHold();
  const statsQ = useStats();
  const robotsQ = useRobots();
  const { navigate } = useFleetRoute();

  const stats = statsQ.data;
  const robots = robotsQ.data;

  const [order, setOrder] = useState<'id' | 'severity' | 'site'>('id');
  /* Three questions the ledger answers, not two. `open` is the backlog — what
     the fleet has been told and has not finished; `failed` is the part of the
     ledger that has already lost, which the newest-first `all` view buries the
     moment a scheduler issues a dozen commands in one minute. A refusal is a
     fact about the fleet that no robot's own status row carries: koko-04 can
     be nominal on all six metrics and still have refused its last three
     commands. */
  const [cmdView, setCmdView] = useState<CommandView>('all');
  /* ---- the surface's two selections --------------------------------------
     Robots and commands are two different subjects and therefore two
     different sets. The robot set is shared between the matrix and the
     exception table on purpose: picking a broken column out of the texture
     and picking its row out of the list are the same act, and a console that
     made them two selections would make the operator do it twice. */
  const [robotSel, setRobotSel] = useState<ReadonlySet<string>>(NO_SELECTION);
  const [cmdSel, setCmdSel] = useState<ReadonlySet<string>>(NO_SELECTION);
  /* One staged action for the whole surface, and one receipt. Two bars can
     draw them — the matrix's header and the table's — because there is only
     ever one of each, so the two bars cannot disagree. */
  const [staged, setStaged] = useState<Staged | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [sending, setSending] = useState(false);
  const shift = useShiftLatch();
  const robotAnchor = useRef<string | null>(null);
  const cmdAnchor = useRef<string | null>(null);
  const profilesQ = useCommandProfiles();
  const [fitRef, fitWidth] = useBoxWidth();
  /* The one robot this surface is pointing at, wherever the pointing happened
     — a matrix column, a site dot, a keyboard focus in either. Held as an ID,
     not as a dot: the roster repolls every five seconds, and a cursor holding
     a snapshot of a robot would keep printing a fix the fleet has since moved
     on from. One cursor, so the band's column mark, the census
     cross-highlight, the charge bin, the ringed site dot and the raw fix in
     the Sites header are all the same fact rather than five hovers. */
  const [cursor, setCursor] = useState<string | null>(null);
  /* The floor for band 1's grid row, reported by the band's own geometry. See
     `PANEL_CHROME`. Held here rather than in the band because the row it has
     to constrain is this component's grid, not the band's.

     It starts at the height the band takes for a fleet the size of this one at
     the benchmark pitch, so the three states that have no geometry to report —
     loading, unreachable, empty roster — hold the SAME row the loaded band
     will, and the page does not restack under the first poll (§8, tell 3). */
  const [bandH, setBandH] = useState(BAND_DEFAULT_H);
  /* And how many columns it painted, so the panel's `6×N` describes the
     picture rather than the roster. `null` until the band has reported —
     before that the header states the roster, which is what it will be for
     every fleet the band can hold whole. */
  const [bandShown, setBandShown] = useState<number | null>(null);
  /* The estate rails share one `1fr` column, so measuring the block reports
     both — and the bar cap derived from it cannot feed back into it. */
  const [railRef, railWidth] = useBoxWidth();
  /* The two bottom scrollers' quantised ceilings. Each pair is an OUTER box
     that reports the height the page grid gave it and an INNER box that is
     clipped to a whole number of rows; observing one and writing the other is
     what keeps the measurement out of a loop with its own result. */
  const [excRef, excBoxH] = useBoxHeight();
  const [cmdRef, cmdBoxH] = useBoxHeight();
  const excClip = wholeRows(excBoxH);
  const cmdClip = wholeRows(cmdBoxH);
  /* The group grid's column set is a function of ITS OWN box, not of the
     viewport: three panels share the main column now, so the same 1600px
     screen gives this panel 410px and a 1920 one 540px. A media query cannot
     see that difference; a ResizeObserver on a box the grid cannot widen can.
     `--cols` values are the three templates' own minimum sums. */
  const [groupsRef, groupsWidth] = useBoxWidth();
  /* Same policy for the history panel's header: below ~300px the `Panel`
     primitive gives its meta the room before its TITLE, and a panel that has
     traded its own name for a coverage fraction has lost the more important
     of the two. So the fraction sheds instead — it is on the strip above as
     well, over the whole window rather than the last hour. */
  const [histRef, histWidth] = useBoxWidth();
  /* The strip's own box, on the same policy: the source path is the clause
     that sheds first, because the liveness dot is the half an operator scans
     for and the paths are already named in full on the line under the title
     the moment one of them fails. */
  const [stripRef, stripWidth] = useBoxWidth();
  const groupCols = groupsWidth === 0 || groupsWidth >= 490
    ? 'full'
    : groupsWidth >= 400 ? 'mid'
      : groupsWidth >= 340 ? 'lean' : 'min';

  const fleet = useMemo(() => robots ?? [], [robots]);
  const ids = useMemo(() => fleet.map((r) => r.robot_id).sort(), [fleet]);

  const telemetry = useFleetTelemetry(ids);
  const commands = useFleetCommands(ids);
  const scans = useFleetScans(ids);

  /* One Feed per endpoint. Every figure, chip and empty line below asks these
     rather than asking a query whether its array happens to be empty. */
  const statsFeed = feedOf(statsQ, stats !== undefined, '/api/stats', 'stats', EVERY_STATS);
  const robotsFeed = feedOf(robotsQ, robots !== undefined, '/api/robots', 'robots', EVERY_ROSTER);
  const telemetryFeed = fanFeed(
    telemetry, telemetry.data !== undefined,
    '/api/robots/{id}/telemetry', 'telemetry', EVERY_TELEMETRY, robotsFeed, ids.length > 0,
  );
  const commandsFeed = fanFeed(
    commands, commands.data !== undefined,
    '/api/robots/{id}/commands', 'commands', EVERY_COMMANDS, robotsFeed, ids.length > 0,
  );
  const scansFeed = fanFeed(
    scans, scans.data !== undefined,
    '/api/robots/{id}/scans', 'scans', EVERY_SCANS, robotsFeed, ids.length > 0,
  );

  /* ---- one reading per source, taken once ---------------------------------
     Every panel header below asks its OWN source how old its answer is, and
     they all ask on the same tick: `now` and `held` are read once here, so
     eight marks cannot disagree with each other or with the ages printed in
     the rows underneath them. `frozen` is what dims a panel's values — see
     `markOf`. */
  const rosterMark = markOf(robotsFeed, now, held);
  /* `/api/stats` gets no mark of its own because it draws no panel: every
     figure on this surface is counted from the roster, and the aggregate
     stands in only while the roster is unknown — the case the strip's own
     mark already covers, because `stripFeeds` holds whichever of the two is
     actually the source.

     And when the roster IS answering, a failing `/api/stats` is reported
     nowhere on this surface, on purpose: not one figure on screen is then
     taken from it, and a console that raises an alarm about a source it is
     not reading has taught its operator to discount alarms. The shell's own
     strip declares `stats` for this surface and reports it there. */
  const telemetryMark = markOf(telemetryFeed, now, held);
  const commandsMark = markOf(commandsFeed, now, held);
  const scansMark = markOf(scansFeed, now, held);
  /* Fleet history is the one panel here with TWO sources — an hourly ingest
     rate off the telemetry fan-out and an hourly command rate off the ledger
     — so its header reports the worse of the two. Printing telemetry's
     `● Polling 2m` while the ledger had never answered was this page's most
     expensive remaining lie: one green light over two series, only one of
     which existed. The panel's DIMMING stays telemetry's own (see the Fleet
     history panel), because a live trace may not be greyed for a dead
     sibling; that sibling draws its own gap instead. */
  const histPick = worstOf([telemetryFeed, commandsFeed], now, held);

/* Legs of the telemetry fan-out that did not answer this cycle. Partial
     truth is still truth: a fan-out where SOME legs failed is not a quieter
     fleet, and the trace's header says so rather than pretending to a full
     census. It also decides whether the coverage fraction beside it fits. */
  const legsFailed = telemetryFeed.ok && telemetry.data !== undefined
    ? telemetry.data.failed
    : 0;

  /* A roster that answered with nothing. Not a failure, and not a wait: the
     panels fed by the fan-outs have nothing to draw and say exactly that. */
  const rosterEmpty = (robotsFeed.ok || robotsFeed.stale) && ids.length === 0;

  /* One re-read per source, addressed by the endpoint its Feed carries.

     A panel's fault row re-reads THAT panel's source and nothing else. The
     page-wide `Retry 4` this replaces counted endpoints because it had to —
     it stood at the top of the page and could not know which of eight panels
     the operator was actually reading. A verb inside the panel knows.

     A fan-out that failed because the ROSTER failed carries the roster's own
     endpoint (see `fanFeed`), so its verb correctly re-reads the roster, and
     the fan-outs re-enable themselves the moment a roster lands. */
  const retry = useCallback((f: Feed) => {
    switch (f.endpoint) {
      case '/api/stats': void statsQ.refetch(); break;
      case '/api/robots': void robotsQ.refetch(); break;
      case '/api/robots/{id}/telemetry': void telemetry.refetch(); break;
      case '/api/robots/{id}/commands': void commands.refetch(); break;
      case '/api/robots/{id}/scans': void scans.refetch(); break;
      default: break;
    }
  }, [statsQ, robotsQ, telemetry, commands, scans]);

  const attention = useMemo(() => fleetExceptions(fleet), [fleet]);
  const worstBy = useMemo(() => {
    const m = new Map<string, Tone>();
    for (const e of attention) m.set(e.robot.robot_id, worstTone(e));
    return m;
  }, [attention]);

  const ledgerData = commands.data?.value;
  const ledger = useMemo(() => ledgerData ?? [], [ledgerData]);
  const telemetryBy = telemetry.data?.value;

  const history = useMemo(
    () => historyWindow(telemetryBy ?? new Map(), ledger, now),
    [telemetryBy, ledger, now],
  );
  const spanHours = Math.max(1, Math.round((history.to - history.from) / HOUR));
  /* The window is derived FROM the history: its start is the latest first
     sample among the capped robots. With no robot reporting there is nothing
     to derive it from and `historyWindow` falls back to a nominal 24 h — a
     number that describes the fallback, not the fleet. So the window only has
     a label while at least one robot actually contributed to it. */
  const spanKnown = history.source !== 'nominal';
  const spanLabel = spanKnown ? `${spanHours} h` : blank(telemetryFeed);
  /* The same window with the space closed up, for the ONE place it is set in
     an uppercase eyebrow. `Telemetry 19 h` uppercases to `TELEMETRY 19 H`,
     and a lone capital H reads as a unit that has come apart from its
     figure — §4 keeps uppercase for structural headers and never lets a
     value wear it. `19H` survives the transform intact. */
  const spanTight = spanKnown ? `${spanHours}h` : '';
  const extent = (v: readonly number[]): string =>
    v.length === 0 ? NO_VALUE : `${num(Math.min(...v))}–${num(Math.max(...v))}`;

  /* Two traces, not three. A third series would cost each of them a third of
     its height, and the obvious candidate — robots reporting per hour — sits
     at 25–27 of 28 all night: a flat line whose only reading is already on the
     `Telemetry` card and on this panel's own header. Two rates that actually
     move get 78px each and three legible y-ticks. */
  /* The commands trace draws only while its OWN source is answering. Anything
     else — never landed, refresh throwing, still in flight — is a gap: the
     bucketed array is zero-filled, so a dead ledger produces a run of zeros
     that is indistinguishable from a quiet night. `stale` gaps too, and that
     is deliberate: the ledger we hold is real but its TAIL is not, and every
     hour that passes adds one more genuine-looking zero to the right edge —
     a cliff drawn by our own clock. */
  const gapOf = (f: Feed): string | undefined => (f.ok
    ? undefined
    : f.down ? 'feed unavailable'
      : f.stale ? 'feed not refreshing'
        : 'awaiting first read');
  const issuedGap = gapOf(commandsFeed);
  /* THE SAME GUARD ON THE OTHER SERIES, and for the same reason. `readings` is
     a zero-filled array too, so a telemetry fan-out that answered nothing used
     to be indistinguishable from a fleet that ingested nothing — and the panel
     dodged that by blanking its whole body, which threw away a commands series
     that was answering perfectly well. One rule now: a strip whose own source
     is not answering is a gap, the other strip keeps drawing, and the stack's
     shared x-scale does not move. */
  /** Is the telemetry SERIES itself drawable? Distinct from whether the window
      is — the ledger can supply an honest x-axis for a panel whose ingest
      series is a hole, and every figure derived from telemetry has to be gated
      on this rather than on the ruler. */
  const readingsGap = telemetryFeed.ok && history.reporting > 0
    ? undefined
    : gapOf(telemetryFeed) ?? 'no robot reported';
  const telemetrySeries = readingsGap === undefined;
  const traces = useMemo<Trace[]>(() => [
    {
      key: 'ingest',
      eyebrow: 'Telemetry',
      unit: 'readings/h',
      values: readingsGap === undefined ? history.readings : [],
      digits: 0,
      /* No floor pinned to zero, unlike the rate below it: BAR §8 tell 6 asks
         a y-axis to start where the DATA starts, and 625–675 readings/h drawn
         against a zero baseline is a flat line that hides the only movement
         the series has. */
      extent: readingsGap === undefined ? extent(history.readings) : NO_VALUE,
      gap: readingsGap,
      gapTone: telemetryFeed.down || telemetryFeed.stale ? 'error' : 'wait',
    },
    {
      key: 'issued',
      eyebrow: 'Commands',
      unit: 'issued/h',
      values: issuedGap === undefined ? history.issued : [],
      digits: 0,
      min: 0,
      max: countMax(history.issued),
      extent: issuedGap === undefined ? extent(history.issued) : NO_VALUE,
      gap: issuedGap,
      gapTone: commandsFeed.down || commandsFeed.stale ? 'error' : 'wait',
    },
  ], [history, issuedGap, readingsGap, commandsFeed.down, commandsFeed.stale,
    telemetryFeed.down, telemetryFeed.stale]);

  /* Coverage at the last complete hour: how many robots the ingest total is
     actually made of. It is the one number that says whether a dip in the
     trace above is fewer readings or fewer robots. */
  const lastReporters = history.reporters[history.reporters.length - 1] ?? 0;

  /* Matrix column order. `id` is the roster; `severity` puts the broken
     texture at the left edge; `site` groups the columns the way the yard is
     laid out. All three are the same 168 readings. */
  const matrixItems = useMemo(() => {
    const list = [...fleet];
    if (order === 'severity') {
      list.sort((a, b) => {
        const d = (TONE_RANK[worstBy.get(b.robot_id) ?? 'nominal'] ?? 0)
          - (TONE_RANK[worstBy.get(a.robot_id) ?? 'nominal'] ?? 0);
        return d !== 0 ? d : a.robot_id.localeCompare(b.robot_id);
      });
    } else if (order === 'site') {
      list.sort((a, b) => siteOf(a).localeCompare(siteOf(b)) || a.robot_id.localeCompare(b.robot_id));
    } else {
      list.sort((a, b) => a.robot_id.localeCompare(b.robot_id));
    }
    return list;
  }, [fleet, order, worstBy]);

  /* What the current ordering GROUPS the columns by — the mark `Site` used to
     re-order without making. `Id` sorts on the robot id and our ids are
     `<morphology>-NN`, so its runs ARE the morphologies; `Worst` sorts on the
     worst reading, so its runs are the matrix's own five tones, named in the
     words the key beside them uses. Every ordering therefore says what it did. */
  const colKey = useMemo<(r: Robot) => string>(() => {
    if (order === 'site') return siteOf;
    if (order === 'severity') {
      return (r) => MIX_LABEL[worstBy.get(r.robot_id) ?? 'nominal'] ?? 'nominal';
    }
    return (r) => r.morphology;
  }, [order, worstBy]);
  const groupBy = order === 'site' ? 'site' : order === 'severity' ? 'reading' : 'morphology';

  /* Six tallies over the roster in hand — one per row of the band. Re-derived
     on the clock rather than on the poll because two of them, SEEN and CERT,
     are functions of `now`. */
  const census = useMemo(() => buildCensus(fleet, now), [fleet, now]);

  /* The two threshold counts the charge-profile panel used to exist for. They
     are the fleet's two charge ALERTS — how many robots are inside the reclaim
     watermark and how many are under the redline — so they belong on the
     band's caption line, beside the reading count, not in 371px of panel. */
  /* The same readings the BATT row counts, binned — the band's surplus width
     spends itself on the distribution rather than on nothing. Re-derived on
     the roster only: `now` cannot move a charge reading. */
  const chargeProfile = useMemo(() => buildCharge(fleet), [fleet]);
  const charge = useMemo(() => {
    const known = fleet.map((r) => r.battery_pct).filter((v): v is number => v != null);
    return {
      warn: known.filter((v) => v <= BATTERY_WARN).length,
      bad: known.filter((v) => v <= BATTERY_BAD).length,
    };
  }, [fleet]);

  const siteGroups = useMemo(
    () => buildGroups(fleet, worstBy, null, siteOf),
    [fleet, worstBy],
  );
  const morphGroups = useMemo(
    () => buildGroups(fleet, worstBy, MORPHOLOGIES, (r) => r.morphology),
    [fleet, worstBy],
  );
  const sites = useMemo(() => buildSites(siteGroups, worstBy), [siteGroups, worstBy]);
  /* The surface's cursor, reaching a FIFTH panel. Point at a column in the
     band — or at a dot in a site plot — and the two rows that robot is counted
     in mark themselves in the composition: its site, and its morphology.
     "Which group is this one in" is then read rather than remembered, and the
     Formant group card's whole job is answered without opening anything. */
  const cursorGroups = useMemo(() => {
    if (cursor === null) return null;
    const r = fleet.find((x) => x.robot_id === cursor);
    /* Four keys, not two: the estate's rows are grouped by build and by board
       revision, so pointing at a column in the band marks the site, the
       morphology, the agent build AND the hardware that column is on. One
       cursor, every grouping on the surface answering it at once. */
    return r === undefined
      ? null
      : new Set([siteOf(r), r.morphology, r.firmware ?? NO_VALUE, r.model]);
  }, [cursor, fleet]);
  /* Resolved from the live roster every render, so the fix on screen is the
     one the last poll returned rather than the one the cursor landed on. */
  const siteRead = useMemo(
    () => (cursor === null
      ? null
      : fleet.find((r) => r.robot_id === cursor && r.lat != null && r.lon != null) ?? null),
    [fleet, cursor],
  );
  const fixes = sites.reduce((a, s) => a + s.dots.length, 0);

  /* The capture pipeline, tallied off the fan-out. Derived on the roster's
     scans alone — `now` moves the ages printed beside it, not the tally. */
  const scanList = scans.data?.value;
  const capture = useMemo(() => buildCapture(scanList ?? []), [scanList]);
  /* The capture panel's own box, on the same policy every other caption here
     keeps: the clause that is REDUNDANT sheds first. `newest` is the one the
     panel's freshness chip already implies, so it goes before the backlog. */
  const [capRef, capWidth] = useBoxWidth();
  /* The site column's own box. The readout that replaces the `fixed` fraction
     the moment a dot is pointed at is the longest string any header on this
     surface prints, and the `Panel` primitive gives its meta the room before
     its title — so at the rail's real width the panel used to answer a hover
     by ellipsing its own name to `Sit…`. Identity is not the thing that
     sheds. Measured here so the readout can drop its trailing clause instead. */
  const [siteRef, siteWidth] = useBoxWidth();
  const peakGroup = Math.max(
    1,
    ...siteGroups.map((g) => g.robots.length),
    ...morphGroups.map((g) => g.robots.length),
  );

  /* Mode census off the ROSTER, not off `/api/stats.status_counts`.
     They are the same tally — verified against the fixture — but taking it
     here buys two things the aggregate cannot. A fraction whose numerator and
     denominator come from two endpoints polled at two instants can disagree
     with itself (`Faulted` used to read `2` from stats over `28` from the
     roster), and a census we can count from data already in hand should not
     blank because a summary endpoint died. `/api/stats` keeps the two figures
     only it can answer: the active count against its own total, and the fleet
     mean charge. */
  const statusCounts = useMemo(() => {
    const m = new Map<RobotStatus, number>();
    for (const r of fleet) m.set(r.status, (m.get(r.status) ?? 0) + 1);
    return m;
  }, [fleet]);
  const livenessCounts = useMemo(() => {
    const m = new Map<Liveness, number>();
    for (const r of fleet) m.set(r.liveness, (m.get(r.liveness) ?? 0) + 1);
    return m;
  }, [fleet]);
  const builds = useMemo(
    () => buildGroups(fleet, worstBy, null, (r) => r.firmware ?? NO_VALUE)
      .sort((a, b) => b.robots.length - a.robots.length || b.key.localeCompare(a.key)),
    [fleet, worstBy],
  );
  /* Hardware revision, off `model`. Not the same fact as morphology and not
     on this surface anywhere else: a `rover` fleet can be running two board
     revisions at once, and the revision is what pins the version manifest a
     robot's containers come from. The Groups panel answers "which group", the
     matrix answers "which robot"; this answers "what is the fleet made of". */
  const models = useMemo(
    () => buildGroups(fleet, worstBy, null, (r) => r.model)
      .sort((a, b) => b.robots.length - a.robots.length || a.key.localeCompare(b.key)),
    [fleet, worstBy],
  );
  const reachable = (livenessCounts.get('live') ?? 0) + (livenessCounts.get('backfilling') ?? 0);
  const faulted = statusCounts.get('error') ?? 0;

  /* ---- one source of truth for the strip -----------------------------
     The strip used to read `Active` and `Avg battery` off `/api/stats` while
     every other figure beside them came off `/api/robots`. Two sources, no
     attribution, and the failure mode is not hypothetical: with the roster
     answering an empty fleet and the aggregate still serving its last count,
     the strip printed `ACTIVE 12/28` and `55.4% fleet mean, live` directly
     under a subtitle reading `0 robots · 0 sites`. A KPI strip that
     contradicts the line above it is not a strip an operator can trust.

     So every figure on the strip is now the ROSTER's, and `/api/stats` is
     what it should always have been: a second opinion. It fills in only while
     the roster is unknown (marked, by the feed guard, as the strip's source),
     and when both answer and disagree the disagreement is itself reported —
     an aggregate lagging its own roster is a real fact about the platform,
     and it is the only thing on this surface that can see it. */
  const rosterActive = statusCounts.get('active') ?? 0;
  /* A mean over nothing is not zero, and a mean over the robots that HAVE a
     reading is not a mean over the fleet — `jetrover-08` has never phoned home
     and has no charge at all. Averaging it in as 0 would paint a critical
     reading the fleet does not have; averaging it in as anything else would
     invent one. It is excluded, and the note says the figure is over what
     reported. */
  const chargeRead = useMemo(
    () => fleet.map((r) => r.battery_pct).filter((v): v is number => typeof v === 'number'),
    [fleet],
  );
  const meanCharge = known(robotsFeed)
    ? (chargeRead.length === 0
      ? null
      : Math.round((chargeRead.reduce((a, b) => a + b, 0) / chargeRead.length) * 10) / 10)
    : (stats === undefined || stats.total_robots === 0 ? null : stats.avg_battery_pct);
  /* Both answered, and they do not agree about how many robots there are. */
  const rosterSkew = known(robotsFeed) && stats !== undefined
    && stats.total_robots !== fleet.length
    ? stats.total_robots
    : null;

  /* The soonest X.509 expiry in the fleet, and whose. An identity that lapses
     takes a robot off the air with no fault of its own, and it is the one
     deadline nobody is watching — so it gets a slot rather than a column. */
  const nextCert = useMemo(() => {
    let best: { robot: Robot; days: number } | null = null;
    for (const r of fleet) {
      if (r.cert_not_after == null) continue;
      const days = (Date.parse(r.cert_not_after) - now) / 86_400_000;
      if (Number.isNaN(days)) continue;
      if (best === null || days < best.days) best = { robot: r, days };
    }
    return best;
  }, [fleet, now]);

  /* ---- the strip's own provenance ---------------------------------
     Every other region on this surface answers "is this now, and is this
     real" in its own header (§6). The strip — the eight figures read first
     and quoted out loud — answered neither: it carried no liveness mark and
     never named the source its figures came off, which is the one region
     where that matters most, because it is the only one drawn from more than
     one endpoint. `/api/robots` is the source of every figure on it; the
     aggregate stands in only while the roster is unknown, and then the tag
     says so by name. The MARK is taken from the worst of the three feeds the
     strip actually reads, so a dead command ledger under a live roster reads
     `Polling off` beside the `—` it put in the queue cell, rather than a
     green light over a blank. */
  /* The roster is the source until it is DOWN — not merely until it has
     answered. A first paint that names `/api/stats` because the roster is
     still in flight would attribute eight dashes to an endpoint that is not
     going to fill them. */
  const stripSource = robotsFeed.down && known(statsFeed) ? statsFeed : robotsFeed;
  const stripFeeds = [stripSource, commandsFeed, telemetryFeed];

  /* The strip's MARK is the worst of its three sources — a dead command
     ledger under a live roster has to read as a failure beside the `—` it put
     in the queue cell, not as a green light over a blank.

     Its FREEZE is not: it is the strip's own source's. Six of these eight
     figures are counted from the roster and two are not, so dimming the whole
     band because the 60-second scan fan-out went quiet would grey six live
     readings to report one stale one. The mark names which source stopped;
     the failing panel's own fault row spells its path in full. */
  const stripPick = worstOf(stripFeeds, now, held);
  const stripFeed = stripPick.feed;
  const stripMark = { ...stripPick.mark, frozen: markOf(stripSource, now, held).frozen };

  const queued = useMemo(() => ledger.filter((c) => OPEN_COMMANDS.has(c.status)), [ledger]);
  /* The part of the open queue no robot has acknowledged — pending + sent.
     It is exactly what `/api/commands/pending` returns and what the shell's
     `queued` badge counts, so printing it under the open total is what makes
     the two figures reconcile rather than contradict. */
  const unacked = useMemo(
    () => queued.filter((c) => c.status === 'pending' || c.status === 'sent').length,
    [queued],
  );
  /* Commands the fleet refused. Terminal, so they never appear in the queue
     depth, and buried in the feed the moment anything else happens. */
  const refused = useMemo(() => ledger.filter((c) => c.status === 'failed'), [ledger]);
  /* And the tally of WHY, biggest first. Fourteen refusals read as fourteen
     separate incidents; `6 PLAN_TIMEOUT` reads as one navigation-stack story
     across six robots at three sites, which is a different call to make. The
     codes are the agent's own — nothing here invents a category. */
  const refusalTally = useMemo(() => {
    const by = new Map<string, number>();
    for (const c of refused) {
      const r = refusal(c);
      const k = r === null ? NO_VALUE : r.code;
      by.set(k, (by.get(k) ?? 0) + 1);
    }
    return [...by.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([code, n]) => ({ code, n }));
  }, [refused]);
  const commandRows = useMemo(() => {
    const rows = cmdView === 'open' ? [...queued] : cmdView === 'failed' ? [...refused] : [...ledger];
    /* Oldest first for the backlog — the queue's own question is "what has
       been sitting there longest". Newest first for the other two, which are
       feeds. */
    rows.sort(cmdView === 'open'
      ? (a, b) => Date.parse(a.updated_at) - Date.parse(b.updated_at) || a.id - b.id
      : (a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at) || b.id - a.id);
    return rows;
  }, [ledger, queued, refused, cmdView]);

  /* The exception table's rows carry everything its cells need, so the column
     array can stay a module constant. See the note above ATTENTION_SETS. */
  const attentionRows = useMemo<AttentionRow[]>(
    () => attention.map((e) => ({
      robot: e.robot,
      reasons: e.reasons,
      site: siteOf(e.robot),
      bay: metaString(e.robot, 'bay'),
      build: e.robot.firmware ?? NO_VALUE,
      tone: worstTone(e),
      trace: batteryTrace(telemetryBy?.get(e.robot.robot_id)),
    })),
    [attention, telemetryBy],
  );
  const attentionCols = attentionColumns(fitWidth);

  /* How far a panel's values recede once they have stopped being current,
     and it is TWO depths because there are two facts:

       40%  the source THREW. §5's number for a stalled feed, and the panel
            carries a `--error` fault row saying which endpoint said what.
       55%  the values are real and merely old — the console is held, or the
            poll silently stopped. A lighter touch on purpose: an operator who
            presses `L` is holding the console in order to READ it.

     The header never dims at either depth, so the pair legible from across
     the room stays "this panel is frozen, and here is why". The class rides
     the `Panel` and the stylesheet dims its body's rows — the fault row is
     excluded, because greying a fault report is the one thing this must not
     do. `undefined` rather than `''` keeps the primitive's single-class path. */
  /* ---- the commit path, bound to this surface's two selections ------------

     Selection is pruned against the live data on every poll: a command that
     has completed, or a robot the roster has dropped, cannot stay in a set
     whose count is printed on a verb. A stale count on a button is the same
     defect as a stale reading in a cell. */
  const cmdById = useMemo(() => new Map(ledger.map((c) => [String(c.id), c])), [ledger]);
  const fleetIds = useMemo(() => new Set(fleet.map((r) => r.robot_id)), [fleet]);
  useEffect(() => {
    if (robotSel.size === 0 || !known(robotsFeed)) return;
    const kept = [...robotSel].filter((id) => fleetIds.has(id));
    if (kept.length !== robotSel.size) setRobotSel(new Set(kept));
  }, [robotSel, fleetIds, robotsFeed]);
  useEffect(() => {
    if (cmdSel.size === 0 || !known(commandsFeed)) return;
    const kept = [...cmdSel].filter((id) => cmdById.has(id));
    if (kept.length !== cmdSel.size) setCmdSel(new Set(kept));
  }, [cmdSel, cmdById, commandsFeed]);

  const selRobots = useMemo(
    () => fleet.filter((r) => robotSel.has(r.robot_id)),
    [fleet, robotSel],
  );
  const selMorphs = useMemo(
    () => [...new Set(selRobots.map((r) => r.morphology))].sort(),
    [selRobots],
  );
  /* Robots the command will sit in a queue for rather than reach. Not a
     blocker — a queued command is exactly what the platform is for — but it
     is the fact the operator has to know BEFORE pressing send, so it is on
     the staged line rather than in a toast afterwards. */
  const selUnreachable = useMemo(
    () => selRobots.filter((r) => r.liveness !== 'live' && r.liveness !== 'backfilling').length,
    [selRobots],
  );
  const selCommands = useMemo(
    () => [...cmdSel].map((id) => cmdById.get(id)).filter((c): c is Command => c !== undefined),
    [cmdSel, cmdById],
  );

  const clearRobots = useCallback(() => {
    setRobotSel(NO_SELECTION);
    robotAnchor.current = null;
    setStaged((v) => (v?.scope === 'robots' ? null : v));
    setReceipt((r) => (r?.scope === 'robots' ? null : r));
  }, []);
  const clearCommands = useCallback(() => {
    setCmdSel(NO_SELECTION);
    cmdAnchor.current = null;
    setStaged((v) => (v?.scope === 'commands' ? null : v));
    setReceipt((r) => (r?.scope === 'commands' ? null : r));
  }, []);
  /* Esc discards a staged action, wherever the focus is. A commit that has
     been framed and not sent has to be abandonable without hunting for the
     button that abandons it (§6: every dialog closes on Esc — and a staged
     destructive verb is the closest thing this surface has to one). */
  useEffect(() => {
    if (staged === null) return undefined;
    const on = (e: Event) => {
      if ((e as globalThis.KeyboardEvent).key === 'Escape') setStaged(null);
    };
    document.addEventListener('keydown', on);
    return () => { document.removeEventListener('keydown', on); };
  }, [staged]);

  /* A staged action whose subject has gone unreadable is not stageable. The
     ledger's panel draws nothing at all while its source is down, so a
     `PATCH` framed against rows that are no longer on screen would be a
     commit against a list the operator can no longer check. */
  useEffect(() => {
    if (staged?.scope === 'commands' && commandsFeed.down) setStaged(null);
    if (staged?.scope === 'robots' && robotsFeed.down) setStaged(null);
  }, [staged, commandsFeed.down, robotsFeed.down]);

  /* Nothing is optimistic. The bar reports what the API said and the panel
     re-reads its own source; the row's `State` column is the durable record
     of `sent → acked`, and it changes when the ledger says so. */
  const commit = useCallback(async () => {
    if (staged === null || sending) return;
    setSending(true);
    const r = await staged.run();
    setSending(false);
    setStaged(null);
    setReceipt(r);
    void commands.refetch();
  }, [staged, sending, commands]);

  const stageRobot = useCallback((key: string) => {
    const v = ROBOT_VERBS.find((x) => x.key === key);
    if (v === undefined || selRobots.length === 0) return;
    const chk = profileCheck(profilesQ.data, selMorphs, v.command);
    const targets = selRobots.map((r) => r.robot_id);
    setReceipt(null);
    setStaged({
      scope: 'robots',
      verb: v.command,
      label: v.label,
      danger: false,
      n: targets.length,
      says: [
        v.command,
        chk.hazard ?? 'hazard —',
        chk.version === null ? 'profile —' : `profile ${chk.version}`,
        `${targets.length} ${targets.length === 1 ? 'robot' : 'robots'}`,
        selUnreachable > 0 ? `${selUnreachable} unreachable · queues` : null,
      ].filter((x): x is string => x !== null).join('  ·  '),
      run: () => dispatch(
        'robots',
        v.said,
        targets.map((id) => () => leg(
          `/api/robots/${encodeURIComponent(id)}/commands`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ command: v.command, params: {} }),
          },
        )),
      ),
    });
  }, [selRobots, selMorphs, selUnreachable, profilesQ.data]);

  const stageCommand = useCallback((key: string) => {
    const v = CMD_VERBS.find((x) => x.key === key);
    if (v === undefined) return;
    const hits = selCommands.filter((c) => v.applies.has(c.status));
    if (hits.length === 0) return;
    setReceipt(null);
    const skipped = selCommands.length - hits.length;
    const tail = skipped === 0 ? null : `${skipped} not ${v.needs}`;
    if (v.key === 'retry') {
      const robots = new Set(hits.map((c) => c.robot_id));
      setStaged({
        scope: 'commands',
        verb: 'retry',
        label: v.label,
        danger: false,
        n: hits.length,
        says: [
          're-issue',
          `${hits.length} ${hits.length === 1 ? 'command' : 'commands'}`,
          `${robots.size} ${robots.size === 1 ? 'robot' : 'robots'}`,
          tail,
        ].filter((x): x is string => x !== null).join('  ·  '),
        run: () => dispatch(
          'commands',
          'reissued',
          hits.map((c) => () => leg(
            `/api/robots/${encodeURIComponent(c.robot_id)}/commands`,
            {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ command: c.command, params: c.params }),
            },
          )),
        ),
      });
      return;
    }
    const status: CommandStatus = v.key === 'ack' ? 'acked' : 'cancelled';
    setStaged({
      scope: 'commands',
      verb: v.key === 'ack' ? 'acknowledge' : 'cancel',
      label: v.label,
      danger: v.danger === true,
      n: hits.length,
      says: [
        `PATCH status=${status}`,
        `${hits.length} of ${selCommands.length}`,
        tail,
      ].filter((x): x is string => x !== null).join('  ·  '),
      run: () => dispatch(
        'commands',
        v.key === 'ack' ? 'acknowledged' : 'cancelled',
        hits.map((c) => () => leg(
          `/api/commands/${c.id}`,
          {
            method: 'PATCH',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ status }),
          },
        )),
      ),
    });
  }, [selCommands]);

  /* A verb's count is the part of the selection it would actually touch, and
     its reason to be off is the platform's, not ours. */
  const robotVerbs = useMemo<BarVerb[]>(() => ROBOT_VERBS.map((v) => {
    const chk = profileCheck(profilesQ.data, selMorphs, v.command);
    return {
      key: v.key,
      label: v.label,
      n: robotSel.size,
      why: chk.ok
        ? undefined
        : `\`${v.command}\` is not in the ${chk.missing.join(', ')} command profile`,
    };
  }), [profilesQ.data, selMorphs, robotSel.size]);

  const cmdVerbs = useMemo<BarVerb[]>(() => CMD_VERBS.map((v) => {
    const n = selCommands.filter((c) => v.applies.has(c.status)).length;
    return {
      key: v.key,
      label: v.label,
      n,
      danger: v.danger,
      why: n === 0 ? `Nothing selected is ${v.needs}` : undefined,
    };
  }), [selCommands]);

  const robotBar = (count: number | null) => (
    <ActionBar
      subject={robotSel.size === 1 ? 'robot' : 'robots'}
      count={count}
      verbs={robotVerbs}
      staged={staged?.scope === 'robots' ? staged : null}
      receipt={staged === null && receipt !== null && receipt.scope === 'robots' ? receipt : null}
      sending={sending}
      onStage={stageRobot}
      onDiscard={() => setStaged(null)}
      onCommit={() => { void commit(); }}
      onClear={clearRobots}
    />
  );

  /* Range selection, on the row order the table is currently showing. */
  const attentionKeys = useMemo(() => attentionRows.map((r) => r.robot.robot_id), [attentionRows]);
  const commandKeys = useMemo(() => commandRows.map((c) => String(c.id)), [commandRows]);
  const pickRobots = useCallback((next: Set<string>) => {
    setRobotSel((prev) => rangeSelect(attentionKeys, prev, next, shift.current, robotAnchor));
    setStaged(null);
    setReceipt(null);
  }, [attentionKeys, shift]);
  const pickCommands = useCallback((next: Set<string>) => {
    setCmdSel((prev) => rangeSelect(commandKeys, prev, next, shift.current, cmdAnchor));
    setStaged(null);
    setReceipt(null);
  }, [commandKeys, shift]);
  /* A whole run, from the group strip's own label. `fault 4` in the `Worst`
     ordering is the fleet's fault set; one press puts it in the bar. Pressing
     it again with the run already picked takes it back out, so the label is a
     toggle rather than a one-way accumulator. */
  const toggleRun = useCallback((ids: readonly string[]) => {
    setRobotSel((prev) => {
      const next = new Set(prev);
      if (ids.every((id) => prev.has(id))) for (const id of ids) next.delete(id);
      else for (const id of ids) next.add(id);
      return next;
    });
    robotAnchor.current = ids[ids.length - 1] ?? null;
    setStaged(null);
    setReceipt(null);
  }, []);
  /* The matrix hands the event straight through, so its range needs no latch
     — but it shares the anchor with the table, because they share the set. */
  const toggleColumn = useCallback((robotId: string, withShift: boolean) => {
    setRobotSel((prev) => {
      const next = new Set(prev);
      if (next.has(robotId)) next.delete(robotId); else next.add(robotId);
      const order = matrixItems.map((r) => r.robot_id);
      return rangeSelect(order, prev, next, withShift, robotAnchor);
    });
    setStaged(null);
    setReceipt(null);
  }, [matrixItems]);


  const dim = (m: Mark): string | undefined => (
    m.state === 'stale' ? styles.dead : m.frozen ? styles.chill : undefined
  );

  return (
    /* ---- the surface, WITHOUT a page-head band ----------------------------
       This view draws no identity row of its own. The shell's 36px surface
       strip above it already says `Fleet › Operations 13/28 | Robot list 28 |
       Map 27/28 ┆ 28 hardware · 4 morphologies · 5 builds · 3 sites`, and the
       40px header this frame used to render under it re-stated the same four
       counts in a second vocabulary (`28 robots · 3 sites · 4 morphologies ·
       5 agent builds`), printed `Operations` twice and `Fleet` three times,
       and asked the operator to reconcile `28 hardware` with `28 robots`.
       One identity line, one vocabulary, one place to look.

       The two verbs it carried went with it, and neither is lost:
         · `All robots →` is the strip's `Robot list 28` TAB. A one-way link
           to a sibling surface is what a tab is; drawing both is drawing the
           same affordance twice, and the shell already declares this surface
           `hoist: 'none'` for exactly that reason.
         · `Retry N` broke up into eight per-panel verbs, each on the fault
           row of the panel whose source is the one that stopped — the verb
           sits ON its subject instead of 1300px away at the other bezel. On a
           healthy fleet none of them is drawn, and the ~40px the header held
           went to the fleet-status band.

       And there is no page-level OUTAGE band either, for the same reason
       twice: it restated what the panels' own marks and bodies already say,
       and it took a grid row, so a transient 503 moved every panel on the
       surface down 27px and back. See `PanelFault`.

       The element itself is a `display: contents` carrier for this surface's
       cursor tokens. The ACCENT is the console's amber, unchanged: this view
       marks what it is pointing at without a hue, so it never needs a second
       one. */
    <section className={styles.view}>
      <div className={styles.pageBody}>
      <div className={styles.stack} style={{ ['--band-h' as string]: `${bandH}px` }}>
        {/* Three sources, eight figures, one band. `data-frozen` recedes the
            six the ROSTER feeds; the two fed by the fan-outs recede on their
            own, so a dead ledger greys `Open commands` and leaves `In error`
            and `Avg battery` — which are still current — at full strength. */}
        <div
          className={styles.stripCell}
          ref={stripRef}
          data-frozen={stripMark.frozen ? '' : undefined}
          data-open-frozen={commandsMark.frozen ? '' : undefined}
          data-telemetry-frozen={telemetryMark.frozen ? '' : undefined}
        >
          <MetricStrip>
            {/* Every figure on this strip is guarded by the feed it came off.
                A KPI with no source prints `—`; its NOTE prints `—` too where
                the note is itself a measurement, and keeps its words where the
                note is a definition of the metric. */}
            {/* Note vocabulary, and it is one rule applied eight times: a note
                that DEFINES the metric ("executing a task", "refusing motion")
                is true whether or not the endpoint answered, so it stands; a
                note that MEASURES something ("6 not yet sent", "wukong-02",
                "27/28 reporting") is a figure like any other and blanks to the
                same `—` the value above it uses. Naming the failed endpoint
                here instead — which is what two of these cards used to do — put
                three different vocabularies in one strip and repeated, for
                every source, a fact the strip's own trust cell states once. */}
            <MetricCard
              eyebrow="Active"
              value={known(robotsFeed) ? num(rosterActive) : fmt(stats?.active_robots, statsFeed)}
              of={known(robotsFeed) ? num(fleet.length) : fmt(stats?.total_robots, statsFeed)}
              note="executing a task"
            />
            <MetricCard
              eyebrow="Reachable"
              value={fmt(known(robotsFeed) ? reachable : null, robotsFeed)}
              of={fmt(known(robotsFeed) ? fleet.length : null, robotsFeed)}
              note="live or backfilling"
            />
            {/* `In error`, not `Faulted`. `status === 'error'` is what this
                counts, `Error` is what the census under the MODE ribbon calls
                it, and `fault` is already taken: it is the name of the matrix's
                RED TONE — the shell's own strip reads `4 fault` twenty pixels
                above this cell, because four robots have a red reading of some
                kind and only two of them are refusing motion. Two numbers, two
                real facts; they may not share a word. */}
            <MetricCard
              eyebrow="In error"
              value={fmt(known(robotsFeed) ? faulted : null, robotsFeed)}
              of={fmt(known(robotsFeed) ? fleet.length : null, robotsFeed)}
              note="refusing motion"
              /* No reading, no threshold: an unreachable roster must not paint
                 a healthy zero any more than it may print one. */
              reading={known(robotsFeed) ? faulted : null}
              warn={1}
              bad={1}
              invert
            />
            <MetricCard
              eyebrow="Needs attention"
              value={fmt(known(robotsFeed) ? attention.length : null, robotsFeed)}
              of={fmt(known(robotsFeed) ? fleet.length : null, robotsFeed)}
              note="readings out of limits"
              /* Amber, never red: a robot on 36% charge needs a human this
                 shift, not this minute. Red belongs to `Faulted` beside it, and
                 to nothing else on this strip. */
              reading={known(robotsFeed) ? attention.length : null}
              warn={1}
              bad={Math.max(2, fleet.length)}
              invert
            />
            <MetricCard
              eyebrow="Avg battery"
              value={fmt(meanCharge, known(robotsFeed) ? robotsFeed : statsFeed)}
              unit="%"
              /* A definition, not a claim: it is the mean of the charges the
                 fleet actually reported, which is true whether or not the
                 source is refreshing. The old note said `fleet mean, live`,
                 and both halves were wrong — the figure excluded robots with
                 no reading, and `live` re-asserted a currency the panel chrome
                 had already withdrawn. How many reported is on the charge
                 profile below, beside the distribution it shapes. */
              note="mean of reported charge"
              reading={known(robotsFeed) || known(statsFeed) ? meanCharge : null}
              warn={BATTERY_WARN}
              bad={BATTERY_BAD}
              /* The battery series is bucketed off the SAME telemetry the
                 trace above is, so it is gated on that series and not on the
                 window: a ruler the ledger can supply is not a reason to draw
                 a battery history nobody reported. */
              trend={telemetrySeries ? history.battery : undefined}
              trendTimes={history.times}
            />
            <MetricCard
              eyebrow="Next cert"
              value={!known(robotsFeed)
                ? blank(robotsFeed)
                : nextCert === null ? NO_VALUE : duration(nextCert.days * 86_400_000)}
              /* `no certificates on file` is a CLAIM about the fleet's X.509
                 estate, so it waits for the roster like every other claim. */
              note={!known(robotsFeed)
                ? blank(robotsFeed)
                : nextCert === null ? 'no certificates on file' : nextCert.robot.robot_id}
              reading={known(robotsFeed) ? nextCert?.days : null}
              warn={CERT_WARN_DAYS}
              bad={CERT_BAD_DAYS}
            />
            {/* `Open`, not `Queue`, and the note reconciles it.
                `/api/commands/pending` — which is what the shell's `16 queued`
                badge counts — returns pending + sent: the commands no robot
                has acknowledged. This cell counts everything non-terminal,
                pending + sent + acked, because a command the robot has taken
                and not finished is still work outstanding. Both are true and
                they are ten apart, so the strip printing `COMMAND QUEUE 26`
                under a header reading `16 queued` was one screen giving two
                answers to one word. The word is now different and the note
                prints the other figure, so the two reconcile on sight. */}
            <MetricCard
              eyebrow="Open commands"
              value={fmt(known(commandsFeed) ? queued.length : null, commandsFeed)}
              /* No sparkline here on purpose: the only hourly series the
                 ledger supports is commands ISSUED per hour, which is not the
                 depth of this queue, and a trace under a number is read as
                 that number's history. The rate has its own trace in the
                 history panel, named for what it is. */
              note={`${known(commandsFeed) ? unacked : blank(commandsFeed)} unacknowledged`}
            />
            <MetricCard
              eyebrow={telemetrySeries ? `Telemetry ${spanTight}` : 'Telemetry'}
              value={fmt(
                known(telemetryFeed) ? history.readings.reduce((a, b) => a + b, 0) : null,
                telemetryFeed,
              )}
              note={`${fraction(history.reporting, fleet.length, telemetryFeed, robotsFeed)} reporting`}
              trend={telemetrySeries ? history.readings : undefined}
              trendTimes={history.times}
            />
            {/* Not a ninth figure — the strip's own trust line, in the band's
                last cell where every panel below it keeps the same mark. */}
            <div className={styles.stripFeed}>
              {/* The path the figures came off. Dropped when the mark beside
                  it is already naming that same source as the thing that
                  failed — `/api/robots  ◌ robots Failed to fetch` prints one
                  endpoint twice in two spellings, in the cell whose whole job
                  is to be unambiguous about provenance. */}
              {(stripWidth === 0 || stripWidth >= 1300)
                && !(stripFeed.endpoint === stripSource.endpoint && stripMark.state !== 'live'
                  && stripMark.state !== 'wait') && (
                <span
                  className={styles.stripSrc}
                  title={known(robotsFeed)
                    ? 'Every figure on this strip is counted from the roster.'
                    : 'The roster is unknown, so the aggregate is standing in for the figures it can answer.'}
                >
                  {stripSource.endpoint}
                </span>
              )}
              {/* The aggregate's own headcount, printed ONLY where it
                  disagrees with the roster every figure above it was counted
                  from. Silence is the healthy state; a figure here means one
                  of the two sources is behind, and says which one this page
                  believes. It rides the strip's trust cell because that cell
                  is where the strip names its source — the disagreement is a
                  fact about the source, not a ninth reading. */}
              {rosterSkew !== null && (
                <b
                  className={styles.stripSkew}
                  title={`/api/stats reports ${rosterSkew} robots; /api/robots lists ${fleet.length}. Every figure on this page is the roster's.`}
                >
                  /api/stats {num(rosterSkew)}
                </b>
              )}
              <FeedMark feed={stripFeed} mark={stripMark} named />
            </div>
          </MetricStrip>
        </div>

        {/* ---- band 1: the fleet as texture, full width ----------------

            One panel spanning the whole surface — ~1440px at 1600 and ~1700 at
            1920, room for 75 and 99 columns at the 19px pitch. It costs the row
            its two neighbours, and that is the trade: 371px of charge profile
            drew 28 anonymous bars beside a BATT row that already draws the same
            28 readings, and the composition's rails now sit at the texture's own
            row pitch — the two state rails in the census beside the ink they
            count, the two estate rails in the panel below.

            The columns track is `minmax(0, max-content)`, not `max-content`. At
            28 robots the two are identical; 150 robots is 2 850px, and an
            unbounded track would push the census off the viewport instead of
            shedding it. Adding robots must widen the texture, never delete a
            panel (THE BAR §8, tell 11). */}
        <PanelGrid className={styles.bandWide}>
          <Panel
            title="Fleet status"
            className={dim(rosterMark)}
            /* The dimensions of the MATRIX, not of the roster. They are the
               same number for every fleet the band can paint whole — which is
               every fleet up to ~240 per bank at 1600 — and where they differ
               the header says so in the same breath the caption does. */
            count={bandShown !== null && bandShown < fleet.length && known(robotsFeed)
              ? `${FLEET_METRICS.length}×${bandShown} of ${fleet.length}`
              : `${FLEET_METRICS.length}×${countOf(robotsFeed, fleet.length)}`}
            meta={<FeedMark feed={robotsFeed} mark={rosterMark} />}
            /* The counted action bar lands in the band the ordering control
               already owns, ahead of it (§5;
               `foxglove-recordings-bulk-action-bar.png`). It costs the header
               no height and it does not take the ordering control away: the
               tint follows the robot id, not the column index, so re-sorting
               under a live selection re-draws the same picked robots
               somewhere else and is a legitimate thing to want to do. */
            tools={(
              <>
              {robotSel.size > 0 && (
                <span className={styles.headBar}>{robotBar(robotSel.size)}</span>
              )}
              <SegmentedControl
                label="Column order"
                value={order}
                onChange={setOrder}
                /* A control that cannot reorder anything says so. The CURRENT
                   segment stays enabled — it still reports which order the
                   matrix will come back in. */
                items={[
                  { value: 'id', label: 'Id', disabled: !known(robotsFeed) && order !== 'id' },
                  { value: 'severity', label: 'Worst', disabled: !known(robotsFeed) && order !== 'severity' },
                  { value: 'site', label: 'Site', disabled: !known(robotsFeed) && order !== 'site' },
                ]}
              />
              </>
            )}
            loading={robotsFeed.loading}
            scroll={false}
          >
            {/* The band's height is a function of the roster — 288px at 28
                robots, 188px at 150, taller again once it banks. The three
                states with NO roster have no such function, so they hold the
                floor instead of collapsing the page around them (§8, tell 3:
                an absent state must not be a different shape). */}
            <PanelFault feed={robotsFeed} mark={rosterMark} onRetry={retry} />
            {/* AN EMPTY MATRIX KEEPS ITS METRICS. The six row labels are the
                panel's chrome — they are true before the first read, after a
                failed one, and on an empty roster — so the instrument is drawn
                in all three and only the cells are ever absent. This used to be
                a bare 188px box. */}
            {robotsFeed.down || robotsFeed.loading || rosterEmpty ? (
              <div className={styles.bandFallback}>
                <StoppedContext.Provider
                  value={
                    rosterEmpty && !robotsFeed.down
                      ? { mode: 'empty', note: <Blank noun="robots" where="on the roster" /> }
                      : { mode: robotsFeed.down ? 'stopped' : 'reading' }
                  }
                >
                  <SkeletonMatrix labels={MATRIX_ROWS} columns={30} what="the fleet matrix" />
                </StoppedContext.Provider>
              </div>
            ) : (
              <FleetTexture
                items={matrixItems}
                groupKey={colKey}
                groupBy={groupBy}
                worstBy={worstBy}
                census={census}
                charge={chargeProfile}
                cursorId={cursor}
                onCursor={setCursor}
                onHeight={setBandH}
                onShown={setBandShown}
                selected={robotSel}
                onToggle={toggleColumn}
                onToggleRun={toggleRun}
                onPick={(r) => navigate('detail', { robotId: r.robot_id })}
                /* After the painted reading count: the fleet's two charge
                   ALERTS — the only two figures the killed charge-profile panel
                   carried that the BATT row beside them does not. */
                caption={
                  <>
                    <i className={styles.capSep}>·</i>
                    <span className={styles.capKey}>charge ≤{BATTERY_WARN}</span>
                    <span
                      className={styles.capVal}
                      data-tone={charge.warn > 0 ? 'warn' : 'nominal'}
                    >
                      {charge.warn}
                    </span>
                    <span className={styles.capKey}>≤{BATTERY_BAD}</span>
                    <span
                      className={styles.capVal}
                      data-tone={charge.bad > 0 ? 'error' : 'nominal'}
                    >
                      {charge.bad}
                    </span>
                    <i className={styles.capSep}>·</i>
                    {/* The band's own affordance line. Two verbs, because a
                        column now does two things and neither is guessable
                        from a grid of 6px cells. */}
                    <span className={styles.capHint}>
                      click selects · ⏎ or double-click opens
                    </span>
                  </>
                }
              />
            )}
          </Panel>
        </PanelGrid>

        {/* ---- band 2: groups and trend -------------------------------- */}
        <PanelGrid className={styles.bandTrend}>
          <Panel
            title="Groups"
            className={dim(rosterMark)}
            count={countOf(robotsFeed, siteGroups.length + morphGroups.length)}
            meta={
              <>
                {/* Only true while the census bar is on screen; it sheds with
                    the bar rather than describing a column that is not there. */}
                {!robotsFeed.down && !rosterEmpty && (groupCols === 'full' || groupCols === 'mid') && (
                  <span>bar ∝ headcount</span>
                )}
                <FeedMark feed={robotsFeed} mark={rosterMark} />
              </>
            }
            loading={robotsFeed.loading}
            pad="none"
          >
            <PanelFault feed={robotsFeed} mark={rosterMark} onRetry={retry} />
            <div className={styles.fit} ref={groupsRef}>
            {/* The register's columns are chrome: true before the roster
                lands, still true when the read fails. Drawing them in every
                state is what stops this panel changing shape on a 503. */}
            {robotsFeed.down || robotsFeed.loading || rosterEmpty ? (
              <StoppedContext.Provider
                value={
                  rosterEmpty && !robotsFeed.down
                    ? { mode: 'empty', note: <Blank noun="robots" where="on the roster" /> }
                    : { mode: robotsFeed.down ? 'stopped' : 'reading' }
                }
              >
                <SkeletonTable columns={groupColumns(groupCols)} rows={9} edge what="groups" />
              </StoppedContext.Provider>
            ) : (
              <div
                className={styles.groups}
                data-cols={groupCols}
                role="grid"
                aria-label="Fleet groups. Enter picks a group into the action bar."
              >
                <div className={styles.gHead} role="row">
                  <span className={styles.gTick} aria-hidden="true" />
                  <span role="columnheader">Group</span>
                  <span role="columnheader" className={styles.gNum}>Robots</span>
                  <span role="columnheader" className={styles.gNum}>Link</span>
                  <span role="columnheader" className={styles.gNum}>Attn</span>
                  <span role="columnheader" className={styles.gMixHead}>Census</span>
                  <span role="columnheader" className={styles.gNum}>Batt</span>
                  <span role="columnheader" className={`${styles.gNum} ${styles.gCert}`}>Cert</span>
                  <span role="columnheader" className={styles.gFw}>Builds</span>
                </div>
                <GroupSection
                  label="Site"
                  groups={siteGroups}
                  peak={peakGroup}
                  first
                  current={cursorGroups}
                  selected={robotSel}
                  onPick={toggleRun}
                />
                <GroupSection
                  label="Morphology"
                  groups={morphGroups}
                  peak={peakGroup}
                  first={false}
                  current={cursorGroups}
                  selected={robotSel}
                  onPick={toggleRun}
                />
              </div>
            )}
            </div>
          </Panel>

          {/* The estate. What the composition panel keeps once the census
              beside the texture has taken mode and link: which AGENT BUILD the
              fleet is actually on — the fact that decides whether a rollout is
              done — and which HARDWARE revision each robot is, which is what
              pins the version manifest its containers come from. Neither has a
              fixed vocabulary (the builds ARE the data), so with no roster
              there is nothing honest to draw and the panel says so in one
              line rather than listing zero rows under a live header. */}
          <Panel
            title="Estate"
            className={dim(rosterMark)}
            /* No count on the title line: `(9)` over a panel whose two rails
               already carry `5` and `4` is a third number to reconcile. */
            meta={
              <>
                {/* THE DENOMINATOR, STATED. Every bar in this panel is drawn
                    against the whole roster, so the grey track IS the fleet and
                    a bar's length is that build's share of it — which is the
                    question the panel exists to answer ("is the rollout done").
                    A rail normalised to its own peak cannot say that, and two
                    rails each normalised to their own peak cannot even be
                    compared with each other. Printed rather than implied,
                    because a proportional bar with an unstated denominator is
                    a picture, not a reading. */}
                {/* 214px is the rail's content width at the panel width where
                    `Estate` + this clause + the liveness mark stop fitting on
                    one line; below it the RULE sheds and the bars stay, because
                    what the panel is for survives losing its footnote and does
                    not survive losing its rows. */}
                {!robotsFeed.down && !rosterEmpty && railWidth >= 226 && (
                  <span>bar ∝ {fleet.length} robots</span>
                )}
                <FeedMark feed={robotsFeed} mark={rosterMark} />
              </>
            }
            loading={robotsFeed.loading}
            pad="none"
            scroll={false}
          >
            <PanelFault feed={robotsFeed} mark={rosterMark} onRetry={retry} />
            {robotsFeed.down || robotsFeed.loading || rosterEmpty ? (
              <StoppedContext.Provider
                value={
                  rosterEmpty && !robotsFeed.down
                    ? { mode: 'empty', note: <Blank noun="robots" where="on the roster" /> }
                    : { mode: robotsFeed.down ? 'stopped' : 'reading' }
                }
              >
                <SkeletonTable columns={ESTATE_COLUMNS} rows={10} what="builds" />
              </StoppedContext.Provider>
            ) : (
              /* ONE GRID FOR THE WHOLE PANEL. Both sections are rows of the
                 same three-column grid — label sized by the longest string in
                 EITHER section, one count column, one track — so `2.10.7 = 7`
                 and `f3-jetrover-v1 = 8` land on one x and can be read as one
                 column of figures. Two independent rails put the counts at two
                 x positions and the tracks at two widths, and the taller bar
                 was then the SMALLER number.

                 The bar itself is the group's census, not a plain length: the
                 same worst-first mix the Groups panel paints, against the same
                 kind of steel track, so an operator can see that of the eleven
                 robots on `3.0.0-rc2` three are outside a limit — a fact that a
                 hue on the whole row could only exaggerate (worst-of-eleven is
                 red the moment one of the eleven is) and that a bare count
                 cannot place. */
              <div
                className={styles.estate}
                ref={railRef}
                role="grid"
                aria-label={`Fleet estate, ${fleet.length} robots.`
                  + ' Enter picks a build or a revision into the action bar.'}
              >
                <EstateSection
                  label="Agent build"
                  noun="agent build"
                  groups={builds}
                  peak={fleet.length}
                  first
                  current={cursorGroups}
                  selected={robotSel}
                  onPick={toggleRun}
                />
                <EstateSection
                  label="Hardware"
                  noun="hardware revision"
                  groups={models}
                  peak={fleet.length}
                  first={false}
                  current={cursorGroups}
                  selected={robotSel}
                  onPick={toggleRun}
                />
              </div>
            )}
          </Panel>

          <Panel
            title="Fleet history"
            className={dim(telemetryMark)}
            /* The window length sheds before the panel's NAME does. It only
               ever has to: `Fleet history` + `(19 h)` + `2 min` fits the
               247px this panel gets at 1280, and the same header with the
               liveness mark carrying the instant it went stale
               (`Polling off 15:52:06Z`, ~70px wider) does not — and what the
               primitive shortens when it does not fit is the title. A panel
               that answers to `Fleet …` while a timestamp keeps its last
               glyph has its priorities backwards. The window is legible off
               the plot's own hour ticks; the panel's name is legible nowhere
               else. */
            count={histWidth > 0 && histWidth < 300 && !telemetryFeed.ok ? undefined : spanLabel}
            meta={
              <>
                {/* Partial truth is still truth: a fan-out where SOME legs
                    failed is not a quieter fleet, and the trace says so on
                    this line rather than pretending to a full census. */}
                {legsFailed > 0 && (
                  <span
                    className={styles.metaWarn}
                    title="Robots whose telemetry call failed this cycle. Their readings are missing from the trace, not zero."
                  >
                    {/* The denominator goes before the panel's name does: it
                        is the roster size, which the strip above already
                        prints twice. The numerator is the fact. */}
                    {histWidth === 0 || histWidth >= 360
                      ? `${legsFailed}/${telemetry.data?.asked ?? fleet.length} feeds failed`
                      : `${legsFailed} feeds failed`}
                  </span>
                )}
                {/* The coverage fraction sheds before the panel's own NAME
                    does. Its floor is 300px on a healthy fan-out and 480 once
                    the failure clause is on the line beside it, because the
                    failure clause is the more urgent of the two and the title
                    is more urgent than either. */}
                {/* +72px once the mark is a FAILURE rather than a cadence:
                    `● Polling 2m` is 90px and `◌ commands no reply` is 135,
                    and the `Panel` primitive gives its meta the room before
                    its title — so a threshold blind to the mark's own width
                    bought a coverage fraction by clipping the panel's name to
                    `Fleet h…`, in exactly the state where identity matters
                    most. The fraction sheds instead; it is on the strip
                    above, over the whole window rather than the last hour. */}
                {(histWidth === 0 || histWidth >= (legsFailed > 0 ? 480 : 300)
                  + (histPick.mark.state === 'live' || histPick.mark.state === 'wait' ? 0 : 72)) && (
                  /* `this hour`, not `reporting`: the strip above carries
                     `27/28 reporting` under a `TELEMETRY 19H` eyebrow, over
                     the WHOLE window, and two adjacent fractions that read as
                     the same sentence and disagree by two robots is a page
                     arguing with itself. One word makes the scope explicit,
                     at the same 15 characters the width policy below was
                     measured against. */
                  <span title="Robots that sent at least one reading in the last complete hour. The strip above counts the whole window.">
                    {fraction(lastReporters, fleet.length, telemetryFeed, robotsFeed)} this hour
                  </span>
                )}
                <FeedMark
                  feed={histPick.feed}
                  mark={histPick.mark}
                  named={histPick.feed !== telemetryFeed}
                />
              </>
            }
            loading={telemetryFeed.loading || telemetry.isFetching}
            pad="none"
            scroll={false}
          >
            {/* Only TELEMETRY reports here. The commands feed's failure is
                already stated by its own trace, in the strip that would have
                drawn it — a fault row above the stack would say it twice and
                cost the plots a row of height to do it. */}
            <PanelFault feed={telemetryFeed} mark={telemetryMark} onRetry={retry} />
            <div className={styles.fit} ref={histRef}>
            {/* The stack draws while ANY series can be drawn on an honest
                axis — `history.source` names which source the ruler came off.
                A dead telemetry fan-out used to blank the whole panel and take
                a perfectly live command series down with it; now the telemetry
                strip is a gap and the ledger keeps its trace, which is the
                same rule the commands strip has always been held to. */}
            {history.source !== 'nominal' ? (
              <HistoryStack
                times={history.times}
                traces={traces}
                height={76}
                /* Provenance, and the same width policy every other caption
                   on this surface keeps: the clause that is REDUNDANT with
                   what is already on screen goes first. The bucket width is
                   readable off the axis ticks under it; the sample cap is
                   readable nowhere else, so it is the half that survives. */
                foot={histWidth > 0 && histWidth < 400
                  ? `${history.capped} capped histories`
                  : `complete UTC hours · ${history.capped} capped histories`}
                hint={histWidth === 0 || histWidth >= 300 ? 'hover or ← →' : undefined}
                compact={histWidth > 0 && histWidth < 260}
              />
            ) : (
              /* The two strips' names and their axis are true before any
                 history lands and stay true when the fan-out fails, so the
                 panel keeps them and only the traces are ever absent. */
              <StoppedContext.Provider
                value={
                  telemetryFeed.down
                    ? { mode: 'stopped' }
                    : telemetryFeed.loading
                      ? { mode: 'reading' }
                      : {
                        mode: 'empty',
                        note: rosterEmpty
                          ? <Blank noun="robots" where="on the roster" />
                          : <Blank noun="readings" where="in the window" />,
                      }
                }
              >
                <SkeletonPlot lanes={HISTORY_LANES} ticks={4} what="fleet history" />
              </StoppedContext.Provider>
            )}
            </div>
          </Panel>
        </PanelGrid>

        {/* ---- band 3: what needs a human ------------------------------ */}
        <div className={styles.bandMain}>
          <Panel
            title="Needs attention"
            className={dim(rosterMark)}
            count={countOf(robotsFeed, attention.length)}
            meta={
              <>
                <span>worst first · same limits as the matrix</span>
                <FeedMark feed={robotsFeed} mark={rosterMark} />
              </>
            }
            loading={robotsFeed.loading}
            pad="none"
          >
            <PanelFault feed={robotsFeed} mark={rosterMark} onRetry={retry} />
            {/* The measured box. Its width comes from the page grid's
                `minmax(0, 1fr)` track, so nothing the table renders can widen
                it — which is what makes the column set above stable. */}
            <div className={styles.clipBox} ref={excRef}>
            <div
              className={`${styles.fit} ${styles.selTable}`}
              ref={fitRef}
              /* Whole rows or none — see `wholeRows`. The ≤25px this leaves
                 stays as the panel's ground under the last row. */
              style={excClip === undefined ? undefined : { maxHeight: `${excClip}px` }}
            >
              {robotsFeed.down || robotsFeed.loading ? (
                /* The exception register keeps its columns while the roster is
                   unread. "Every robot is inside its limits" is a claim about
                   28 machines nobody has spoken to, so the instrument stands
                   in for the rows and says nothing about them. */
                <StoppedContext.Provider value={{ mode: robotsFeed.down ? 'stopped' : 'reading' }}>
                  <SkeletonTable columns={ATTENTION_COLUMNS} rows={10} edge what="exceptions" />
                </StoppedContext.Provider>
              ) : (
              <DataTable
                rows={attentionRows}
                columns={attentionCols}
                rowKey={(r) => r.robot.robot_id}
                /* The triage surface commits. Checkbox at the leading edge,
                   shift-click for a run, and the 22px column header swaps for
                   the counted verbs — the same set the matrix's own header is
                   driving, because it is the same selection. */
                selectable
                selected={robotSel}
                onSelectedChange={pickRobots}
                actions={robotBar(robotSel.size)}
                /* The edge bar carries the WORST reading, not the mode. This
                   table is sorted worst-first and says so in its header; an
                   edge painted from `status` contradicted that on its own top
                   row — jetrover-06 is offline on 22% charge and 7h stale, and
                   its mode is `idle`, so the leading edge read nominal directly
                   above two amber rows. Now the edge and the sort are the same
                   fact, and the column of ticks is a severity gradient. */
                rowTone={(r) => r.tone}
                rowAlarm={(r) => r.robot.status === 'error'}
                /* The surface's cursor, reaching the fourth panel: point at a
                   column in the band, or at a dot in a site plot, and the row
                   that names the same robot marks itself here. It is the same
                   `data-current` a row gets when its detail page is open,
                   because it is the same fact — this is the robot the screen
                   is talking about. */
                currentKey={cursor}
                onRowClick={(r) => navigate('detail', { robotId: r.robot.robot_id })}
                /* "Every robot is inside its limits" is a CLAIM about the
                   fleet. It may only be made when the fleet answered — and
                   when it did not, the panel's own fault row four pixels
                   above has already said so, with the verb. The table stays
                   silent rather than printing the same sentence twice under
                   its own column header. */
                empty={robotsFeed.down
                  ? ''
                  : robotsFeed.loading
                    ? READING.robots
                    : rosterEmpty
                      ? 'No robots on the roster.'
                      : 'Every robot is inside its limits.'}
              />
              )}
            </div>
            </div>
          </Panel>
        </div>

        {/* ---- the rail: where they are, and what they are being told --- */}
        <div className={styles.rail}>
          <Panel
            title="Sites"
            className={dim(rosterMark)}
            /* The site count is on the strip above (`3 sites`) and in the
               three named plots below. While the header is carrying a raw fix
               it is the cheapest 30px on the line, and it goes first. */
            count={siteRead === null ? countOf(robotsFeed, siteGroups.length) : undefined}
            meta={
              <>
                {/* One readout line for the three plots, in the panel header:
                    point at a dot and the census is replaced by that robot's
                    id and its RAW fix — five decimals of degree, and the
                    heading it is reporting. THE BAR §5: click a feature, get
                    the raw values, not a rounded paraphrase. */}
                {siteRead === null ? (
                  <span title="Robots reporting a position fix. A robot that has never phoned home has no position and is not drawn.">
                    {fraction(fixes, fleet.length, robotsFeed)} fixed
                  </span>
                ) : (
                  <span
                    className={styles.siteRead}
                    title={'lat, lon in degrees and heading in degrees, exactly as the'
                      + ' robot reported them. The contract states no geodetic datum'
                      + ' and no true-vs-magnetic reference for the heading.'}
                  >
                    <span className={styles.siteReadId}>{siteRead.robot_id}</span>
                    {/* A bare trailing `28°` is a bearing against no stated
                        north, and this line is the one place the raw fix is
                        printed. The contract declares neither a datum nor a
                        magnetic correction, so the frame is named the only
                        honest way available: with the field the number came
                        off, in mono (§11.5 — the label is the topic, not a
                        paraphrase). Printing `°T` would be a claim the API has
                        not made. The pair keeps `lat`/`lon` in the dot's own
                        title and aria label, where there is room for both.

                        Abbreviated: this line lives in a 412px panel header
                        beside the panel's own name, and spelled out it was
                        16px over — which the flex row paid for by ellipsing
                        `Sites` down to `S..`, i.e. by deleting the panel's
                        identity to print a bearing. `hdg` is the field, still
                        in mono; the dot's title and aria label spell it. */}
                    {siteRead.lat!.toFixed(5)}, {siteRead.lon!.toFixed(5)}
                    {/* And the bearing sheds before the fix does, because the
                        fix is the thing that cannot be read anywhere else on
                        this surface — the heading is drawn, as the dot's own
                        tail, in the plot the pointer is already inside. Below
                        the measured floor the pair leaves and the panel keeps
                        its name. */}
                    {siteRead.heading_deg != null && (siteWidth === 0 || siteWidth >= 392) && (
                      <>
                        <i className={styles.siteReadKey}>hdg</i>
                        <i className={styles.siteReadHdg}>
                          {Math.round(siteRead.heading_deg)}°
                        </i>
                      </>
                    )}
                  </span>
                )}
                <FeedMark feed={robotsFeed} mark={rosterMark} />
              </>
            }
            loading={robotsFeed.loading}
            pad="tight"
            scroll={false}
          >
            <PanelFault feed={robotsFeed} mark={rosterMark} onRetry={retry} />
            {robotsFeed.down ? (
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonStrip cells={SITE_CELLS} what="the sites" />
              </StoppedContext.Provider>
            ) : robotsFeed.loading ? (
              <Empty>{READING.robots}</Empty>
            ) : sites.length === 0 ? (
              /* A roster that answered with no sites is an EMPTY line, not an
                 error one. The two states are told apart by colour and by
                 words, never by one standing in for the other. */
              <Empty>No sites on the roster.</Empty>
            ) : (
              <div className={styles.sites} ref={siteRef}>
                {sites.map((s) => (
                  <SitePlot
                    key={s.key}
                    site={s}
                    hoverId={cursor}
                    onHover={setCursor}
                    onPick={(r) => navigate('detail', { robotId: r.robot_id })}
                  />
                ))}
              </div>
            )}
          </Panel>

          {/* ---- what the fleet actually captured ----------------------

              The domain this overview was missing. These machines exist to
              drive a nursery block and come back with a session, so "did we
              get the data" sits beside "is the robot healthy", not two screens
              away — and a failed capture is a fault the fleet's own status
              rows cannot express: koko-04 can be nominal on all six metrics
              and still have lost `20260828T0845Z_perimeter`.

              Two independent axes, both on the contract and neither
              aggregated by any endpoint: `processing_state` (where the session
              is in the pipeline) and `sync_state` (whether its bytes ever
              reached the bucket). The stages are the rail; the transfer
              backlog is the caption under it, because it is a fact about
              BYTES and the rows above it count sessions. */}
          <Panel
            title="Capture"
            className={dim(scansMark)}
            count={countOf(scansFeed, capture.total)}
            meta={
              <>
                {/* The one figure worth colouring on this panel, and it is on
                    the header so it is answered without a click. Suppressed
                    with the feed: nobody may claim zero failures off a call
                    that did not come back. */}
                {known(scansFeed) && capture.total > 0 && capWidth >= 380 && (
                  <span>bar ∝ {capture.total} sessions</span>
                )}
                {known(scansFeed) && capture.failed > 0 && (
                  <span
                    className={styles.metaBad}
                    title={`${capture.failed} capture sessions whose COPC processing failed.`
                      + ' Their raw bytes may still be in the bucket; their derivative is not.'}
                  >
                    {capture.failed} failed
                  </span>
                )}
                <FeedMark feed={scansFeed} mark={scansMark} />
              </>
            }
            loading={scansFeed.loading || scans.isFetching}
            pad="none"
            scroll={false}
          >
            <PanelFault feed={scansFeed} mark={scansMark} onRetry={retry} />
            {scansFeed.down ? (
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonTable columns={CAPTURE_COLUMNS} rows={4} what="capture sessions" />
              </StoppedContext.Provider>
            ) : scansFeed.loading ? (
              <Empty>{READING.scans}</Empty>
            ) : rosterEmpty ? (
              <Empty>No robots on the roster.</Empty>
            ) : capture.total === 0 ? (
              <Empty>No capture sessions on the roster.</Empty>
            ) : (
              <div className={styles.capture} ref={capRef}>
                {/* Same grid, same rule as the Estate: one track, and it is
                    the WHOLE ledger — so `copc 38` draws half the track and
                    reads as "half of everything we have ever captured is
                    processed", which is the reading a rail normalised to its
                    own biggest stage destroys by drawing that same 38 full
                    width. Every proportional bar on this surface now answers
                    the same question in the same units. */}
                <div
                  className={styles.estate}
                  role="table"
                  aria-label={`Capture pipeline, ${capture.total} sessions`}
                >
                  {capture.stages.map((s) => (
                    <div
                      key={s.key}
                      className={styles.eRow}
                      role="row"
                      title={s.n === 0
                        ? `No sessions in ${scanState(s.key).label}`
                        : `${s.n} of ${capture.total} sessions in ${scanState(s.key).label}`
                          + (s.oldest === null ? '' : ` · oldest started ${utc(new Date(s.oldest).toISOString())}`)}
                    >
                      <span className={styles.eLabel} role="gridcell">
                        {scanState(s.key).label}
                      </span>
                      <span className={styles.eNum} role="gridcell">{s.n}</span>
                      <span className={styles.eTrack} role="gridcell">
                        <span
                          className={styles.eBar}
                          data-tone={matrixTone(s.tone)}
                          data-hollow={s.hollow === true}
                          style={{ width: `${(s.n / Math.max(1, capture.total)) * 100}%` }}
                        />
                      </span>
                    </div>
                  ))}
                </div>
                {/* The transfer backlog. `sync_state` is the axis that says
                    whether the bytes are anywhere but the robot, so a fleet
                    with a clean pipeline and 40 GB still on-vehicle reads
                    correctly here and nowhere else on this screen. */}
                <div className={styles.capFoot}>
                  <span
                    data-tone={capture.syncing > 0 ? 'warn' : 'neutral'}
                    title="Sessions whose sync_state is not `synced` — bytes that have not finished reaching the bucket."
                  >
                    {capture.syncing} syncing
                  </span>
                  {capture.syncing > 0 && (
                    <>
                      <i>·</i>
                      <span title="Total size_bytes of the sessions still syncing.">
                        {bytes(capture.syncingBytes)} in flight
                      </span>
                    </>
                  )}
                  {capture.newest !== null && (capWidth === 0 || capWidth >= 300) && (
                    <>
                      <i>·</i>
                      <span title={`Most recent session started ${utc(new Date(capture.newest).toISOString())}`}>
                        newest <b>{duration(Math.max(0, now - capture.newest))}</b> ago
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </Panel>

          <Panel
            title="Commands"
            className={dim(commandsMark)}
            /* No `count` on the title line, unlike every other panel here. In a
               424px rail this header carries a title, a liveness chip that
               grows by a Zulu stamp the moment polling stops, and a two-segment
               control — and `(110)` was the 33px that tipped it over, clipping
               the panel's own name to `Comman…` in exactly the state where
               identity matters most. The counts move onto the segments, where
               they are strictly better: both views' depths are legible at once
               rather than only the one you are looking at. */
            meta={<FeedMark feed={commandsFeed} mark={commandsMark} />}
            tools={
              <SegmentedControl
                label="Command view"
                value={cmdView}
                onChange={setCmdView}
                items={[
                  /* A depth nobody could read is not zero — every count sheds
                     with the feed rather than reporting an empty ledger. */
                  { value: 'all', label: 'All', count: known(commandsFeed) ? ledger.length : undefined },
                  { value: 'open', label: 'Open', count: known(commandsFeed) ? queued.length : undefined },
                  { value: 'failed', label: 'Failed', count: known(commandsFeed) ? refused.length : undefined },
                ]}
              />
            }
            loading={commandsFeed.loading || commands.isFetching}
            pad="none"
            /* The panel hands its whole body to one flex column: the list
               scrolls, the fault row and the refusal tally do not. */
            scroll={false}
          >
            <div className={styles.cmdStack}>
              <PanelFault feed={commandsFeed} mark={commandsMark} onRetry={retry} />
              {commandsFeed.down ? (
                <StoppedContext.Provider value={{ mode: 'stopped' }}>
                  <SkeletonTable columns={CMD_COLUMNS} rows={6} edge what="commands" />
                </StoppedContext.Provider>
              ) : (
                <div className={styles.cmdList} ref={cmdRef}>
                <div
                  className={`${styles.clipBox} ${styles.selTable}`}
                  style={cmdClip === undefined ? undefined : { maxHeight: `${cmdClip}px` }}
                >
                  <DataTable
                    rows={commandRows}
                    columns={cmdView === 'failed' ? REFUSAL_COLUMNS : COMMAND_COLUMNS}
                    rowKey={(c) => String(c.id)}
                    rowTone={(c) => COMMAND_TONE[c.status]}
                    /* The ledger's rows were readable and nothing else: a
                       Failed tab holding fourteen PLAN_TIMEOUTs that the
                       operator could only look at. Acknowledge and Cancel are
                       `PATCH /api/commands/{id}`; Retry re-issues the same
                       command with the same params to the same robot. Each
                       verb counts the part of the selection it applies to. */
                    selectable
                    selected={cmdSel}
                    onSelectedChange={pickCommands}
                    actions={(
                      <ActionBar
                        subject={cmdSel.size === 1 ? 'command' : 'commands'}
                        count={cmdSel.size}
                        verbs={cmdVerbs}
                        staged={staged?.scope === 'commands' ? staged : null}
                        receipt={staged === null && receipt !== null && receipt.scope === 'commands' ? receipt : null}
                        sending={sending}
                        onStage={stageCommand}
                        onDiscard={() => setStaged(null)}
                        onCommit={() => { void commit(); }}
                        onClear={clearCommands}
                      />
                    )}
                    onRowClick={(c) => navigate('detail', { robotId: c.robot_id })}
                    empty={commandsFeed.loading
                      ? 'Reading command ledger…'
                      : rosterEmpty
                        ? 'No robots on the roster.'
                        : cmdView === 'open'
                          ? 'Nothing open.'
                          : cmdView === 'failed'
                            ? 'Nothing has been refused.'
                            : 'No commands issued.'}
                  />
                </div>
                </div>
              )}
              {/* The refusal view's own footer: the same 11px mono caption the
                  capture panel and the history stack keep, holding the one
                  reading the list itself cannot give — the SHAPE of the
                  failures. Only on this view, because it is the only one whose
                  rows share a vocabulary worth tallying. */}
              {cmdView === 'failed' && !commandsFeed.down && refusalTally.length > 0 && (
                <div className={styles.cmdFoot}>
                  {refusalTally.map((t, i) => (
                    <span key={t.code}>
                      {i > 0 && <i>·</i>}
                      <b>{t.n}</b> {t.code}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
   Local layout helpers.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   A picked ROW is a picked run.

   The matrix's group strip already turns `fault 4` into a four-robot selection
   with one press. These two panels name the same kinds of run — a site, a
   morphology, an agent build, a board revision — and until now they were the
   only groupings on the surface an operator could read and not act on. They
   drive the same `toggleRun`, so the count in the action bar, the tint on the
   matrix columns and the tint on these rows are one selection seen four ways.

   Rows, not buttons, because they are cells of a grid and `aria-selected` on a
   `row` is what a screen reader is looking for. One row per panel is in the tab
   order and the arrows walk the rest (§6 — a list must not cost one Tab per
   row); Enter and Space pick.
   -------------------------------------------------------------------------- */
function pickRowKeyDown(e: KeyboardEvent<HTMLElement>, pick: () => void): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    pick();
    return;
  }
  const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
  if (step === 0) return;
  e.preventDefault();
  const rows = [...(e.currentTarget.closest('[role="grid"]')
    ?.querySelectorAll<HTMLElement>('[data-pick]') ?? [])];
  const next = rows[rows.indexOf(e.currentTarget) + step];
  next?.focus();
}

/** One section of the estate grid: an eyebrow row carrying its own count
    (§4 — an eyebrow never floats above content without something right-aligned
    on its line), then the section's rows as members of the PANEL's grid rather
    than of a grid of their own. That is the whole point: `grid-column: 1 / -1`
    plus `subgrid` means a section adds rows to the shared three columns and
    can never introduce a fourth x position. */
function EstateSection({
  label,
  noun,
  groups,
  peak,
  first,
  current,
  selected,
  onPick,
}: {
  label: string;
  /** What one row of this section IS, for the row's own sentence. */
  noun: string;
  groups: readonly Group[];
  /** The shared denominator: every bar in the panel is a share of this. */
  peak: number;
  /** Is this the panel's first section? Its first row holds the tab stop. */
  first: boolean;
  current: ReadonlySet<string> | null;
  selected: ReadonlySet<string>;
  onPick: (ids: readonly string[]) => void;
}) {
  return (
    <>
      <div className={styles.eSection} role="row">
        <span role="rowheader">{label}</span>
        <span className={styles.spacer} aria-hidden="true" />
        <span role="gridcell" className={styles.eSectionCount}>{groups.length}</span>
      </div>
      {groups.map((g, i) => {
        const n = g.robots.length;
        const census = g.mix.map((m) => `${m.n} ${MIX_LABEL[m.tone] ?? m.tone}`).join(' · ');
        const ids = g.robots.map((r) => r.robot_id);
        const picked = n > 0 && ids.every((id) => selected.has(id));
        const pick = () => onPick(ids);
        return (
          <div
            key={g.key}
            className={styles.eRow}
            role="row"
            data-pick
            data-current={current !== null && current.has(g.key)}
            aria-selected={picked}
            tabIndex={first && i === 0 ? 0 : -1}
            onClick={pick}
            onKeyDown={(e) => pickRowKeyDown(e, pick)}
            title={`${n} of ${peak} robots on ${noun} ${g.key} — ${census}`
              + `  ·  click to ${picked ? 'clear' : 'select'} the run`}
          >
            <span className={styles.eLabel} role="gridcell">{g.label}</span>
            <span className={styles.eNum} role="gridcell">{n}</span>
            <span className={styles.eTrack} role="gridcell">
              <MixBar mix={g.mix} total={n} peak={peak} census={census} />
            </span>
          </div>
        );
      })}
    </>
  );
}

function GroupSection({
  label,
  groups,
  peak,
  first,
  current,
  selected,
  onPick,
}: {
  label: string;
  groups: readonly Group[];
  peak: number;
  /** Is this the panel's first section? Its first row holds the tab stop. */
  first: boolean;
  /** The group keys the surface's cursor falls in — one site and one
      morphology, or nothing while nothing is pointed at. */
  current: ReadonlySet<string> | null;
  selected: ReadonlySet<string>;
  onPick: (ids: readonly string[]) => void;
}) {
  return (
    <>
      <div className={styles.gSection} role="row">
        <span role="rowheader">{label}</span>
        <span className={styles.spacer} aria-hidden="true" />
        <span role="gridcell" className={styles.gSectionCount}>{groups.length}</span>
      </div>
      {groups.map((g, i) => {
        const ids = g.robots.map((r) => r.robot_id);
        const picked = ids.length > 0 && ids.every((id) => selected.has(id));
        const pick = () => onPick(ids);
        return (
        <div
          key={g.key}
          className={styles.gRow}
          role="row"
          data-tone={g.worst}
          data-current={current !== null && current.has(g.key)}
          aria-selected={picked}
          onClick={pick}
          title={`${g.label} — ${g.robots.length} robots`
            + `  ·  click to ${picked ? 'clear' : 'select'} the group`}
        >
          <span className={styles.gTick} data-tone={g.worst} aria-hidden="true" />
          {/* The KEYBOARD LANDS ON THE LABEL, not on the row: `.gRow` is
              `display: contents` so it has no box, and an element with no box
              cannot take focus in any engine. The whole row is still the click
              target and still wears the ring — `:has(:focus-visible)` paints
              it on the cells, the same way the cursor wash is painted. */}
          <span
            className={styles.gLabel}
            role="gridcell"
            data-pick
            tabIndex={first && i === 0 ? 0 : -1}
            onKeyDown={(e) => pickRowKeyDown(e, pick)}
          >{g.label}</span>
          <span className={styles.gNum} role="gridcell">{g.robots.length}</span>
          <span className={styles.gNum} role="gridcell">
            {g.live}
            <i>/{g.robots.length}</i>
          </span>
          <span className={styles.gNum} role="gridcell">
            {g.attention === 0 ? <i>0</i> : g.attention}
          </span>
          <span className={styles.gMix} role="gridcell">
            <MixBar
              mix={g.mix}
              total={g.robots.length}
              peak={peak}
              census={g.mix.map((m) => `${m.n} ${MIX_LABEL[m.tone] ?? m.tone}`).join(' · ')}
            />
          </span>
          <span className={styles.gNum} role="gridcell">
            {g.battery === null ? NO_VALUE : g.battery.toFixed(0)}
            <i>%</i>
          </span>
          <span
            className={`${styles.gNum} ${styles.gCert}`}
            role="gridcell"
            data-tone={g.certDays !== null && g.certDays <= CERT_WARN_DAYS
              ? (g.certDays <= CERT_BAD_DAYS ? 'error' : 'warn')
              : 'neutral'}
          >
            {g.certDays === null ? NO_VALUE : duration(g.certDays * 86_400_000)}
          </span>
          <span className={styles.gFw} role="gridcell" title={g.firmware.join(', ')}>
            {g.firmware.length === 0 ? NO_VALUE : g.firmware[g.firmware.length - 1]}
            {g.firmware.length > 1 && <i>+{g.firmware.length - 1}</i>}
          </span>
        </div>
        );
      })}
    </>
  );
}
